import { Search } from 'lucide-react';
import React from 'react';

export const renderAlgorithmContent = (activeTopic) => {
  switch (activeTopic) {
    case 'What is an Algorithm':
      return (
        <div className="space-y-6">
          <section>
            <h2 className="text-2xl font-bold mb-4">What is an Algorithm?</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              An algorithm is a well-defined, step-by-step procedure to solve a specific problem. It takes a set of inputs and produces a desired output.
            </p>
          </section>
          
          <div className="flex flex-col items-center justify-center p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
             <div className="flex items-center gap-4 text-sm font-bold">
                <div className="px-4 py-6 border-2 border-slate-200 rounded-xl text-center">INPUT<br/><span className="text-slate-400 font-mono text-xs">Raw Data</span></div>
                <span className="text-slate-300">→</span>
                <div className="relative px-6 py-8 border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
                   <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 bg-blue-500 text-white text-[10px] rounded">Logic</div>
                   <span className="text-blue-600">ALGORITHM</span><br/>
                   <span className="text-blue-400 text-[10px] font-mono">Step 1 → Step 2 → ...</span>
                </div>
                <span className="text-slate-300">→</span>
                <div className="px-4 py-6 border-2 border-green-500 bg-green-50 dark:bg-green-900/20 rounded-xl text-center">OUTPUT<br/><span className="text-green-600 font-mono text-xs">Solution</span></div>
             </div>
          </div>

          <section>
            <h3 className="text-xl font-bold mb-2">Pseudocode</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
              Pseudocode is a high-level description of an algorithm that uses structural conventions of programming languages but is intended for human reading.
            </p>
            <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl overflow-x-auto">
              <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-2 text-[10px]">
                <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span><span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span></div>
                <span className="text-slate-500">TEXT</span>
              </div>
              <pre>{`ALGORITHM FindMax(arr):
    max_val = arr[0]
    FOR i FROM 1 TO length(arr) - 1:
        IF arr[i] > max_val:
            max_val = arr[i]
    RETURN max_val`}</pre>
            </div>
          </section>
        </div>
      );

    case 'Time & Space Complexity':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Complexity Analysis</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Complexity analysis helps us predict the resources (time and memory) an algorithm requires.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/30">
               <h4 className="font-bold text-green-700 mb-2">Time Complexity</h4>
               <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">The computational complexity that describes the amount of time it takes to run an algorithm.</p>
               <ul className="text-xs font-mono space-y-1 text-green-600">
                 <li>• O(1): Constant</li>
                 <li>• O(log n): Logarithmic</li>
                 <li>• O(n): Linear</li>
                 <li>• O(n²): Quadratic</li>
               </ul>
            </div>
            <div className="p-6 rounded-2xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30">
               <h4 className="font-bold text-blue-700 mb-2">Space Complexity</h4>
               <p className="text-sm text-slate-600 dark:text-slate-400">The total amount of memory space used by an algorithm, including input values and auxiliary memory.</p>
            </div>
          </div>

          <section>
            <h3 className="text-xl font-bold mb-4">Asymptotic Notations</h3>
            <div className="space-y-3 text-sm">
               <div className="flex items-center gap-4 p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 rounded-xl">
                  <div className="w-8 h-8 rounded-full border-2 border-red-500 flex items-center justify-center font-bold text-red-500">O</div>
                  <div><span className="font-bold text-red-700">Big-O (Upper Bound)</span><p className="text-xs opacity-70 italic">Worst-case scenario. The algorithm will never take longer than this.</p></div>
               </div>
               <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/10 border border-green-100 rounded-xl">
                  <div className="w-8 h-8 rounded-full border-2 border-green-500 flex items-center justify-center font-bold text-green-500">Ω</div>
                  <div><span className="font-bold text-green-700">Omega (Lower Bound)</span><p className="text-xs opacity-70 italic">Best-case scenario.</p></div>
               </div>
               <div className="flex items-center gap-4 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 rounded-xl">
                  <div className="w-8 h-8 rounded-full border-2 border-amber-500 flex items-center justify-center font-bold text-amber-500">Θ</div>
                  <div><span className="font-bold text-amber-700">Theta (Tight Bound)</span><p className="text-xs opacity-70 italic">Average case behavior.</p></div>
               </div>
            </div>
          </section>
        </div>
      );

    case 'Prime Numbers & Sieve':
      return (
        <div className="space-y-6">
          <section>
            <h2 className="text-2xl font-bold mb-2">Prime Numbers</h2>
            <p className="text-slate-600 dark:text-slate-400">A prime number is a natural number greater than 1 that has no positive divisors other than 1 and itself.</p>
          </section>
          <section>
            <h2 className="text-2xl font-bold mb-2">Sieve of Eratosthenes</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm">An efficient algorithm to find all prime numbers up to a specified integer n. Complexity: <strong>O(n log log n)</strong>.</p>
            <div className="bg-slate-900 rounded-xl p-5 mt-4 font-mono text-sm text-slate-300">
               <pre>{`void sieve(int n) {
    vector<bool> is_prime(n + 1, true);
    is_prime[0] = is_prime[1] = false;
    for (int p = 2; p * p <= n; p++) {
        if (is_prime[p]) {
            for (int i = p * p; i <= n; i += p)
                is_prime[i] = false;
        }
    }
}`}</pre>
            </div>
          </section>
        </div>
      );

    case 'GCD & LCM':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">GCD & LCM</h2>
          <p className="text-slate-600 dark:text-slate-400">
            <strong>GCD (Greatest Common Divisor)</strong> is the largest positive integer that divides two numbers without a remainder. <strong>Euclidean Algorithm</strong> is an efficient method to compute it.
          </p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-lg">
             <pre>{`// Euclidean Algorithm
int gcd(int a, int b) {
    if (b == 0) return a;
    return gcd(b, a % b);
}

// LCM (Least Common Multiple)
int lcm(int a, int b) {
    return (a / gcd(a, b)) * b;
}`}</pre>
          </div>
        </div>
      );

    case 'Modular Arithmetic':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Modular Arithmetic</h2>
          <p className="text-slate-600 dark:text-slate-400">Calculations "wrapping around" a certain value (the modulus). Fundamental in cryptography and CP.</p>
          <ul className="space-y-2 font-mono text-sm bg-slate-50 dark:bg-slate-800/50 p-6 rounded-xl border border-slate-100 dark:border-slate-800">
            <li>• (a + b) % m = ((a % m) + (b % m)) % m</li>
            <li>• (a * b) % m = ((a % m) * (b % m)) % m</li>
            <li>• (a - b) % m = ((a % m) - (b % m) + m) % m</li>
          </ul>
          <h3 className="text-xl font-bold">Fast Exponentiation (Modular)</h3>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>{`long long power(long long base, long long exp) {
    long long res = 1;
    base = base % 1000000007;
    while (exp > 0) {
        if (exp % 2 == 1) res = (res * base) % 1000000007;
        base = (base * base) % 1000000007;
        exp = exp / 2;
    }
    return res;
}`}</pre>
          </div>
        </div>
      );

    case 'Two Pointer Technique':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Two Pointer Technique</h2>
          <p className="text-slate-600 dark:text-slate-400">Used to search for pairs in a sorted array or to merge two sorted arrays. It reduces time complexity typically from O(N²) to O(N).</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl">
             <pre>{`// Find if pair exists with sum target
bool hasPair(int arr[], int n, int target) {
    int left = 0, right = n - 1;
    while (left < right) {
        int sum = arr[left] + arr[right];
        if (sum == target) return true;
        if (sum < target) left++;
        else right--;
    }
    return false;
}`}</pre>
          </div>
        </div>
      );

    case 'Sliding Window':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Sliding Window</h2>
          <p className="text-slate-600 dark:text-slate-400">Used to perform an operation on a specific window size of an array or string. It converts nested loops into a single loop.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>{`// Max sum of subarray of size k
int maxSum(int arr[], int n, int k) {
    int max_sum = 0, window_sum = 0;
    for (int i=0; i<k; i++) window_sum += arr[i];
    max_sum = window_sum;

    for (int i=k; i<n; i++) {
        window_sum += arr[i] - arr[i-k];
        max_sum = max(max_sum, window_sum);
    }
    return max_sum;
}`}</pre>
          </div>
        </div>
      );

    case 'Kadane\'s Algorithm':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Kadane's Algorithm</h2>
          <p className="text-slate-600 dark:text-slate-400">Efficiently finds the <strong>Maximum Subarray Sum</strong> in O(n) time.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl">
             <pre>{`int maxSubArraySum(int a[], int size) {
    int max_so_far = INT_MIN, max_ending_here = 0;
    for (int i = 0; i < size; i++) {
        max_ending_here = max_ending_here + a[i];
        if (max_so_far < max_ending_here)
            max_so_far = max_ending_here;
        if (max_ending_here < 0)
            max_ending_here = 0;
    }
    return max_so_far;
}`}</pre>
          </div>
        </div>
      );

   case 'KMP Algorithm':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">KMP (Knuth-Morris-Pratt) Algorithm</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            The KMP matching algorithm uses degenerating property (pattern having same sub-patterns appearing more than once in the pattern) of the pattern and improves the worst-case complexity to <strong>O(n + m)</strong>.
          </p>
          
          <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-xl">
            <h4 className="font-bold text-blue-800 dark:text-blue-400 mb-2">Key Concept: LPS Array</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              LPS indicates Longest Proper Prefix which is also Suffix. We use this array to skip characters while matching.
            </p>
          </div>

          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl overflow-x-auto">
            <div className="flex gap-2 mb-4 border-b border-slate-800 pb-2 text-[10px]">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="ml-2 uppercase text-slate-500 font-bold">LPS Implementation</span>
            </div>
            <pre>{`void computeLPSArray(string pat, int M, int* lps) {
    int len = 0;
    lps[0] = 0;
    int i = 1;
    while (i < M) {
        if (pat[i] == pat[len]) {
            len++;
            lps[i] = len;
            i++;
        } else {
            if (len != 0) len = lps[len - 1];
            else { lps[i] = 0; i++; }
        }
    }
}`}</pre>
          </div>
        </div>
      );

    case 'Dutch National Flag':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Dutch National Flag Algorithm</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Also known as <strong>Sort 0s, 1s, and 2s</strong>. It uses three pointers to partition the array in a single pass.
          </p>
          
          {/* Visual Diagram from Image */}
          <div className="flex flex-col items-center p-8 bg-slate-100 dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-slate-700">
             <div className="flex gap-2 mb-4">
                <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center text-white font-bold">0</div>
                <div className="w-12 h-12 bg-white border-2 border-slate-300 rounded-lg flex items-center justify-center text-slate-800 font-bold">1</div>
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">2</div>
             </div>
             <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Time Complexity: O(n) | Space: O(1)</p>
          </div>

          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
            <pre>{`void sort012(int a[], int n) {
    int lo = 0, mid = 0, hi = n - 1;
    while (mid <= hi) {
        switch (a[mid]) {
        case 0:
            swap(a[lo++], a[mid++]);
            break;
        case 1:
            mid++;
            break;
        case 2:
            swap(a[mid], a[hi--]);
            break;
        }
    }
}`}</pre>
          </div>
        </div>
      );

    // ... (Previous topics like Algorithm Basics, Mathematics, etc. are already there)

    case 'Linear & Binary Search':
      return (
        <div className="space-y-6">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-slate-800 dark:text-white">Searching Algorithms</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-blue-600 mb-2">Linear Search</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Checks every element in the list until a match is found. Best for unsorted data.</p>
                <div className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 text-[10px] font-bold rounded-full w-fit">Complexity: O(n)</div>
              </div>
              <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-green-600 mb-2">Binary Search</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">Repeatedly dividing the search interval in half. Requires **Sorted** data.</p>
                <div className="px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 text-[10px] font-bold rounded-full w-fit">Complexity: O(log n)</div>
              </div>
            </div>
          </section>

          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl overflow-x-auto">
            <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-2 text-[10px]">
              <span className="text-slate-500 uppercase tracking-widest font-bold">Binary Search (Recursive)</span>
              <span className="text-blue-400 font-bold italic">CPP</span>
            </div>
            <pre>{`int binarySearch(int arr[], int l, int r, int x) {
    if (r >= l) {
        int mid = l + (r - l) / 2;
        if (arr[mid] == x) return mid;
        if (arr[mid] > x) return binarySearch(arr, l, mid - 1, x);
        return binarySearch(arr, mid + 1, r, x);
    }
    return -1;
}`}</pre>
          </div>
        </div>
      );

    case 'Ternary Search':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Ternary Search</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            Similar to binary search but divides the array into **three parts** instead of two using two mid points: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">m1</code> and <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">m2</code>.
          </p>
          <div className="p-6 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <h4 className="font-bold text-amber-800 dark:text-amber-400 mb-1">Time Complexity</h4>
             <p className="text-sm text-slate-600 dark:text-slate-400">O(log₃ n) — Logarithmic base 3.</p>
          </div>
        </div>
      );

    case 'Basic Sorts (Bubble, Insertion, Selection)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Simple Sorting Algorithms</h2>
          <p className="text-slate-600 dark:text-slate-400 italic">Easy to implement but inefficient for large datasets (O(n²)).</p>
          
          <div className="space-y-4">
            <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
               <span className="font-bold text-primary">Bubble Sort:</span> Repeatedly steps through the list, compares adjacent elements and swaps them if they are in the wrong order.
            </div>
            <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
               <span className="font-bold text-primary">Selection Sort:</span> Finds the minimum element from the unsorted part and puts it at the beginning.
            </div>
            <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
               <span className="font-bold text-primary">Insertion Sort:</span> Builds the final sorted array one item at a time, much like how you sort playing cards in your hands.
            </div>
          </div>
        </div>
      );

    case 'Advanced Sorts (Merge, Quick)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Efficient Sorting (Divide & Conquer)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
             <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-2xl">
                <h4 className="font-bold text-indigo-700 dark:text-indigo-400">Merge Sort</h4>
                <p>Divide array into halves, sort them and merge back. Stable sort. O(n log n).</p>
             </div>
             <div className="p-6 bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800 rounded-2xl">
                <h4 className="font-bold text-sky-700 dark:text-sky-400">Quick Sort</h4>
                <p>Pick a 'pivot' and partition around it. O(n log n) average. In-place sort.</p>
             </div>
          </div>
        </div>
      );

      case 'Linear Sorts (Counting, Radix)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Counting Sort</h2>
            <p className="text-slate-600 dark:text-slate-400">
              A non-comparison-based sorting algorithm that works by counting the number of objects having distinct key values. Complexity: <strong>O(n + k)</strong>.
            </p>
            <div className="mt-4 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-mono text-xs">
               Input: [1, 4, 1, 2, 7, 5, 2] <br/>
               Count: [0:0, 1:2, 2:2, 3:0, 4:1, 5:1, 6:0, 7:1]
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Radix Sort</h2>
            <p className="text-slate-600 dark:text-slate-400">
              Sorts numbers by processing individual digits. It uses Counting Sort as a subroutine to sort from Least Significant Digit (LSD) to Most Significant Digit (MSD).
            </p>
            <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-bold">
               <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">170, 45, 75<br/>(Original)</div>
               <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-lg">170, 045, 075<br/>(By 1s Digit)</div>
               <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 rounded-lg">045, 075, 170<br/>(Sorted)</div>
            </div>
          </section>
        </div>
      );
    case 'Recursion Basics':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Recursion Basics</h2>
          <p className="text-slate-600 dark:text-slate-400">
            A process in which a function calls itself directly or indirectly. It must have a **Base Case** to prevent infinite execution.
          </p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>{`// Factorial Example
int factorial(int n) {
    if (n <= 1) return 1; // Base Case
    return n * factorial(n - 1); // Recursive Call
}`}</pre>
          </div>
        </div>
      );

    case 'N-Queens Problem':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">N-Queens (Backtracking)</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Placing N chess queens on an N×N chessboard so that no two queens attack each other.
          </p>
          <div className="flex justify-center py-8 bg-slate-100 dark:bg-slate-800 rounded-3xl">
             <div className="grid grid-cols-4 gap-1 w-32">
                {[0,1,2,3].map(r => [0,1,2,3].map(c => (
                  <div key={r+c} className={`h-8 w-8 border border-slate-300 flex items-center justify-center text-lg ${ (r+c)%2===0 ? 'bg-white' : 'bg-slate-300'}`}>
                    {(r===0 && c===1) || (r===1 && c===3) || (r===2 && c===0) || (r===3 && c===2) ? '👑' : ''}
                  </div>
                )))}
             </div>
          </div>
          <p className="text-xs text-center text-slate-500 italic font-medium tracking-wide uppercase">Visual: 4-Queens Solution Board</p>
        </div>
      );
      // ... (Previous logic for Search, Sorting, Recursion)

    case 'Rat in a Maze':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Rat in a Maze (Backtracking)</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            A rat starts from <strong>(0,0)</strong> and has to reach <strong>(N-1, N-1)</strong>. The maze is a 2D grid where 1 is a path and 0 is a wall.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-3xl border-2 border-dashed border-slate-300 flex justify-center">
               <div className="grid grid-cols-4 gap-1">
                 {[1,0,0,0, 1,1,0,1, 0,1,0,0, 1,1,1,1].map((v, i) => (
                   <div key={i} className={`w-8 h-8 flex items-center justify-center border ${v === 1 ? 'bg-white' : 'bg-slate-400'}`}>
                     {i === 0 ? '🐀' : i === 15 ? '🧀' : ''}
                   </div>
                 ))}
               </div>
            </div>
            <ul className="space-y-2 text-sm text-slate-500">
              <li>• <strong>Backtrack:</strong> If a path leads to a dead-end, go back.</li>
              <li>• <strong>Visited:</strong> Keep track of cells to avoid infinite loops.</li>
            </ul>
          </div>
          
        </div>
      );

    case 'Divide & Conquer Strategy':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Divide & Conquer</h2>
          <div className="flex flex-col items-center gap-4 py-6 bg-blue-50 dark:bg-blue-900/10 rounded-3xl">
             <div className="px-6 py-3 border-2 border-blue-500 rounded-xl font-bold">BIG PROBLEM</div>
             <div className="text-blue-400">⬇ DIVIDE</div>
             <div className="flex gap-4">
                <div className="px-4 py-2 border border-blue-400 rounded-lg">Sub 1</div>
                <div className="px-4 py-2 border border-blue-400 rounded-lg">Sub 2</div>
             </div>
             <div className="text-blue-400">⬇ CONQUER & COMBINE</div>
             <div className="px-6 py-3 bg-green-500 text-white rounded-xl font-bold">SOLUTION</div>
          </div>
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            Used in <strong>Merge Sort</strong> and <strong>Quick Sort</strong>. It breaks a large problem into independent sub-problems.
          </p>
          
        </div>
      );

    case 'Greedy Algorithms':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Greedy Algorithms</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Picks the <strong>locally optimal</strong> choice at each step with the hope of finding the global optimum.
          </p>
          <div className="p-6 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-2xl">
             <h4 className="font-bold text-amber-800 mb-1">Standard Problems</h4>
             <ul className="text-sm space-y-1 text-amber-700">
                <li>• Fractional Knapsack</li>
                <li>• Huffman Coding</li>
                <li>• Dijkstra's Algorithm</li>
             </ul>
          </div>
          
        </div>
      );

   case 'Common DP Problems':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Common Dynamic Programming Problems</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            These classic problems illustrate the core concepts of overlapping subproblems and optimal substructure.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:border-primary/40 transition-colors">
              <h4 className="font-bold text-primary mb-2">0/1 Knapsack</h4>
              <p className="text-xs text-slate-500">Decide whether to pick an item or leave it to maximize value without exceeding capacity.</p>
            </div>
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:border-primary/40 transition-colors">
              <h4 className="font-bold text-primary mb-2">Longest Common Subsequence (LCS)</h4>
              <p className="text-xs text-slate-500">Finding the longest subsequence present in two sequences in the same relative order.</p>
            </div>
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:border-primary/40 transition-colors">
              <h4 className="font-bold text-primary mb-2">Longest Increasing Subsequence (LIS)</h4>
              <p className="text-xs text-slate-500">Find the length of the longest subsequence of a given sequence such that all elements are sorted in increasing order.</p>
            </div>
            <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:border-primary/40 transition-colors">
              <h4 className="font-bold text-primary mb-2">Matrix Chain Multiplication</h4>
              <p className="text-xs text-slate-500">Find the most efficient way to multiply a given sequence of matrices.</p>
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300 shadow-xl overflow-x-auto">
             <div className="text-xs text-slate-500 mb-3 uppercase font-bold tracking-widest border-b border-slate-800 pb-2">LCS Recursive with Memoization</div>
             <pre>{`int lcs(string X, string Y, int m, int n, vector<vector<int>>& dp) {
    if (m == 0 || n == 0) return 0;
    if (dp[m][n] != -1) return dp[m][n];

    if (X[m - 1] == Y[n - 1])
        return dp[m][n] = 1 + lcs(X, Y, m - 1, n - 1, dp);
    
    return dp[m][n] = max(lcs(X, Y, m, n - 1, dp), lcs(X, Y, m - 1, n, dp));
}`}</pre>
          </div>
        </div>
      );
  case 'Tree Basics & Traversals':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Binary Tree Traversals</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Traversal is a process to visit all the nodes of a tree and may print their values too.
          </p>

          <div className="flex flex-col items-center p-8 bg-slate-100 dark:bg-slate-800/50 rounded-3xl border border-slate-200">
             <div className="font-mono text-sm text-center">
                <div className="mb-2 px-4 py-2 border-2 border-slate-400 rounded-full inline-block">Root (A)</div>
                <div className="flex gap-12 mt-4">
                   <div className="px-3 py-1 border border-slate-300 rounded-lg bg-white">Left (B)</div>
                   <div className="px-3 py-1 border border-slate-300 rounded-lg bg-white">Right (C)</div>
                </div>
             </div>
          </div>

          <div className="space-y-4">
             <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
                <span className="font-bold text-red-700">Preorder:</span> Root → Left → Right
             </div>
             <div className="p-4 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-500 rounded-r-xl">
                <span className="font-bold text-green-700">Inorder:</span> Left → Root → Right
             </div>
             <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
                <span className="font-bold text-blue-700">Postorder:</span> Left → Right → Root
             </div>
          </div>
        </div>
      );
      case 'Advanced Tree Structures':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Advanced Trees</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Specialized tree structures designed for specific operations like auto-balancing or string searching.
          </p>

          <div className="space-y-4">
            <div className="group p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:shadow-lg transition-all">
              <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2 group-hover:text-primary">AVL Trees</h4>
              <p className="text-sm text-slate-500 italic">Self-balancing binary search tree. The heights of the two child subtrees of any node differ by at most one.</p>
            </div>
            
            <div className="group p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:shadow-lg transition-all">
              <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2 group-hover:text-primary">Red-Black Trees</h4>
              <p className="text-sm text-slate-500 italic">Balanced BST that uses "colors" (red or black) to ensure the tree remains approximately balanced during insertions and deletions.</p>
            </div>

            <div className="group p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:shadow-lg transition-all">
              <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2 group-hover:text-primary">Trie (Prefix Tree)</h4>
              <p className="text-sm text-slate-500 italic">An efficient information retrieval data structure used to store strings. Complexity: O(length of string).</p>
            </div>

            <div className="group p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl hover:shadow-lg transition-all">
              <h4 className="text-xl font-bold text-slate-800 dark:text-white mb-2 group-hover:text-primary">B-Trees & B+ Trees</h4>
              <p className="text-sm text-slate-500 italic">Self-balancing search trees that store sorted data and allow searches, sequential access, insertions, and deletions in logarithmic time. Widely used in **Databases**.</p>
            </div>
          </div>
        </div>
      );


    case 'BFS & DFS':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Graph Traversals</h2>
          <div className="space-y-4">
             <div className="flex items-start gap-4 p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center font-bold">Q</div>
                <div>
                   <h4 className="font-bold text-sky-600">BFS (Level-Order)</h4>
                   <p className="text-xs text-slate-500">Uses <strong>Queue</strong>. Explores neighbors level-by-level.</p>
                </div>
             </div>
             <div className="flex items-start gap-4 p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">S</div>
                <div>
                   <h4 className="font-bold text-purple-600">DFS (Depth-First)</h4>
                   <p className="text-xs text-slate-500">Uses <strong>Stack</strong>. Explores as deep as possible before backtracking.</p>
                </div>
             </div>
          </div>
          
        </div>
      );

    case 'Topological Sort':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Topological Sorting</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Linear ordering of vertices in a <strong>Directed Acyclic Graph (DAG)</strong> such that for every edge u → v, u comes before v.
          </p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
            <pre>{`// Application: Task Scheduling\nBuild -> Test -> Deploy`}</pre>
          </div>
        </div>
      );

    case 'Strongly Connected Components':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">SCC (Strongly Connected Components)</h2>
          <p className="text-slate-600 dark:text-slate-400">
            A subset of a directed graph where every vertex is reachable from every other vertex. <strong>Kosaraju's Algorithm</strong> is commonly used.
          </p>
        </div>
      );
    // ... (Previous cases: id 1 to 11 remain same)

    case 'Dijkstra & Bellman-Ford':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Shortest Path Algorithms</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 shadow-sm">
              <h4 className="font-bold text-blue-600 mb-2">Dijkstra's Algorithm</h4>
              <p className="text-sm text-slate-500">Finds shortest paths from source to all vertices with <strong>non-negative</strong> weights. Uses Min-Priority Queue. <strong>O(E log V)</strong>.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 shadow-sm">
              <h4 className="font-bold text-red-600 mb-2">Bellman-Ford Algorithm</h4>
              <p className="text-sm text-slate-500">Handles <strong>negative weights</strong> and detects negative cycles. Slower than Dijkstra. <strong>O(VE)</strong>.</p>
            </div>
          </div>
        </div>
      );

    case 'Floyd-Warshall':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Floyd-Warshall Algorithm</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            An all-pairs shortest path algorithm. It finds shortest paths between every pair of vertices in a weighted graph. Complexity: <strong>O(V³)</strong>.
          </p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
            <pre>{`for (int k = 0; k < V; k++) {
    for (int i = 0; i < V; i++) {
        for (int j = 0; j < V; j++) {
            if (dist[i][k] + dist[k][j] < dist[i][j])
                dist[i][j] = dist[i][k] + dist[k][j];
        }
    }
}`}</pre>
          </div>
        </div>
      );

    case 'Prim & Kruskal':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Minimum Spanning Tree (MST)</h2>
          <p className="text-slate-600 dark:text-slate-400">A subset of edges that connects all vertices without cycles and with minimum total weight.</p>
          <div className="space-y-4">
            <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
               <span className="font-bold text-indigo-700">Prim's Algorithm:</span> Grows tree from a starting vertex. Adds cheapest edge connecting tree to outside.
            </div>
            <div className="p-4 bg-sky-50 dark:bg-sky-900/10 border-l-4 border-sky-500 rounded-r-xl">
               <span className="font-bold text-sky-700">Kruskal's Algorithm:</span> Sorts edges by weight. Adds edges if they don't form a cycle (using Union-Find).
            </div>
          </div>
        </div>
      );

    case 'Max Flow':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Max Flow Min Cut</h2>
          <p className="text-slate-600 dark:text-slate-400">Finds the maximum amount of flow possible from a source to a sink in a flow network.</p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-500">
             <li><strong>Ford-Fulkerson:</strong> Repeatedly finds augmenting paths in residual graph.</li>
             <li><strong>Edmonds-Karp:</strong> Uses BFS to find paths, improving complexity.</li>
          </ul>
        </div>
      );

    case 'Disjoint Set (Union-Find)':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Disjoint Set Union (DSU)</h2>
          <p className="text-slate-600 dark:text-slate-400">Efficiently keeps track of partitioned elements. Supports <strong>union</strong> and <strong>find</strong> operations in nearly constant time.</p>
          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl font-bold text-center">Crucial for Kruskal's Algorithm and Cycle Detection.</div>
        </div>
      );

    case 'Bitwise Tricks':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Bit Manipulation</h2>
          <p className="text-slate-600 dark:text-slate-400 italic font-medium tracking-tight">Fast, low-level operations on bits.</p>
          <div className="bg-slate-900 rounded-xl p-6 font-mono text-sm text-green-400">
             <ul className="space-y-3">
               <li>x & (x-1) : <span className="text-slate-400">// Clears the lowest set bit</span></li>
               <li>x & -x : <span className="text-slate-400">   // Extracts the lowest set bit</span></li>
               <li>x ^ x = 0 : <span className="text-slate-400">  // XORing same number results in 0</span></li>
             </ul>
          </div>
        </div>
      );

    case 'Advanced String Algos':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Advanced String Algorithms</h2>
          <div className="space-y-3">
            {["Rabin-Karp: Uses rolling hash", "Z-Algorithm: Constructs Z-array", "Aho-Corasick: Automata for multiple words", "Suffix Tree: Powerful for substring queries"].map((item, i) => (
              <div key={i} className="p-3 border border-slate-100 dark:border-slate-800 rounded-lg text-sm text-slate-600 dark:text-slate-400">{item}</div>
            ))}
          </div>
        </div>
      );

      case 'Convex Hull':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white leading-tight">Convex Hull</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            The <strong>Convex Hull</strong> of a set of points is the smallest convex polygon that contains all the points. 
            Imagine stretching a rubber band around all the points; when released, it will form the convex hull.
          </p>

          <div className="flex flex-col items-center justify-center p-8 bg-slate-100 dark:bg-slate-800/50 rounded-3xl border-2 border-dashed border-slate-300">
             <div className="relative w-48 h-48 bg-white dark:bg-slate-900 rounded-xl border border-slate-200">
                {/* Visualizing points inside a hull */}
                <div className="absolute top-10 left-10 w-2 h-2 bg-slate-400 rounded-full"></div>
                <div className="absolute top-20 left-30 w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="absolute bottom-10 right-10 w-2 h-2 bg-slate-400 rounded-full"></div>
                <div className="absolute top-5 right-15 w-2 h-2 bg-blue-500 rounded-full"></div>
                {/* The 'Rubber Band' line */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                   <path d="M40 40 L120 20 L160 140 L40 140 Z" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4" />
                </svg>
             </div>
             <p className="mt-4 text-xs font-bold text-slate-500 uppercase tracking-widest text-center">
               Rubber Band Analogy Visualization
             </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Jarvis March</h4>
                <p className="text-xs text-slate-500 italic">
                  Gift Wrapping algorithm. Complexity: <strong>O(nh)</strong>, where n is the number of points and h is the number of points on the hull.
                </p>
             </div>
             <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Graham Scan</h4>
                <p className="text-xs text-slate-500 italic">
                  Uses sorting based on polar angles. Complexity: <strong>O(n log n)</strong>. Efficient for larger datasets.
                </p>
             </div>
          </div>

          <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <h4 className="font-bold text-blue-800 dark:text-blue-400 mb-2">Orientation Concept</h4>
             <p className="text-sm text-slate-600 dark:text-slate-400">
               To determine if three points (p, q, r) are collinear, clockwise, or counter-clockwise:
               <br/>
               <code className="block mt-2 font-mono text-xs text-blue-600 bg-white dark:bg-slate-800 p-2 rounded">
                 val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y)
               </code>
               If <strong>val &gt; 0</strong>: Clockwise. <br/>
               If <strong>val &lt; 0</strong>: Counter-clockwise.
             </p>
          </div>
        </div>
      );
    case 'Minimax & Nim':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white leading-tight">Game Theory</h2>
          <section>
            <h4 className="font-bold text-primary mb-2 italic">Minimax Algorithm</h4>
            <p className="text-sm text-slate-500">Used for n-player games (Chess, Tic-Tac-Toe). One player maximizes score, the other minimizes it.</p>
          </section>
          <section>
             <h4 className="font-bold text-primary mb-2 italic">Nim Game</h4>
             <p className="text-sm text-slate-500">Solved using XOR sum. If XOR sum of pile sizes is non-zero, the first player wins.</p>
          </section>
        </div>
      );

    case 'Gradient Descent & variants':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Gradient Descent</h2>
          <p className="text-slate-600 dark:text-slate-400">Optimization algorithm used to minimize cost function in machine learning.</p>
          <div className="bg-slate-100 dark:bg-slate-800 h-48 rounded-3xl flex items-center justify-center border-2 border-dashed border-slate-300">
             <span className="text-slate-400 font-bold uppercase tracking-widest text-xs">Gradient Descent Visualization Graph</span>
          </div>
        </div>
      );

    case 'Backpropagation':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Backpropagation</h2>
          <p className="text-slate-600 dark:text-slate-400">The core algorithm for training neural networks by calculating gradients of the loss function.</p>
          <div className="p-8 bg-blue-50 dark:bg-blue-900/10 rounded-3xl border border-blue-100 text-center font-bold text-blue-600">
             Chain Rule: ∂L/∂w = (∂L/∂y) * (∂y/∂z) * (∂z/∂w)
          </div>
        </div>
      );

    case 'K-Means Clustering':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">K-Means Clustering</h2>
          <ol className="list-decimal pl-6 space-y-3 text-slate-600 dark:text-slate-400">
             <li>Initialize K centroids randomly.</li>
             <li>Assign each point to the nearest centroid.</li>
             <li>Recompute centroids (average of points).</li>
             <li>Repeat until convergence.</li>
          </ol>
        </div>
      );

    case 'Decision Trees & Random Forest':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white tracking-tight">Tree-Based Algorithms</h2>
          <div className="space-y-4">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <span className="font-bold text-emerald-600 block mb-1">Decision Trees:</span> Prone to overfitting. Splits based on Information Gain.
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <span className="font-bold text-emerald-600 block mb-1">Random Forest:</span> Ensemble method using <strong>Bagging</strong>. Builds multiple trees and averages predictions.
             </div>
          </div>
        </div>
      );

    case 'PCA & SVM':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-2">PCA (Principal Component Analysis)</h2>
            <p className="text-slate-500 text-sm">Reduces dimensionality by projecting data onto orthogonal axes. Uses Eigenvalues and Eigenvectors.</p>
          </section>
          <section>
            <h2 className="text-2xl font-bold mb-2">SVM Optimization</h2>
            <p className="text-slate-500 text-sm">Finds the optimal hyperplane that maximizes the <strong>Margin</strong> between classes. Uses Kernel Trick for non-linear data.</p>
          </section>
        </div>
      );

      case 'Bloom Filters':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white leading-tight">Bloom Filters</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            A <strong>Bloom Filter</strong> is a space-efficient probabilistic data structure used to test whether an element is a member of a set. 
            It can have <strong>false positives</strong>, but never false negatives. 
          </p>

          <div className="p-6 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <h4 className="font-bold text-amber-800 dark:text-amber-400 mb-2">How it works:</h4>
             <ul className="text-sm space-y-2 text-slate-600 dark:text-slate-400">
                <li>• Uses multiple hash functions to set bits in a bit array.</li>
                <li>• If all bits for a key are 1, the element <em>might</em> be in the set.</li>
                <li>• If any bit is 0, the element is <em>definitely not</em> in the set.</li>
             </ul>
          </div>
          
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
            <pre>{`// Complexity: O(k) where k is number of hash functions\n// Space: Very low compared to HashSets`}</pre>
          </div>
        </div>
      );

    case 'Skip Lists & Ropes':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Skip Lists</h2>
            <p className="text-slate-600 dark:text-slate-400"> A probabilistic data structure that allows <strong>O(log n)</strong> search, insertion, and deletion by maintaining multiple layers of linked lists. </p>
          </section>
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Ropes</h2>
            <p className="text-slate-600 dark:text-slate-400"> A tree-based data structure used to store and manipulate very long strings efficiently.  Operations like concatenation and slicing are much faster than traditional arrays. </p>
          </section>
        </div>
      );

    case 'LRU Cache':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white leading-tight">LRU (Least Recently Used) Cache</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">A caching strategy that removes the least recently used items first when the cache reaches its capacity. </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 shadow-sm"> <h4 className="font-bold text-primary mb-2 italic">Data Structure</h4> <p className="text-xs text-slate-500 italic">Combination of a <strong>HashMap</strong> (for O(1) access) and a <strong>Doubly Linked List</strong> (to maintain usage order).</p> </div>
             <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 shadow-sm text-center flex flex-col justify-center"> <span className="text-2xl font-bold text-primary">O(1)</span> <span className="text-[10px] text-slate-400 uppercase font-bold">Time Complexity</span> </div>
          </div>
        </div>
      );

    case 'System Design Algos':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white leading-tight">System Design Algorithms</h2>
          <div className="space-y-4">
             <div className="p-5 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 rounded-2xl"> <h4 className="font-bold text-blue-700 dark:text-blue-400 mb-1">Consistent Hashing</h4> <p className="text-sm text-slate-600 dark:text-slate-400">Used for load balancing and distributed caching to minimize data reshuffling when nodes are added/removed.</p> </div>
             <div className="p-5 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 rounded-2xl"><h4 className="font-bold text-purple-700 dark:text-purple-400 mb-1">Rate Limiting (Token Bucket)</h4><p className="text-sm text-slate-600 dark:text-slate-400">Controls the rate of traffic sent or received by a network interface or service.</p> </div>
             <div className="p-5 bg-green-50 dark:bg-green-900/10 border border-green-100 rounded-2xl"><h4 className="font-bold text-green-700 dark:text-green-400 mb-1">QuadTrees / Geo-Hashing</h4><p className="text-sm text-slate-600 dark:text-slate-400">Used for spatial indexing and location-based services like Google Maps or Uber.</p> </div>
          </div>
        </div>
      );
    default:
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center text-slate-500"><Search className="w-12 h-12 mb-4 opacity-20" /><p>This content module for <strong>{activeTopic}</strong> is ready for implementation.</p></div>
      );
  }
};


// ... (Existing renderAlgorithmContent function stays here)

export const renderJSContent = (activeTopic) => {
  switch (activeTopic) {
    case 'Introduction to JS':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Introduction to JavaScript</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            JavaScript is a versatile, high-level programming language that is primarily used to create interactive and dynamic content on websites. It is an essential part of web development alongside HTML and CSS.
          </p>
          <div className="p-6 bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-400 rounded-r-xl">
            <h4 className="font-bold text-yellow-700 dark:text-yellow-500 mb-2">Did you know?</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              JavaScript was created in just 10 days by Brendan Eich in 1995. Today, it powers almost 98% of the web!
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900">
              <span className="block text-2xl mb-2">🌐</span>
              <span className="text-xs font-bold uppercase text-slate-400">Client Side</span>
            </div>
            <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900">
              <span className="block text-2xl mb-2">🚀</span>
              <span className="text-xs font-bold uppercase text-slate-400">Fast Execution</span>
            </div>
            <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900">
              <span className="block text-2xl mb-2">🔧</span>
              <span className="text-xs font-bold uppercase text-slate-400">Dynamic</span>
            </div>
          </div>
        </div>
      );

    case 'Variables (var, let, const)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">JavaScript Variables</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Variables are containers for storing data values. In modern JavaScript, we have three ways to declare them.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-red-500 mb-2">var</h4>
                <p className="text-xs text-slate-500">Function-scoped or globally-scoped. Can be re-declared and updated. (Old way)</p>
             </div>
             <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-blue-500 mb-2">let</h4>
                <p className="text-xs text-slate-500">Block-scoped. Can be updated but not re-declared within the same block.</p>
             </div>
             <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm">
                <h4 className="font-bold text-green-500 mb-2">const</h4>
                <p className="text-xs text-slate-500">Block-scoped. Cannot be updated or re-declared. Must be initialized.</p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-2xl border border-slate-800">
             <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-2">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Example Usage</span>
                <span className="text-yellow-500 text-xs font-mono italic">JS</span>
             </div>
             <pre className="text-slate-300 font-mono text-sm leading-relaxed">
{`var name = "Koteshwar"; // Global
let age = 24;           // Block-scoped
const country = "India"; // Fixed value

// Re-assigning
age = 25;       // Allowed
country = "USA"; // Error!`}
             </pre>
          </div>
          
        </div>
      );

case 'Data Types':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">JavaScript Data Types</h2>
            <p className="text-slate-600 dark:text-slate-400">
              In JavaScript, data types can be broadly classified into two categories: <strong>Primitive</strong> and <strong>Non-Primitive</strong>.
            </p>
          </section>

          {/* Classification Diagram Placeholder */}
          <div className="flex flex-col items-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800">
             <div className="px-6 py-3 bg-primary text-white font-black rounded-xl mb-6 shadow-lg shadow-primary/20">DATA TYPES</div>
             <div className="flex gap-12 w-full justify-center relative">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[2px] bg-slate-200 dark:bg-slate-700 -z-10 mt-[-1px]"></div>
                <div className="flex flex-col items-center">
                   <div className="px-4 py-2 border-2 border-blue-400 rounded-lg font-bold text-blue-500 bg-white dark:bg-slate-800 mb-4">Primitive</div>
                   <ul className="text-[10px] space-y-1 text-slate-500 font-mono text-center">
                      <li>Number, String</li>
                      <li>Boolean, Null</li>
                      <li>Undefined, Symbol</li>
                      <li>BigInt</li>
                   </ul>
                </div>
                <div className="flex flex-col items-center">
                   <div className="px-4 py-2 border-2 border-green-400 rounded-lg font-bold text-green-500 bg-white dark:bg-slate-800 mb-4">Non-Primitive</div>
                   <ul className="text-[10px] space-y-1 text-slate-500 font-mono text-center">
                      <li>Object</li>
                      <li>Array</li>
                      <li>Function</li>
                   </ul>
                </div>
             </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
               <div className="w-1.5 h-6 bg-blue-500 rounded-full"></div> 1. Primitive Types
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="p-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                  <span className="font-bold text-blue-600 block mb-1">String:</span> Represents textual data. <code className="text-xs opacity-70">"Hello World"</code>
               </div>
               <div className="p-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                  <span className="font-bold text-blue-600 block mb-1">Number:</span> Integer and floating-point. <code className="text-xs opacity-70">10, 3.14</code>
               </div>
               <div className="p-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                  <span className="font-bold text-blue-600 block mb-1">Boolean:</span> Logial values. <code className="text-xs opacity-70">true / false</code>
               </div>
               <div className="p-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                  <span className="font-bold text-blue-600 block mb-1">Null:</span> Represents intentional absence of any object value.
               </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
               <div className="w-1.5 h-6 bg-green-500 rounded-full"></div> 2. Non-Primitive (Reference) Types
            </h3>
            <p className="text-sm text-slate-500 italic">Non-primitive types are objects and can store collections of data.</p>
            <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
               <div className="flex gap-1.5 mb-4"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span><span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span></div>
               <pre className="text-yellow-400">
{`// Object Example
const user = {
    name: "Koteshwar",
    age: 24
};

// Array Example
const colors = ["red", "green", "blue"];`}
               </pre>
            </div>
          </div>
          
        </div>
      );

    case 'Operators':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Operators</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl">
               <h4 className="font-bold text-primary mb-2">Arithmetic</h4>
               <p className="text-xs text-slate-500">+ , - , * , / , % , **</p>
             </div>
             <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl">
               <h4 className="font-bold text-primary mb-2">Assignment</h4>
               <p className="text-xs text-slate-500">= , += , -= , *=</p>
             </div>
             <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl">
               <h4 className="font-bold text-primary mb-2">Comparison</h4>
               <p className="text-xs text-slate-500">== , === , != , !== , &gt; , &lt;</p>
             </div>
             <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl">
               <h4 className="font-bold text-primary mb-2">Logical</h4>
               <p className="text-xs text-slate-500">&& (AND), || (OR), ! (NOT)</p>
             </div>
          </div>
        </div>
      );

    case 'if / else / else if':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Conditional Statements</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">Used to perform different actions based on different conditions.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
            <pre>{`if (time < 12) {\n    greeting = "Good Morning";\n} else if (time < 20) {\n    greeting = "Good Day";\n} else {\n    greeting = "Good Evening";\n}`}</pre>
          </div>
        </div>
      );

    case 'switch':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Switch Statement</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">Picks one of many code blocks to be executed.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
            <pre>{`switch(day) {\n  case 1: \n    console.log("Monday"); \n    break;\n  case 2: \n    console.log("Tuesday");\n    break;\n  default:\n    console.log("Weekend!");\n}`}</pre>
          </div>
        </div>
      );

 case 'Loops (for, while)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">Loops in JavaScript</h2>
            <p className="text-slate-600 dark:text-slate-400">Loops are used to execute a block of code multiple times as long as a condition is true.</p>
          </section>

          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
               <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-blue-600">
                 <div className="w-2 h-6 bg-blue-500 rounded-full"></div> For Loop
               </h3>
               <p className="text-sm text-slate-500 mb-4">Best when you know exactly how many times you want to loop.</p>
               <div className="bg-slate-900 rounded-xl p-4 font-mono text-xs text-slate-300">
                  <pre>{`for (let i = 0; i < 5; i++) {\n  console.log("Iteration: " + i);\n}`}</pre>
               </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
               <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-green-600">
                 <div className="w-2 h-6 bg-green-500 rounded-full"></div> While Loop
               </h3>
               <p className="text-sm text-slate-500 mb-4">Repeats code while a specific condition remains true.</p>
               <div className="bg-slate-900 rounded-xl p-4 font-mono text-xs text-slate-300">
                  <pre>{`let i = 0;\nwhile (i < 5) {\n  console.log("Count: " + i);\n  i++;\n}`}</pre>
               </div>
            </div>
          </div>
          
        </div>
      );

      case 'Function Definition':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Function Definition</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            A function is a block of code designed to perform a particular task. It is executed when "something" invokes it.
          </p>
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <pre className="text-blue-400">
{`function name(parameter1, parameter2) {
  // code to be executed
  return result;
}`}
             </pre>
          </div>
          
        </div>
      );

    case 'Arrow Functions':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Arrow Functions</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Arrow functions allow us to write shorter function syntax. They were introduced in ES6.
          </p>
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm">
             <pre className="text-green-400">
{`// Traditional
let sum = function(a, b) { return a + b; };

// Arrow Function
const sum = (a, b) => a + b;`}
             </pre>
          </div>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-500 italic">Note: Arrow functions do not have their own 'this'.</p>
          </div>
        </div>
      );

    case 'Introduction to Arrays':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">JavaScript Arrays</h2>
          <p className="text-slate-600 dark:text-slate-400">Arrays are used to store multiple values in a single variable. Each element has an index starting from 0.</p>
          <div className="flex justify-center p-8 bg-slate-100 dark:bg-slate-800 rounded-3xl border border-slate-200">
             <div className="flex gap-2">
                {["Apple", "Banana", "Mango"].map((v, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-white dark:bg-slate-900 border-2 border-primary rounded-xl flex items-center justify-center font-bold text-xs">{v}</div>
                    <span className="text-[10px] mt-2 font-mono text-slate-400">Index {i}</span>
                  </div>
                ))}
             </div>
          </div>
          
        </div>
      );

    case 'Common Array Methods':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Array Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             {[
               { m: "push()", d: "Adds element to end" },
               { m: "pop()", d: "Removes from end" },
               { m: "shift()", d: "Removes from start" },
               { m: "unshift()", d: "Adds to start" },
               { m: "map()", d: "Creates new array by transforming each element" },
               { m: "filter()", d: "Creates array with elements that pass a test" }
             ].map((item, i) => (
               <div key={i} className="p-4 bg-white dark:bg-slate-900 border border-slate-100 rounded-xl">
                  <code className="text-primary font-bold">{item.m}</code>
                  <p className="text-xs text-slate-500 mt-1">{item.d}</p>
               </div>
             ))}
          </div>
        </div>
      );

    case 'Selecting Elements':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">DOM Selection</h2>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>
{`document.getElementById("id");
document.getElementsByClassName("class");
document.querySelector(".selector");
document.querySelectorAll(".selector");`}
             </pre>
          </div>
          
        </div>
      );

    case 'Changing HTML/CSS':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Modifying the DOM</h2>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl font-mono text-xs">
             <p>element.innerHTML = "New Text";</p>
             <p>element.style.color = "blue";</p>
             <p>element.setAttribute("src", "img.jpg");</p>
          </div>
        </div>
      );

    case 'Common Event Types':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Event Listeners</h2>
          <ul className="grid grid-cols-2 gap-3 text-xs">
             <li className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg"><strong>click</strong>: When clicked</li>
             <li className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg"><strong>mouseover</strong>: Hovering</li>
             <li className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg"><strong>keydown</strong>: Pressing key</li>
             <li className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg"><strong>submit</strong>: Form submit</li>
          </ul>
        </div>
      );
      case 'String Methods':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">JavaScript String Methods</h2>
          <p className="text-slate-500 text-sm italic mb-6">Commonly used methods to manipulate and inspect strings.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
             {[
               { m: "toUpperCase()", d: "Converts to ALL CAPS" },
               { m: "toLowerCase()", d: "converts to all lower" },
               { m: "slice(start, end)", d: "Extracts a part of a string" },
               { m: "replace(val, new)", d: "Replaces a value with a new one" },
               { m: "trim()", d: "Removes whitespace from both sides" },
               { m: "split(char)", d: "Converts string into an array" }
             ].map((item, i) => (
               <div key={i} className="p-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                  <code className="text-primary font-bold text-sm">{item.m}</code>
                  <p className="text-xs text-slate-400 mt-1">{item.d}</p>
               </div>
             ))}
          </div>
        </div>
      );

 case 'Template Literals':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Template Literals</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Introduced in ES6, template literals allow for multi-line strings and string interpolation using backticks (`) and the <strong>${`{}`}</strong> syntax.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <h4 className="font-bold text-primary mb-2 text-xs">Traditional Concatenation</h4>
                <code className="text-[10px] text-slate-500">"Hello, " + name + "!"</code>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl ring-2 ring-primary/20">
                <h4 className="font-bold text-primary mb-2 text-xs">Template Literal</h4>
                <code className="text-[10px] text-primary">{`Hello, name!`}</code>
             </div>
          </div>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-yellow-400">
             <pre>
{`const user = "Koteshwar";
const greeting = \`Welcome back, \${user}!
How can I help you today?\`;`}
             </pre>
          </div>
        </div>
      );

    case 'Callbacks':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Callbacks</h2>
          <p className="text-slate-500">A function passed as an argument to another function.</p>
          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl font-mono text-xs">
             <pre>
{`function greet(name, callback) {
  console.log("Hi " + name);
  callback();
}`}
             </pre>
          </div>
        </div>
      );

   case 'Promises':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Promises</h2>
          <p className="text-slate-600 dark:text-slate-400">
            A <strong>Promise</strong> is an object representing the eventual completion or failure of an asynchronous operation and its resulting value.
          </p>
          
          <div className="flex flex-col items-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800">
             <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
                <div className="flex flex-col items-center">
                   <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center border-2 border-amber-400 text-amber-600 font-bold italic">P</div>
                   <span className="text-[10px] mt-2 font-bold text-amber-600 uppercase">Pending</span>
                </div>
                <div className="flex flex-col items-center">
                   <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center border-2 border-green-400 text-green-600 font-bold italic">F</div>
                   <span className="text-[10px] mt-2 font-bold text-green-600 uppercase">Fulfilled</span>
                </div>
                <div className="flex flex-col items-center">
                   <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center border-2 border-red-400 text-red-600 font-bold italic">R</div>
                   <span className="text-[10px] mt-2 font-bold text-red-600 uppercase">Rejected</span>
                </div>
             </div>
          </div>
          

          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>
{`const myPromise = new Promise((resolve, reject) => {
  if (success) resolve("Done!");
  else reject("Error!");
});

myPromise
  .then(res => console.log(res))
  .catch(err => console.error(err));`}
             </pre>
          </div>
        </div>
      );

      case 'async/await':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Async / Await</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Built on top of Promises, <strong>async/await</strong> provides a cleaner and more readable way to handle asynchronous code, making it look and behave more like synchronous code.
          </p>
          
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span><span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span></div>
             <pre className="text-blue-400">
{`async function fetchData() {
  try {
    let response = await fetch('https://api.example.com');
    let data = await response.json();
    console.log(data);
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}`}
             </pre>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> The <code className="font-bold">await</code> keyword can only be used inside an <code className="font-bold">async</code> function.
             </p>
          </div>
          
        </div>
      );
      case 'Arrow Functions':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Arrow Functions</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Arrow functions allow us to write shorter function syntax. They are anonymous and change how <code>this</code> binds in functions.
          </p>
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm">
             <pre className="text-green-400">
{`// ES5
var x = function(x, y) { return x * y; };

// ES6 (Arrow)
const x = (x, y) => x * y;`}
             </pre>
          </div>
          
        </div>
      );

    case 'Default Parameters':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Default Parameters</h2>
          <p className="text-slate-600 dark:text-slate-400">
            ES6 allows function parameters to have default values if no value or <code>undefined</code> is passed.
          </p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>
{`function greet(user = "Guest") {
  return "Hello " + user;
}

console.log(greet()); // "Hello Guest"
console.log(greet("Koteshwar")); // "Hello Koteshwar"`}
             </pre>
          </div>
        </div>
      );

    case 'Spread/Rest Operator (...)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Spread & Rest Operator</h2>
          <div className="space-y-4">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <h4 className="font-bold text-primary mb-1">Spread (Expand)</h4>
                <p className="text-xs text-slate-500 italic">Expands an array into individual elements.</p>
                <code className="text-[10px] text-blue-500 font-mono">const combined = [...arr1, ...arr2];</code>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <h4 className="font-bold text-primary mb-1">Rest (Collect)</h4>
                <p className="text-xs text-slate-500 italic">Collects multiple elements into an array.</p>
                <code className="text-[10px] text-green-500 font-mono">function sum(...args) [ ... ]</code>
             </div>
          </div>
          
        </div>
      );

    case 'Destructuring':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Destructuring</h2>
          <p className="text-slate-600 dark:text-slate-400">A syntax that allows us to unpack values from arrays or properties from objects into distinct variables.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-yellow-400">
             <pre>
{`// Object Destructuring
const person = { name: "Kote", age: 24 };
const { name, age } = person;

// Array Destructuring
const [first, second] = ["Red", "Green"];`}
             </pre>
          </div>
        </div>
      );

    case 'Closures':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Closures</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            A closure gives you access to an outer function's scope from an inner function. In JS, closures are created every time a function is created.
          </p>
          <div className="p-6 bg-purple-50 dark:bg-purple-900/10 border-l-4 border-purple-500 rounded-r-xl">
             <p className="text-sm font-bold text-purple-700">Function + Lexical Environment = Closure</p>
          </div>
          
        </div>
      );

    case 'Hoisting':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Hoisting</h2>
          <p className="text-slate-600 dark:text-slate-400">JavaScript's default behavior of moving declarations to the top of the current scope.</p>
          <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl border border-dashed border-slate-300 text-xs font-mono">
             <p>// Variables declared with var are hoisted but initialized as undefined.</p>
             <p>// let and const are hoisted but stay in the "Temporal Dead Zone".</p>
          </div>
        </div>
      );

case 'Prototype & Prototypal Inheritance':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Prototype & Inheritance</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            In JavaScript, objects have a special hidden property <code>[[Prototype]]</code> that is either null or references another object. This is the base of <strong>Prototypal Inheritance</strong>.
          </p>

          <div className="flex flex-col items-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800">
             <div className="flex flex-col gap-4 w-full max-w-xs">
                <div className="p-4 bg-white dark:bg-slate-800 border-2 border-blue-500 rounded-xl text-center shadow-sm">
                   <span className="font-bold text-blue-600">Child Object</span>
                   <p className="text-[10px] text-slate-400">__proto__ points to 👇</p>
                </div>
                <div className="flex justify-center"><div className="w-1 h-6 bg-slate-200 dark:bg-slate-700"></div></div>
                <div className="p-4 bg-white dark:bg-slate-800 border-2 border-green-500 rounded-xl text-center shadow-sm">
                   <span className="font-bold text-green-600">Parent Prototype</span>
                   <p className="text-[10px] text-slate-400">Contains shared methods</p>
                </div>
             </div>
          </div>
          

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm">
             <pre className="text-blue-300">
{`let animal = { eats: true };
let rabbit = { jumps: true };

rabbit.__proto__ = animal; // sets prototype

console.log(rabbit.eats); // true (inherited)`}
             </pre>
          </div>
        </div>
      );

   case 'The Event Loop':
      return (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">The Event Loop</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            JavaScript is single-threaded but performs non-blocking I/O operations by offloading tasks to the system kernel whenever possible.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-48">
             <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl flex flex-col items-center justify-center text-center">
                <span className="font-black text-blue-600 text-xs mb-2">CALL STACK</span>
                <p className="text-[9px] text-slate-500">Executes synchronous code</p>
             </div>
             <div className="flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
             </div>
             <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-2xl flex flex-col items-center justify-center text-center">
                <span className="font-black text-amber-600 text-xs mb-2">CALLBACK QUEUE</span>
                <p className="text-[9px] text-slate-500">Wait list for async tasks</p>
             </div>
          </div>
          

          <div className="p-6 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
             <h4 className="font-bold mb-2">Execution Order:</h4>
             <ol className="list-decimal pl-5 text-sm text-slate-600 dark:text-slate-400 space-y-2">
                <li>Execute script (Sync code).</li>
                <li>Async tasks (setTimeout, Fetch) go to Web APIs.</li>
                <li>Completed tasks enter the Callback Queue.</li>
                <li>Event Loop pushes them to Stack when it's empty.</li>
             </ol>
          </div>
        </div>
      );
      case 'Classes':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">JavaScript Classes</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Classes are a template for creating objects. They encapsulate data with code to work on that data.
          </p>
          
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">Class Syntax & Inheritance</span>
             </div>
             <pre className="text-blue-300">
{`class Animal {
  constructor(name) {
    this.name = name;
  }
  speak() {
    console.log(this.name + ' makes a noise.');
  }
}

class Dog extends Animal {
  speak() {
    console.log(this.name + ' barks.');
  }
}

let d = new Dog('Mitzie');
d.speak(); // Mitzie barks.`}
             </pre>
          </div>
          
        </div>
      );

    case 'Modules (import/export)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">JavaScript Modules</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Modules allow you to break your code into separate files, making it easier to maintain. You use <code>import</code> and <code>export</code> to share code between them.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="space-y-2">
                <div className="flex items-center gap-2 px-2">
                   <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                   <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">mathUtils.js</span>
                </div>
                <div className="bg-slate-900 rounded-xl p-4 font-mono text-xs text-green-400 border border-slate-800 shadow-lg">
                   <pre>{`// Exporting
export const add = (a, b) => a + b;
export const PI = 3.14;`}</pre>
                </div>
             </div>
             <div className="space-y-2">
                <div className="flex items-center gap-2 px-2">
                   <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                   <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">app.js</span>
                </div>
                <div className="bg-slate-900 rounded-xl p-4 font-mono text-xs text-blue-400 border border-slate-800 shadow-lg">
                   <pre>{`// Importing
import { add, PI } from './mathUtils.js';

console.log(add(10, PI));`}</pre>
                </div>
             </div>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> Modules always use <code>"use strict"</code> by default and have their own top-level scope.
             </p>
          </div>
          
        </div>
      )
      case 'try / catch / finally':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Error Handling</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            The <code>try...catch...finally</code> statement handles some or all of the errors that may occur in a block of code, while still running code.
          </p>
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm">
             <pre className="text-blue-300">
{`try {
  // Code that might throw an error
  let data = JSON.parse(invalidJson);
} catch (error) {
  // Code to handle the error
  console.log("Error caught: " + error.message);
} finally {
  // Code that always runs (e.g., closing a connection)
  console.log("Cleanup complete.");
}`}
             </pre>
          </div>
          
        </div>
      );

    case 'Throw Statement':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">The Throw Statement</h2>
          <p className="text-slate-600 dark:text-slate-400">The <code>throw</code> statement allows you to create custom errors. Technically you can throw anything (number, string, object).</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-red-400">
             <pre>{`if (age < 18) {\n  throw "Age must be at least 18";\n}`}</pre>
          </div>
        </div>
      );

    case 'Common Error Types':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Standard JS Errors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <span className="font-bold text-red-500">ReferenceError:</span> Using a variable that doesn't exist.
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <span className="font-bold text-red-500">TypeError:</span> Performing an operation on an incompatible type.
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <span className="font-bold text-red-500">SyntaxError:</span> Error in the code syntax.
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-xl">
                <span className="font-bold text-red-500">RangeError:</span> Numeric value outside allowed range.
             </div>
          </div>
        </div>
      );

    case 'Classes & Constructors':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Classes & Constructors</h2>
          <p className="text-slate-600 dark:text-slate-400">A class is a blueprint for creating objects. The <code>constructor</code> method is a special method for creating and initializing objects.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-slate-300">
             <pre>{`class Car {\n  constructor(brand) {\n    this.carname = brand;\n  }\n}\nlet myCar = new Car("Ford");`}</pre>
          </div>
          
        </div>
      );

   case 'Inheritance':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Inheritance</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Inheritance allows a class to inherit properties and methods from another class. In JavaScript, we use the <code>extends</code> keyword.
          </p>

          <div className="flex flex-col items-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800">
             <div className="grid grid-cols-1 gap-6 w-full max-w-xs text-center">
                <div className="p-4 bg-white dark:bg-slate-800 border-2 border-indigo-500 rounded-xl shadow-sm">
                   <span className="font-bold text-indigo-600">Super Class (Parent)</span>
                </div>
                <div className="flex justify-center -my-3"><div className="w-0.5 h-6 bg-slate-300"></div></div>
                <div className="p-4 bg-white dark:bg-slate-800 border-2 border-primary rounded-xl shadow-sm">
                   <span className="font-bold text-primary">Sub Class (Child)</span>
                </div>
             </div>
          </div>
          

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <pre className="text-green-400">
{`class Employee {
  login() { console.log("Logged in"); }
}

class Developer extends Employee {
  code() { console.log("Coding..."); }
}

const dev = new Developer();
dev.login(); // Inherited from Employee
dev.code();  // Developer's own method`}
             </pre>
          </div>
        </div>
      );

    case 'Polymorphism':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Polymorphism</h2>
          <p className="text-slate-600 dark:text-slate-400">Polymorphism means "many forms". It allows a child class to provide its own implementation of a method inherited from a parent class (Method Overriding).</p>
          <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-sm italic">Example: Both Dog and Cat classes inherit "makeSound()" from Animal, but each has its own implementation.</p>
          </div>
        </div>
      );

case 'Encapsulation':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Encapsulation</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            Encapsulation is the bundling of data and the methods that operate on that data into a single unit. It restricts direct access to some of an object's components.
          </p>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm">
             <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                <span className="text-yellow-500 text-[10px] font-bold uppercase">Private Fields (#)</span>
             </div>
             <pre className="text-yellow-200">
{`class Person {
  #name; // Private property
  
  constructor(name) {
    this.#name = name;
  }

  getName() { return this.#name; }
}

const p = new Person("Kote");
console.log(p.#name); // Syntax Error!`}
             </pre>
          </div>
          
          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-500 italic">Encapsulation helps in data hiding and security by making state changes predictable.</p>
          </div>
          
        </div>
      );
      case 'Custom Errors':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Custom Errors</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            You can create your own error classes by extending the built-in <code>Error</code> class. This allows you to include extra properties or specific logic for your app.
          </p>
          
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">Custom Error Class</span>
             </div>
             <pre className="text-blue-300">
{`class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = "ValidationError";
  }
}

throw new ValidationError("Invalid Email Format!");`}
             </pre>
          </div>
          
        </div>
      );

    case 'Abstraction':
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Abstraction</h2>
          <p className="text-slate-600 dark:text-slate-400">Hiding complex implementation details and showing only the essentials. It reduces complexity and increases efficiency.</p>
          <div className="flex justify-center py-6 bg-slate-100 dark:bg-slate-800 rounded-3xl">
             <div className="text-center">
                <div className="p-4 border-2 border-primary rounded-xl bg-white dark:bg-slate-900 font-bold mb-2">INTERFACE</div>
                <div className="text-xs text-slate-400">Essential Features Exposed</div>
             </div>
          </div>
        </div>
      );

    case 'Prototype Inheritance':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Prototype Inheritance</h2>
          <p className="text-slate-600 dark:text-slate-400">JavaScript objects inherit properties from a prototype object. All objects inherit from <code>Object.prototype</code>.</p>
          
        </div>
      );

    case 'ES Modules (import/export)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">ES Modules</h2>
          <p className="text-slate-600 dark:text-slate-400">Modules allow you to break your code into separate files using <code>export</code> and <code>import</code>.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-4 bg-slate-900 rounded-xl font-mono text-xs text-green-400">
                <span className="block text-slate-500 mb-2">// utils.js</span>
                <pre>{`export const add = (a, b) => a + b;`}</pre>
             </div>
             <div className="p-4 bg-slate-900 rounded-xl font-mono text-xs text-blue-400">
                <span className="block text-slate-500 mb-2">// main.js</span>
                <pre>{`import { add } from './utils.js';`}</pre>
             </div>
          </div>
        </div>
      );
      case 'Dynamic Imports':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Dynamic Imports</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Dynamic <code>import()</code> syntax allows you to load modules on demand (lazy loading), which helps in improving the initial load time of your application.
          </p>
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <pre className="text-blue-300">
{`if (userNeedsChart) {
  const { drawChart } = await import('./charts.js');
  drawChart();
}`}
             </pre>
          </div>
          <p className="text-xs text-slate-500 italic">Unlike static imports, dynamic imports can be used inside conditional blocks and return a Promise.</p>
        </div>
      );

    case 'CJS vs ESM (Node.js)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">CJS vs ESM</h2>
          <p className="text-slate-600 dark:text-slate-400">Node.js supports two module systems: CommonJS (CJS) and ECMAScript Modules (ESM).</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl">
                <h4 className="font-bold text-orange-600 mb-2">CommonJS (CJS)</h4>
                <code className="text-[10px] block mb-2">const lib = require('./lib');</code>
                <p className="text-[10px] text-slate-500">Synchronous loading. Default in older Node.js versions.</p>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl ring-2 ring-primary/10">
                <h4 className="font-bold text-blue-600 mb-2">ES Modules (ESM)</h4>
                <code className="text-[10px] block mb-2">import lib from './lib.js';</code>
                <p className="text-[10px] text-slate-500">Asynchronous loading. Standardized browser-friendly system.</p>
             </div>
          </div>
          
        </div>
      );

    case 'Fetch API':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Fetch API</h2>
          <p className="text-slate-600 dark:text-slate-400">The Fetch API provides a modern interface for fetching resources across the network. It returns a Promise.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-green-400">
             <pre>
{`fetch('https://api.example.com/data')
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(err => console.error(err));`}
             </pre>
          </div>
          
        </div>
      );

    case 'JSON':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">JSON (JS Object Notation)</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">A lightweight data-interchange format. de facto standard for web data transfer.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-4 bg-slate-900 rounded-xl font-mono text-xs text-yellow-400">
                <span className="text-slate-500 block mb-2">// JSON.stringify()</span>
                <p>Obj → String</p>
             </div>
             <div className="p-4 bg-slate-900 rounded-xl font-mono text-xs text-yellow-400">
                <span className="text-slate-500 block mb-2">// JSON.parse()</span>
                <p>String → Obj</p>
             </div>
          </div>
        </div>
      );

    case 'REST API & CRUD Operations':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">REST API & CRUD</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">REST (Representational State Transfer) uses HTTP methods to perform CRUD operations.</p>
          <div className="overflow-hidden border border-slate-200 dark:border-slate-800 rounded-2xl">
             <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                   <tr><th className="p-3">CRUD</th><th className="p-3">HTTP Method</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                   <tr><td className="p-3">Create</td><td className="p-3 font-bold text-green-600">POST</td></tr>
                   <tr><td className="p-3">Read</td><td className="p-3 font-bold text-blue-600">GET</td></tr>
                   <tr><td className="p-3">Update</td><td className="p-3 font-bold text-orange-600">PUT / PATCH</td></tr>
                   <tr><td className="p-3">Delete</td><td className="p-3 font-bold text-red-600">DELETE</td></tr>
                </tbody>
             </table>
          </div>
          
        </div>
      );

   case 'Headers, Status Codes, CORS':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">HTTP Basics</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              When a browser requests a page, it sends <strong>Headers</strong> to the server, and the server responds with <strong>Status Codes</strong> and its own headers.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
               <h4 className="font-bold text-primary mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary"></div> Common Status Codes
               </h4>
               <ul className="space-y-3">
                  <li className="flex justify-between items-center text-xs">
                     <span className="font-mono bg-green-100 dark:bg-green-900/30 text-green-600 px-2 py-1 rounded">200 OK</span>
                     <span className="text-slate-500 text-[10px]">Success</span>
                  </li>
                  <li className="flex justify-between items-center text-xs">
                     <span className="font-mono bg-blue-100 dark:bg-blue-900/30 text-blue-600 px-2 py-1 rounded">201 Created</span>
                     <span className="text-slate-500 text-[10px]">Resource Created</span>
                  </li>
                  <li className="flex justify-between items-center text-xs">
                     <span className="font-mono bg-amber-100 dark:bg-amber-900/30 text-amber-600 px-2 py-1 rounded">401 Unauthorized</span>
                     <span className="text-slate-500 text-[10px]">Auth Required</span>
                  </li>
                  <li className="flex justify-between items-center text-xs">
                     <span className="font-mono bg-red-100 dark:bg-red-900/30 text-red-600 px-2 py-1 rounded">404 Not Found</span>
                     <span className="text-slate-500 text-[10px]">Missing Resource</span>
                  </li>
                  <li className="flex justify-between items-center text-xs">
                     <span className="font-mono bg-red-100 dark:bg-red-900/30 text-red-600 px-2 py-1 rounded">500 Server Error</span>
                     <span className="text-slate-500 text-[10px]">Internal Issue</span>
                  </li>
               </ul>
            </div>

            <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
               <h4 className="font-bold text-primary mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary"></div> Key Headers
               </h4>
               <div className="space-y-3 text-[11px] font-mono">
                  <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                     <span className="text-blue-500">Content-Type:</span> application/json
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                     <span className="text-blue-500">Authorization:</span> Bearer &lt;token&gt;
                  </div>
                  <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">
                     <span className="text-blue-500">User-Agent:</span> Browser Info
                  </div>
               </div>
            </div>
          </div>
          

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white">CORS (Cross-Origin Resource Sharing)</h2>
            <p className="text-slate-600 dark:text-slate-400">
              CORS is a security mechanism that allows or restricts requested resources on a web page to be requested from another domain outside the domain from which the first resource was served.
            </p>
            
            <div className="flex flex-col items-center p-8 bg-red-50 dark:bg-red-900/10 border-2 border-dashed border-red-200 dark:border-red-800 rounded-3xl">
               <div className="flex items-center gap-4 text-xs font-bold mb-4">
                  <div className="px-3 py-2 bg-white dark:bg-slate-800 border rounded shadow-sm text-red-500">Domain A</div>
                  <div className="w-12 h-[2px] bg-red-300 relative">
                     <div className="absolute -top-1 right-0 w-2 h-2 border-t-2 border-r-2 border-red-300 rotate-45"></div>
                  </div>
                  <div className="px-3 py-2 bg-white dark:bg-slate-800 border rounded shadow-sm text-slate-400">Domain B</div>
               </div>
               <p className="text-[10px] text-red-600 font-black uppercase tracking-tighter text-center">
                  Blocked by CORS policy unless Domain B sends "Access-Control-Allow-Origin" Header
               </p>
            </div>
            
          </section>
        </div>
      );
      case 'Postman Basics':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Postman Basics</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Postman is a popular API client that makes it easy to create, share, test, and document APIs. It allows you to send HTTP requests with custom headers, bodies, and parameters, and view the response without writing any code.
          </p>

          <div className="flex flex-col items-center p-6 bg-slate-100 dark:bg-slate-800/50 rounded-3xl border border-slate-200 dark:border-slate-700">
             <div className="w-full bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                {/* Mock Postman UI */}
                <div className="bg-slate-100 dark:bg-slate-800 p-2 flex gap-2 border-b border-slate-200">
                   <div className="w-3 h-3 rounded-full bg-red-400"></div>
                   <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                   <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="p-4 flex gap-2">
                   <div className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded">GET</div>
                   <div className="flex-1 bg-slate-50 dark:bg-slate-800 border rounded px-2 py-1 text-[10px] text-slate-400">https://api.example.com/users</div>
                   <div className="px-4 py-1 bg-blue-600 text-white text-[10px] font-bold rounded">SEND</div>
                </div>
                <div className="p-4 border-t border-slate-100 dark:border-slate-800 h-24 overflow-hidden">
                   <p className="text-[10px] font-mono text-slate-400">{`{ "status": 200, "data": [...] }`}</p>
                </div>
             </div>
             <p className="mt-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Visual: Postman Request/Response Interface</p>
          </div>
          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2 italic">Request Building</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• Set HTTP methods (GET, POST, etc.)</li>
                   <li>• Add Query Params & Headers</li>
                   <li>• Configure Auth (Bearer Tokens, API Keys)</li>
                </ul>
             </div>
             <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2 italic">Testing & Debugging</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• Inspect Status Codes & Response Time</li>
                   <li>• View JSON/HTML response formats</li>
                   <li>• Save requests into Collections</li>
                </ul>
             </div>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border-l-4 border-orange-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
               <strong>Why Postman?</strong> It eliminates the need for <code>console.log</code> or writing temporary fetch scripts just to check if an endpoint is working correctly.
             </p>
          </div>
        </div>
      );
      case 'npm & package.json':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">NPM & Package.json</h2>
          <p className="text-slate-600 dark:text-slate-400">
            <strong>NPM</strong> is the world's largest software registry. <code>package.json</code> is the heart of any Node.js project, containing metadata and dependencies.
          </p>
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">package.json</div>
             <pre className="text-yellow-400">
{`{
  "name": "my-app",
  "version": "1.0.0",
  "dependencies": {
    "express": "^4.18.2"
  }
}`}
             </pre>
          </div>
        </div>
      );

    case 'Node modules':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">The node_modules Folder</h2>
          <p className="text-slate-600 dark:text-slate-400">
            When you run <code>npm install</code>, packages are downloaded into <code>node_modules</code>. You typically don't commit this folder to Git; it can be regenerated using <code>package.json</code>.
          </p>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-amber-700">Add <strong>node_modules/</strong> to your <code>.gitignore</code> file!</p>
          </div>
          
        </div>
      );

    case 'File System (fs)':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">File System (fs)</h2>
          <p className="text-slate-600 dark:text-slate-400">The Node.js file system module allows you to work with the file system on your computer.</p>
          <div className="bg-slate-900 rounded-xl p-5 font-mono text-sm text-blue-300">
             <pre>{`const fs = require('fs');\n\n// Read File\nfs.readFile('test.txt', 'utf8', (err, data) => {\n  console.log(data);\n});`}</pre>
          </div>
        </div>
      );

    case 'Events':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Events & EventEmitter</h2>
          <p className="text-slate-600 dark:text-slate-400">Node.js uses an event-driven architecture. The <code>EventEmitter</code> class is at the core of this system.</p>
          <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl font-mono text-xs">
             <p className="text-primary font-bold">emitter.on('event', listener);</p>
             <p className="text-primary font-bold">emitter.emit('event');</p>
          </div>
        </div>
      );

    case 'Streams':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Streams</h2>
          <p className="text-slate-600 dark:text-slate-400">Streams handle large amounts of data in chunks, without loading everything into memory at once. Perfect for video or large log files.</p>
          
        </div>
      );

 case 'Express.js basics':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Express.js Basics</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Express is a minimal and flexible Node.js web application framework that provides a robust set of features for web and mobile applications. It simplifies the process of building servers and handling routes.
          </p>

          <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <h4 className="font-bold text-blue-700 dark:text-blue-400 mb-2">Core Features:</h4>
             <ul className="text-sm space-y-2 text-slate-600 dark:text-slate-400">
                <li>• <strong>Middleware:</strong> Functions that have access to request and response objects.</li>
                <li>• <strong>Routing:</strong> Defines how an application responds to client requests (GET, POST).</li>
                <li>• <strong>Templates:</strong> Integrated with engines like EJS, Pug, etc.</li>
             </ul>
          </div>
          

          <div className="space-y-4">
            <h3 className="text-xl font-bold">Simple Express Server</h3>
            <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
               <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                  <span className="text-green-400 font-bold uppercase tracking-widest text-[10px]">app.js</span>
               </div>
               <pre className="text-blue-300">
{`const express = require('express');
const app = express();
const port = 3000;

// Basic Route
app.get('/', (req, res) => {
  res.send('Hello World!');
});

app.listen(port, () => {
  console.log(\`Server running at http://localhost:\${port}\`);
});`}
               </pre>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl">
                <h4 className="font-bold text-primary mb-1">Request (req)</h4>
                <p className="text-xs text-slate-500">Contains info like headers, query parameters, and body.</p>
             </div>
             <div className="p-4 border border-slate-200 dark:border-slate-800 rounded-xl">
                <h4 className="font-bold text-primary mb-1">Response (res)</h4>
                <p className="text-xs text-slate-500">Used to send data (HTML, JSON) back to the client.</p>
             </div>
          </div>
        </div>
      );

case 'Webpack':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Webpack</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Webpack is a powerful and highly configurable module bundler. It crawls your application, looking for <code>import</code> and <code>require</code> statements, and bundles all of those modules and their dependencies into one or more output files for the browser.
          </p>

          <div className="flex flex-col items-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800">
             <div className="flex items-center gap-4 w-full justify-between max-w-2xl">
                {/* Source Assets */}
                <div className="grid grid-cols-2 gap-2">
                   <div className="px-3 py-2 bg-yellow-100 text-yellow-700 rounded text-[10px] font-bold border border-yellow-200">.JS</div>
                   <div className="px-3 py-2 bg-blue-100 text-blue-700 rounded text-[10px] font-bold border border-blue-200">.CSS</div>
                   <div className="px-3 py-2 bg-green-100 text-green-700 rounded text-[10px] font-bold border border-green-200">.PNG</div>
                   <div className="px-3 py-2 bg-purple-100 text-purple-700 rounded text-[10px] font-bold border border-purple-200">.SASS</div>
                </div>

                {/* The Bundler */}
                <div className="flex flex-col items-center">
                   <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                      <div className="w-8 h-8 border-4 border-white/30 rounded-lg flex items-center justify-center">
                         <div className="w-4 h-4 bg-white rounded-sm"></div>
                      </div>
                   </div>
                   <span className="mt-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Webpack</span>
                </div>

                {/* Final Bundle */}
                <div className="flex flex-col gap-2">
                   <div className="px-4 py-3 bg-white dark:bg-slate-800 border-2 border-primary rounded-xl text-[10px] font-mono font-bold shadow-sm">bundle.js</div>
                </div>
             </div>
             <p className="mt-6 text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Dependency Graph ➔ Bundling Process ➔ Static Assets</p>
          </div>
          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Key Concepts</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• <strong>Entry:</strong> The first file Webpack looks at.</li>
                   <li>• <strong>Output:</strong> Where the final bundle is saved.</li>
                   <li>• <strong>Loaders:</strong> Transform non-JS files (CSS, Images).</li>
                   <li>• <strong>Plugins:</strong> Handle complex tasks like optimization.</li>
                </ul>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Why use it?</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                   It reduces the number of HTTP requests by merging files, minifies code to save space, and allows using modern features that browsers might not support yet.
                </p>
             </div>
          </div>
        </div>
      );

   case 'Babel':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Babel</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Babel is a popular JavaScript compiler that is mainly used to convert ECMAScript 2015+ (ES6+) code into a backwards-compatible version of JavaScript in current and older browsers or environments.
          </p>

          <div className="flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-200 dark:border-slate-800">
             <div className="flex items-center gap-6 w-full max-w-lg">
                <div className="flex-1 p-4 bg-white dark:bg-slate-800 border-2 border-primary rounded-2xl shadow-sm text-center">
                   <span className="text-[10px] font-black text-primary uppercase block mb-1">Input</span>
                   <code className="text-xs font-mono">ES6+ Code</code>
                   <p className="text-[9px] text-slate-400 mt-1">(Arrow functions, Classes)</p>
                </div>
                
                <div className="flex flex-col items-center">
                   <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg font-black text-slate-900">B</div>
                   <div className="h-4 w-[2px] bg-slate-200 dark:bg-slate-700"></div>
                </div>

                <div className="flex-1 p-4 bg-slate-100 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm text-center">
                   <span className="text-[10px] font-black text-slate-500 uppercase block mb-1">Output</span>
                   <code className="text-xs font-mono">ES5 Code</code>
                   <p className="text-[9px] text-slate-400 mt-1">(Plain functions, Prototypes)</p>
                </div>
             </div>
             <p className="mt-6 text-[10px] text-slate-400 uppercase font-bold tracking-widest">The Transpilation Process</p>
          </div>
          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Why Babel?</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• Use the latest JS features today.</li>
                   <li>• Ensure compatibility with IE11 and old browsers.</li>
                   <li>• Supports syntax extensions like <strong>JSX</strong> (for React).</li>
                </ul>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Key Tools</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• <strong>Presets:</strong> Sets of plugins (e.g., @babel/preset-env).</li>
                   <li>• <strong>Plugins:</strong> Individual transformation rules.</li>
                   <li>• <strong>Polyfills:</strong> Mimic missing features (e.g., Promises).</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Transformation Example</div>
             <pre className="text-yellow-400">
{`// ES6 Input
[1, 2, 3].map(n => n + 1);

// Babel ES5 Output
[1, 2, 3].map(function(n) {
  return n + 1;
});`}
             </pre>
          </div>
        </div>
      );

    case 'Vite':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Vite</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Vite is a next-generation frontend tool that focuses on speed and performance. It consists of two major parts: a dev server that serves source files over <strong>native ES modules</strong> and a build command that bundles code with <strong>Rollup</strong>.
          </p>

          <div className="flex flex-col items-center justify-center p-8 bg-indigo-50 dark:bg-indigo-950/20 rounded-3xl border border-indigo-100 dark:border-indigo-900/30">
             <div className="flex items-center gap-8 w-full max-w-lg">
                <div className="flex-1 text-center">
                   <div className="text-[10px] font-black text-indigo-400 mb-2 uppercase">Traditional (Webpack)</div>
                   <div className="p-3 bg-white dark:bg-slate-800 border rounded-xl text-[10px] opacity-50">Bundle ➔ Ready</div>
                </div>
                <div className="text-2xl">⚡</div>
                <div className="flex-1 text-center">
                   <div className="text-[10px] font-black text-primary mb-2 uppercase">Vite Way</div>
                   <div className="p-3 bg-white dark:bg-slate-800 border-2 border-primary rounded-xl text-[10px] font-bold">Ready ➔ Dynamic Import</div>
                </div>
             </div>
             <p className="mt-6 text-[10px] text-slate-400 uppercase font-bold tracking-widest text-center">Vite starts the server instantly by not bundling everything upfront.</p>
          </div>
          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm hover:border-primary/40 transition-colors">
                <h4 className="font-bold text-primary mb-2 italic">Key Advantages</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• <strong>Instant Server Start:</strong> Uses Native ESM.</li>
                   <li>• <strong>Lightning Fast HMR:</strong> Only replaces changed modules.</li>
                   <li>• <strong>Optimized Build:</strong> Uses Rollup for production.</li>
                   <li>• <strong>Rich Features:</strong> Out-of-the-box support for TypeScript & JSX.</li>
                </ul>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm hover:border-primary/40 transition-colors">
                <h4 className="font-bold text-primary mb-2 italic">How it Works</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                   Vite pre-bundles dependencies using <strong>esbuild</strong> (written in Go, which is 10-100x faster than JS-based bundlers) and serves source code via browser-native ES Modules.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Quick Start Command</div>
             <pre className="text-green-400">
{`# Create a new Vite project
npm create vite@latest my-app

# Choose framework (React, Vue, etc.)
cd my-app
npm install
npm run dev`}
             </pre>
          </div>
        </div>
      );

    case 'ESLint & Prettier':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">ESLint & Prettier</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            These tools are essential for maintaining code quality and consistency across a team. While <strong>ESLint</strong> focuses on finding potential bugs, <strong>Prettier</strong> focuses on how the code looks.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* ESLint Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-t-4 border-t-red-500">
                <h4 className="font-bold text-red-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-red-500"></div> ESLint (Linter)
                </h4>
                <p className="text-xs text-slate-500 mb-4 italic">"Finds problems in your code."</p>
                <ul className="text-xs space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• Catches syntax errors.</li>
                   <li>• Enforces best practices (no unused vars).</li>
                   <li>• Highly configurable with <code>.eslintrc</code>.</li>
                </ul>
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg font-mono text-[10px] text-red-500">
                   Error: 'x' is defined but never used.
                </div>
             </div>

             {/* Prettier Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm border-t-4 border-t-green-500">
                <h4 className="font-bold text-green-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-green-500"></div> Prettier (Formatter)
                </h4>
                <p className="text-xs text-slate-500 mb-4 italic">"Enforces a consistent style."</p>
                <ul className="text-xs space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• Handles tabs vs spaces.</li>
                   <li>• Manages line lengths & semicolons.</li>
                   <li>• Saves time by auto-formatting on save.</li>
                </ul>
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg font-mono text-[10px] text-green-600">
                   Success: Code formatted automatically.
                </div>
             </div>
          </div>
          

          <div className="space-y-4">
             <h3 className="text-xl font-bold">Why use them together?</h3>
             <div className="flex flex-col items-center p-8 bg-slate-50 dark:bg-slate-800 rounded-3xl border-2 border-dashed border-slate-300">
                <div className="flex items-center gap-4">
                   <div className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-bold">Linter</div>
                   <span className="text-2xl">+</span>
                   <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-bold">Formatter</div>
                   <span className="text-2xl">=</span>
                   <div className="px-4 py-2 bg-primary text-white rounded-lg font-bold">Clean Code</div>
                </div>
                <p className="mt-4 text-[10px] text-slate-400 uppercase font-bold tracking-widest text-center">
                   Prevents bugs while making the codebase readable for everyone.
                </p>
             </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Typical VS Code Setup</div>
             <pre className="text-yellow-400">
{`// settings.json
"editor.formatOnSave": true,
"editor.defaultFormatter": "esbenp.prettier-vscode",
"editor.codeActionsOnSave": {
  "source.fixAll.eslint": true
}`}
             </pre>
          </div>
        </div>
      );
      case 'Jest':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Jest</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Jest is a delightful JavaScript Testing Framework with a focus on simplicity. It works with projects using Babel, TypeScript, Node, React, Angular, Vue, and more. It is primarily used for <strong>Unit Testing</strong>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800 rounded-2xl">
              <span className="block text-green-600 font-bold text-sm">Fast & Safe</span>
              <p className="text-[10px] text-slate-500">Runs tests in parallel</p>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-2xl">
              <span className="block text-blue-600 font-bold text-sm">Code Coverage</span>
              <p className="text-[10px] text-slate-500">Built-in coverage reports</p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800 rounded-2xl">
              <span className="block text-purple-600 font-bold text-sm">Easy Mocking</span>
              <p className="text-[10px] text-slate-500">Mock functions & modules</p>
            </div>
          </div>
          

          <div className="space-y-4">
            <h3 className="text-xl font-bold">Writing Your First Test</h3>
            <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
               <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                  <span className="text-yellow-400 font-bold uppercase tracking-widest text-[10px]">sum.test.js</span>
               </div>
               <pre className="text-blue-300">
{`const sum = (a, b) => a + b;

test('adds 1 + 2 to equal 3', () => {
  expect(sum(1, 2)).toBe(3);
});`}
               </pre>
            </div>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary"></div> Common Matchers
             </h4>
             <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-xs text-slate-600 dark:text-slate-400">
                <li>• <code>toBe(value)</code> - Exact equality</li>
                <li>• <code>toEqual(obj)</code> - Deep equality (objects/arrays)</li>
                <li>• <code>toContain(item)</code> - Checks array/iterable</li>
                <li>• <code>toThrow(error)</code> - Checks if function throws</li>
             </ul>
          </div>
          

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Run Tests</div>
             <pre className="text-green-400">
{`# Run all tests
npm test

# Run with coverage report
npm test -- --coverage`}
             </pre>
          </div>
        </div>
      );

      case 'Mocha/Chai':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Mocha & Chai</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Mocha is a feature-rich JavaScript test framework running on Node.js and in the browser, while Chai is a BDD/TDD assertion library that can be paired with any javascript testing framework. Unlike Jest, these are separate tools that you combine to create a testing environment.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-2">Mocha (The Runner)</h4>
                <p className="text-xs text-slate-500">Provides the structure for writing tests (describe, it hooks) and executes them.</p>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2">Chai (The Asserter)</h4>
                <p className="text-xs text-slate-500">Provides the 'expect', 'should', and 'assert' styles to verify your code results.</p>
             </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-bold">Expect Syntax (BDD Style)</h3>
            <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
               <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                  <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">test.js</span>
               </div>
               <pre className="text-blue-300">
{`const { expect } = require('chai');

describe('String Tests', function() {
  it('should be a string', function() {
    expect('Koteshwar').to.be.a('string');
  });

  it('should match the value', function() {
    expect('hello').to.equal('hello');
  });
});`}
               </pre>
            </div>
          </div>
          
          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why use Mocha/Chai?</strong> While Jest is an "all-in-one" solution, Mocha is more flexible and allows you to choose your own assertion library, mocking library (like Sinon), and more.
             </p>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Installation</div>
             <pre className="text-green-400">
{`npm install --save-dev mocha chai`}
             </pre>
          </div>
        </div>
      );

      case 'Unit Testing Basics':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Unit Testing Basics</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Unit testing is the practice of testing small, isolated pieces of code (units) to ensure they work correctly. This helps catch bugs early and makes code easier to refactor.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">What is a "Unit"?</h4>
                <p className="text-xs text-slate-500">
                   A unit is the smallest testable part of an application, like a single function, method, or class. Testing it in isolation means without any external dependencies like databases or APIs.
                </p>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Why Unit Test?</h4>
                <ul className="text-xs space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Catch Bugs Early:</strong> Find issues during development.</li>
                   <li>• <strong>Facilitate Change:</strong> Refactor code with confidence.</li>
                   <li>• <strong>Documentation:</strong> Tests show how code is intended to work.</li>
                </ul>
             </div>
          </div>
          
          <div className="space-y-4">
             <h3 className="text-xl font-bold">The AAA Pattern</h3>
             <p className="text-sm text-slate-500">Most unit tests follow this simple three-step structure:</p>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200">
                   <span className="font-black text-blue-500 block">1. ARRANGE</span>
                   <p className="text-[10px] mt-1 text-slate-400">Setup the test data and environment.</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200">
                   <span className="font-black text-green-500 block">2. ACT</span>
                   <p className="text-[10px] mt-1 text-slate-400">Execute the function being tested.</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200">
                   <span className="font-black text-purple-500 block">3. ASSERT</span>
                   <p className="text-[10px] mt-1 text-slate-400">Verify the result matches expectations.</p>
                </div>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Aim for high code coverage, but focus on testing the logic that is most critical to your application's functionality.
             </p>
          </div>
        </div>
      );
      case 'XSS':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">XSS (Cross-Site Scripting)</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              XSS is a vulnerability where an attacker injects malicious scripts into a web page viewed by other users. The #1 rule to prevent this is to <strong>never trust user input</strong> and always sanitize or escape it before rendering it on a page.
            </p>
          </section>

          {/* Vulnerable Example */}
          <div className="rounded-3xl border-2 border-red-100 dark:border-red-900/30 overflow-hidden">
             <div className="bg-red-50 dark:bg-red-900/10 px-6 py-3 border-b border-red-100 dark:border-red-900/30">
                <span className="text-red-600 font-bold text-xs uppercase tracking-widest">Vulnerable Code</span>
             </div>
             <div className="bg-slate-900 p-6 font-mono text-xs leading-relaxed">
                <pre className="text-slate-400">
{`const userInput = '<img src="x" onerror="alert(\\'XSS Attack!\\')">';
const container = document.getElementById('comment-section');

// ❌ Dangerous: Using innerHTML executes malicious scripts
container.innerHTML = userInput;`}
                </pre>
             </div>
          </div>

          {/* Secure Example */}
          <div className="rounded-3xl border-2 border-green-100 dark:border-green-900/30 overflow-hidden">
             <div className="bg-green-50 dark:bg-green-900/10 px-6 py-3 border-b border-green-100 dark:border-green-900/30">
                <span className="text-green-600 font-bold text-xs uppercase tracking-widest">Secure Code</span>
             </div>
             <div className="bg-slate-900 p-6 font-mono text-xs leading-relaxed">
                <pre className="text-slate-400">
{`const userInput = '<img src="x" onerror="alert(\\'XSS Attack!\\')">';
const container = document.getElementById('comment-section');

// ✅ Safe: Using textContent treats the input as plain text
container.textContent = userInput;`}
                </pre>
             </div>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary"></div> Prevention Tips
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-green-500 font-bold">✓</span>
                   <span>Use <code>textContent</code> or <code>innerText</code> instead of <code>innerHTML</code>.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-green-500 font-bold">✓</span>
                   <span>Always sanitize user-generated content using libraries like DOMPurify.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-green-500 font-bold">✓</span>
                   <span>Implement a strong Content Security Policy (CSP) header.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'CSRF':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">CSRF (Cross-Site Request Forgery)</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              CSRF is an attack that tricks a logged-in user into submitting a malicious request to a web application. It inherits the identity and privileges of the victim to perform an undesired function on their behalf.
            </p>
          </section>

          <div className="flex flex-col items-center p-8 bg-amber-50 dark:bg-amber-900/10 border-2 border-dashed border-amber-200 dark:border-amber-800 rounded-3xl">
             <div className="flex items-center gap-6 text-xs font-bold mb-4">
                <div className="flex flex-col items-center gap-2">
                   <div className="px-3 py-2 bg-white dark:bg-slate-800 border rounded shadow-sm">User Browser</div>
                   <span className="text-[10px] text-green-500">(Authenticated)</span>
                </div>
                <div className="w-16 h-[2px] bg-red-400 relative">
                   <div className="absolute -top-1.5 right-0 w-3 h-3 border-t-2 border-r-2 border-red-400 rotate-45"></div>
                </div>
                <div className="px-3 py-2 bg-red-500 text-white border rounded shadow-md">Bank API</div>
             </div>
             <p className="text-[10px] text-amber-700 font-black uppercase tracking-tighter text-center">
                Attacker triggers a request (e.g., /transfer-money) without user's knowledge.
             </p>
          </div>
          
          <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
             <h4 className="font-bold mb-3 flex items-center gap-2 text-primary">
                <div className="w-2 h-6 bg-primary rounded-full"></div> How to Protect?
             </h4>
             <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-3">
                   <div className="mt-1 w-5 h-5 flex-shrink-0 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center font-bold text-[10px]">1</div>
                   <p><strong>Anti-CSRF Tokens:</strong> Use unique, unpredictable tokens for each session. The server validates this token before processing requests.</p>
                </li>
                <li className="flex gap-3">
                   <div className="mt-1 w-5 h-5 flex-shrink-0 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center font-bold text-[10px]">2</div>
                   <p><strong>SameSite Cookie Attribute:</strong> Set cookies to <code>SameSite=Lax</code> or <code>Strict</code> to prevent them from being sent with cross-site requests.</p>
                </li>
                <li className="flex gap-3">
                   <div className="mt-1 w-5 h-5 flex-shrink-0 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full flex items-center justify-center font-bold text-[10px]">3</div>
                   <p><strong>Custom Request Headers:</strong> Verify headers like <code>X-Requested-With</code> which are not easily forged by simple HTML forms.</p>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Sanitization':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">Sanitization</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Input sanitization is the process of cleaning and filtering user input to prevent malicious data from entering your system. This is a crucial step in preventing XSS, SQL Injection, and other vulnerabilities.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">How it Works</h4>
                <p className="text-xs text-slate-500">
                   Sanitization involves removing or encoding dangerous characters (like <code>&lt;</code>, <code>&gt;</code>, or <code>&quot;</code>) from user-provided strings before they are processed or stored.
                </p>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Key Libraries</h4>
                <ul className="text-xs space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>DOMPurify:</strong> For cleaning HTML.</li>
                   <li>• <strong>validator.js:</strong> For string validation and sanitization.</li>
                   <li>• <strong>sanitize-html:</strong> For server-side Node.js cleanup.</li>
                </ul>
             </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Code Example: Using DOMPurify</div>
             <pre className="text-blue-300">
{`import DOMPurify from 'dompurify';

const dirty = '<img src=x onerror=alert(1)>';
const clean = DOMPurify.sanitize(dirty);

// clean is now safe: '<img src="x">'
container.innerHTML = clean;`}
             </pre>
          </div>
          
          <div className="p-4 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Rule of Thumb:</strong> Always sanitize data on the <strong>Server-side</strong> before saving to a database, and on the <strong>Client-side</strong> before rendering to the DOM.
             </p>
          </div>
        </div>
      );
      case 'Secure Cookies':
      return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Secure Cookies</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            When setting cookies for authentication, it is essential to use specific security flags to protect them from being stolen by XSS attacks or sent in cross-site requests.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">HttpOnly</h4>
                <p className="text-[10px] text-slate-500">Prevents JavaScript from accessing the cookie, mitigating XSS risks.</p>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Secure</h4>
                <p className="text-[10px] text-slate-500">Ensures the cookie is only sent over encrypted HTTPS connections.</p>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">SameSite</h4>
                <p className="text-[10px] text-slate-500">Restricts cookies from being sent with cross-site requests (CSRF protection).</p>
             </div>
          </div>
          
          <div className="bg-slate-900 rounded-2xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Implementation Example (Node.js)</div>
             <pre className="text-green-400">
{`res.cookie('sessionID', 'xyz123', {
  httpOnly: true,
  secure: true,
  sameSite: 'Strict',
  maxAge: 3600000 // 1 hour
});`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> Without these flags, sensitive session data can be easily intercepted by attackers using malicious scripts or network sniffing.
             </p>
          </div>
        </div>
      );
      case 'Token-based Auth (JWT)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">Token-based Auth (JWT)</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              JSON Web Tokens (JWT) are a standard for securely transmitting information between parties as a JSON object. They are commonly used for authentication. After a user logs in, the server creates a JWT and sends it to the client.
            </p>
          </section>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-4 text-primary">JWT Authentication Flow</h4>
             <div className="space-y-4 font-mono text-xs text-slate-600 dark:text-slate-400">
                <div className="flex gap-3">
                   <span className="text-blue-500 font-bold">1.</span>
                   <p><strong>Client:</strong> POST /login {`{username, password}`}</p>
                </div>
                <div className="flex gap-3">
                   <span className="text-blue-500 font-bold">2.</span>
                   <p><strong>Server:</strong> Verifies credentials, creates JWT, returns token</p>
                </div>
                <div className="flex gap-3">
                   <span className="text-blue-500 font-bold">3.</span>
                   <p><strong>Client:</strong> Stores JWT (e.g., in localStorage)</p>
                </div>
                <div className="flex gap-3">
                   <span className="text-blue-500 font-bold">4.</span>
                   <p><strong>Client:</strong> GET /protected-data (Header → Authorization: Bearer {`<the_jwt_token>`})</p>
                </div>
                <div className="flex gap-3">
                   <span className="text-blue-500 font-bold">5.</span>
                   <p><strong>Server:</strong> Verifies JWT signature, returns protected data</p>
                </div>
             </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Why JWT?</h4>
                <ul className="text-xs space-y-2 text-slate-500">
                   <li>• <strong>Stateless:</strong> Server doesn't need to store session data.</li>
                   <li>• <strong>Scalable:</strong> Easy to use across multiple servers.</li>
                   <li>• <strong>Secure:</strong> Information is digitally signed.</li>
                </ul>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Structure</h4>
                <p className="text-xs text-slate-500">
                   A JWT consists of three parts separated by dots: <strong>Header</strong>, <strong>Payload</strong>, and <strong>Signature</strong>.
                </p>
             </div>
          </div>
        </div>
      );
      case 'Top-Level Await':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-400">
              Top-Level Await
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Top-level <code>await</code> allows you to use the <code>await</code> keyword in modules without needing an enclosing <code>async</code> function. This is extremely useful for initializing resources, fetching configuration, or loading dependencies when a module is first loaded.
            </p>
          </section>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed relative overflow-hidden">
             <div className="absolute top-0 right-0 p-4 opacity-10">
                <span className="text-4xl">🚀</span>
             </div>
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">data-fetcher.js</span>
             </div>
             <pre className="text-blue-300">
{`// No async wrapper needed!
const response = await fetch('https://api.example.com/data');
const data = await response.json();

export default data;`}
             </pre>
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-teal-400 font-bold uppercase tracking-widest text-[10px]">main.js</span>
             </div>
             <pre className="text-teal-300">
{`// The main module waits for data-fetcher.js to resolve 
// before it starts executing its own code.
import myData from './data-fetcher.js';

console.log(myData);`}
             </pre>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Key Benefit</h4>
                <p className="text-xs text-slate-500">
                   Eliminates the "boilerplate" of wrapping code in async IIFEs (Immediately Invoked Function Expressions) just to use await at the start of a file.
                </p>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-amber-500 mb-2 italic">Important Note</h4>
                <p className="text-xs text-slate-500">
                   Top-level await only works in <strong>Modules</strong>. It will not work in regular classic scripts.
                </p>
             </div>
          </div>
        </div>
      );
      case 'Optional Chaining (?.)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500">
              Optional Chaining (?.)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The optional chaining operator (<code>?.</code>) permits reading the value of a property located deep within a chain of connected objects without having to expressly validate that each reference in the chain is valid. If a reference is <code>null</code> or <code>undefined</code>, the expression short-circuits and returns <code>undefined</code> instead of throwing an error.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Problem Case */}
             <div className="rounded-3xl border-2 border-red-100 dark:border-red-900/30 overflow-hidden shadow-sm">
                <div className="bg-red-50 dark:bg-red-900/10 px-6 py-3 border-b border-red-100 dark:border-red-900/30">
                   <span className="text-red-600 font-bold text-xs uppercase tracking-widest">Without Optional Chaining</span>
                </div>
                <div className="bg-slate-900 p-6 font-mono text-xs leading-relaxed h-full">
                   <pre className="text-slate-400">
{`const user = { name: "Alex" };

// ❌ Throws TypeError: 
// Cannot read property 'street' of undefined
const street = user.address.street;`}
                   </pre>
                </div>
             </div>

             {/* Solution Case */}
             <div className="rounded-3xl border-2 border-green-100 dark:border-green-900/30 overflow-hidden shadow-sm">
                <div className="bg-green-50 dark:bg-green-900/10 px-6 py-3 border-b border-green-100 dark:border-green-900/30">
                   <span className="text-green-600 font-bold text-xs uppercase tracking-widest">With Optional Chaining</span>
                </div>
                <div className="bg-slate-900 p-6 font-mono text-xs leading-relaxed h-full">
                   <pre className="text-green-400">
{`const user = { name: "Alex" };

// ✅ Safe: returns undefined
const street = user?.address?.street;

console.log(street); // undefined`}
                   </pre>
                </div>
             </div>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-primary rounded-full"></div> Why use it?
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span><strong>Cleaner Code:</strong> No more long <code>user && user.address && user.address.street</code> checks.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span><strong>Safety:</strong> Prevents application crashes when dealing with dynamic data from APIs.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span><strong>Short-circuiting:</strong> If the left part is null/undefined, it stops evaluating the rest.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Nullish Coalescing (??)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-blue-500">
              Nullish Coalescing (??)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The nullish coalescing operator (<code>??</code>) is a logical operator that returns its right-hand side operand when its left-hand side operand is <code>null</code> or <code>undefined</code>, and otherwise returns its left-hand side operand.
            </p>
          </section>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-yellow-400 font-bold uppercase tracking-widest text-[10px]">Logic Comparison</span>
             </div>
             <pre className="text-yellow-200">
{`const count = 0;

// ❌ Using OR operator (||) - incorrect result
// Returns 10 because 0 is "falsy"
const quantity_or = count || 10;
console.log(quantity_or); // 10

// ✅ Using nullish coalescing (??) - correct result
// Returns 0 because 0 is NOT null or undefined
const quantity_nullish = count ?? 10;
console.log(quantity_nullish); // 0`}
             </pre>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-primary rounded-full"></div> Why it's a safer alternative
             </h4>
             <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                The <code>||</code> operator can trigger unexpected results on falsy values like <code>0</code>, <code>""</code>, or <code>false</code>. Nullish coalescing is safer because it specifically checks for "nullish" values only.
             </p>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-mono">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
                   <span className="text-red-500">OR (||):</span> Checks for <strong>Falsy</strong> (0, "", false, null, undefined)
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-primary/20">
                   <span className="text-green-500">Nullish (??):</span> Checks for <strong>Nullish</strong> (null, undefined)
                </div>
             </div>
          </div>
        </div>
      );
      case 'Private Class Fields (#)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">
              Private Class Fields (#)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Modern classes allow for truly private fields and methods using a <code>#</code> prefix, ensuring they cannot be accessed from outside the class instance. This significantly strengthens encapsulation in JavaScript.
            </p>
          </section>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-pink-400 font-bold uppercase tracking-widest text-[10px]">Bank.js</span>
             </div>
             <pre className="text-pink-200">
{`class BankAccount {
  #balance = 0; // Private field

  constructor(owner) {
    this.owner = owner;
  }

  deposit(amount) {
    this.#balance += amount;
    console.log(\`New balance: \${this.#balance}\`);
  }
}

const account = new BankAccount("Koteshwar");
account.deposit(100);

// ❌ Syntax Error: Private field '#balance' must be 
// declared in an enclosing class
console.log(account.#balance); `}
             </pre>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-primary rounded-full"></div> Key Features
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-purple-500 font-bold">•</span>
                   <span><strong>Enforced Privacy:</strong> Unlike the <code>_variable</code> convention, <code>#</code> is enforced by the JS engine.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-purple-500 font-bold">•</span>
                   <span><strong>Encapsulation:</strong> Keeps internal state hidden, exposing only necessary methods.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Promise.any()':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-500">
              Promise.any()
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              <code>Promise.any()</code> takes an iterable of Promises and returns a single Promise that fulfills as soon as any of the promises in the iterable fulfills, with the value of that promise. If all promises reject, it rejects with an <code>AggregateError</code>.
            </p>
          </section>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">Fastest Response Example</span>
             </div>
             <pre className="text-cyan-300">
{`const p1 = new Promise((resolve, reject) => setTimeout(reject, 100, 'P1 failed'));
const p2 = new Promise((resolve) => setTimeout(resolve, 200, 'P2 success'));
const p3 = new Promise((resolve) => setTimeout(resolve, 300, 'P3 success'));

try {
  const fastest = await Promise.any([p1, p2, p3]);
  console.log(fastest); // "P2 success" (P1 was faster but rejected)
} catch (error) {
  console.log(error); // AggregateError if ALL fail
}`}
             </pre>
          </div>

          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Key Difference</h4>
                <p className="text-xs text-slate-500">
                   Unlike <code>Promise.race()</code>, which settles as soon as the first promise (resolved or rejected) finishes, <code>Promise.any()</code> waits for the first <strong>fulfillment</strong>.
                </p>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-amber-500 mb-2 italic">AggregateError</h4>
                <p className="text-xs text-slate-500">
                   If every promise in the list fails, Jest/Mocha will show an <code>AggregateError</code>, which groups all individual rejection reasons together.
                </p>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Use Case:</strong> Fetching data from multiple mirror servers or APIs and using the response from the one that returns successfully first.
             </p>
          </div>
        </div>
      );
      case 'WeakRef & FinalizationRegistry':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-teal-500">
              WeakRef & FinalizationRegistry
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              These are advanced features for managing memory. <code>WeakRef</code> holds a "weak" reference to an object, which doesn't prevent it from being garbage collected. <code>FinalizationRegistry</code> lets you register a callback to be invoked when an object is garbage collected.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* WeakRef Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-teal-500"></div> WeakRef
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                   Allows you to keep a reference to an object while still allowing the Garbage Collector to reclaim that object if no strong references remain.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-teal-300">
                   <pre>{`let obj = { name: "cache" };\nconst ref = new WeakRef(obj);\n\n// Accessing later\nconst cachedObj = ref.deref();\nif (cachedObj) console.log(cachedObj.name);`}</pre>
                </div>
             </div>

             {/* FinalizationRegistry Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-green-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-green-500"></div> FinalizationRegistry
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                   Registers a callback that runs after an object has been garbage collected. Useful for cleaning up associated resources.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-green-300">
                   <pre>{`const registry = new FinalizationRegistry((heldValue) => {\n  console.log(\`Object \${heldValue} was GCed\`);\n});\n\nregistry.register(obj, "MyData");`}</pre>
                </div>
             </div>
          </div>

          
          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Warning:</strong> Use these features sparingly. Correct use of WeakRefs and FinalizationRegistry is tricky, and relying on garbage collection timing is generally discouraged in application logic.
             </p>
          </div>
        </div>
      );
      case 'Web Workers':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Web Workers
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Web Workers provide a simple means for web content to run scripts in background threads. The worker thread can perform tasks without interfering with the user interface, making the main page more responsive. This is ideal for heavy computations like image processing or big data filtering.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Main Thread */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-blue-500"></div> Main Thread (UI)
                </h4>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300 mb-4">
                   <pre>{`const worker = new Worker('worker.js');\n\nworker.postMessage('Start calculation');\n\nworker.onmessage = (e) => {\n  console.log('Result:', e.data);\n};`}</pre>
                </div>
                <p className="text-[10px] text-slate-500 italic">Handles user clicks, rendering, and animations.</p>
             </div>

             {/* Worker Thread */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm ring-2 ring-indigo-500/20">
                <h4 className="font-bold text-indigo-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-indigo-500"></div> Worker Thread (worker.js)
                </h4>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-indigo-300 mb-4">
                   <pre>{`onmessage = (e) => {\n  // Heavy work here\n  const result = heavyTask(e.data);\n  postMessage(result);\n};`}</pre>
                </div>
                <p className="text-[10px] text-slate-500 italic">Runs in the background. No access to the DOM.</p>
             </div>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-primary rounded-full"></div> Key Limitations
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-red-500 font-bold">!</span>
                   <span><strong>No DOM Access:</strong> Workers cannot directly manipulate HTML or CSS.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-red-500 font-bold">!</span>
                   <span><strong>Restricted APIs:</strong> They cannot use <code>window</code>, <code>document</code>, or <code>parent</code>.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-green-500 font-bold">✓</span>
                   <span><strong>Supported:</strong> They CAN use <code>fetch</code>, <code>WebSockets</code>, and <code>IndexedDB</code>.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Service Workers (PWA)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Service Workers (PWA)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A service worker is a script that your browser runs in the background, separate from a web page. It's a core component of Progressive Web Apps (PWAs), enabling features like offline capabilities, push notifications, and background sync by intercepting network requests.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-orange-500"></div> Key Capabilities
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Offline Mode:</strong> Serve cached content when there's no internet.</li>
                   <li>• <strong>Intercept Requests:</strong> Act as a proxy between the app and the network.</li>
                   <li>• <strong>Background Sync:</strong> Defer actions until the user has stable connectivity.</li>
                   <li>• <strong>Push Notifications:</strong> Send updates even when the app is not active.</li>
                </ul>
             </div>

             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3">Lifecycle Events</h4>
                <div className="space-y-2 text-[10px] font-mono">
                   <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded border-l-4 border-yellow-400">1. Registration</div>
                   <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded border-l-4 border-blue-400">2. Installation</div>
                   <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded border-l-4 border-green-400">3. Activation</div>
                </div>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Basic Registration (main.js)</div>
             <pre className="text-orange-300">
{`if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(reg => console.log('Service Worker Registered!', reg))
      .catch(err => console.log('Registration failed:', err));
  });
}`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 italic">
               <strong>Important:</strong> Service workers only run over <strong>HTTPS</strong> (except for localhost) for security reasons, as they can intercept all network traffic.
             </p>
          </div>
        </div>
      );
      case 'WebRTC Basics':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-500">
              WebRTC Basics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              WebRTC (Web Real-Time Communication) is an open-source project providing browsers and mobile applications with real-time communication capabilities via simple APIs. It enables peer-to-peer audio, video, and data sharing directly between browsers without needing an intermediary server for the data itself.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-2 text-xs uppercase">MediaStream</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Accesses camera and microphone streams from the user's device.</p>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-2 text-xs uppercase">RTCPeerConnection</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Handles stable and efficient peer-to-peer communication of audio/video.</p>
             </div>
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-2 text-xs uppercase">RTCDataChannel</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Enables high-performance, low-latency transfer of arbitrary data.</p>
             </div>
          </div>

          
          <div className="space-y-4">
             <h3 className="text-xl font-bold">How it Works (The Flow)</h3>
             <div className="p-6 bg-slate-900 rounded-3xl border border-slate-800 font-mono text-xs text-emerald-400">
                <ol className="space-y-3">
                   <li className="flex gap-3">
                      <span className="text-slate-500">1.</span>
                      <span>Signaling: Peers exchange metadata to find each other.</span>
                   </li>
                   <li className="flex gap-3">
                      <span className="text-slate-500">2.</span>
                      <span>Connecting: Peer-to-peer connection is established.</span>
                   </li>
                   <li className="flex gap-3">
                      <span className="text-slate-500">3.</span>
                      <span>Communication: Real-time data/media flows directly.</span>
                   </li>
                </ol>
             </div>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
               <strong>Use Cases:</strong> Video conferencing (Zoom/Meet in browser), File sharing (P2P), Real-time gaming, and IoT control.
             </p>
          </div>
        </div>
      );
      case 'WebSockets (real-time apps)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">
              WebSockets (Real-time Apps)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The WebSocket API is an advanced technology that makes it possible to open an interactive, two-way communication session between the user's browser and a server. Unlike traditional HTTP, you can send messages to a server and receive event-driven responses without having to poll the server for a reply.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* HTTP vs WebSockets */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-500 mb-3 flex items-center gap-2 text-xs uppercase">Traditional HTTP</h4>
                <p className="text-[11px] text-slate-500">Unidirectional: Client asks, Server responds. Connection closes after each request.</p>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm ring-2 ring-blue-500/20">
                <h4 className="font-bold text-blue-500 mb-3 flex items-center gap-2 text-xs uppercase">WebSockets</h4>
                <p className="text-[11px] text-slate-500">Bidirectional: Full-duplex connection stays open. Both can send data at any time.</p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Basic Client Implementation</div>
             <pre className="text-blue-300">
{`const socket = new WebSocket('ws://localhost:8080');

// Connection opened
socket.onopen = () => {
  socket.send('Hello Server!');
};

// Listen for messages
socket.onmessage = (event) => {
  console.log('Message from server:', event.data);
};`}
             </pre>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-primary rounded-full"></div> Core Use Cases
             </h4>
             <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-blue-500">✔</span>
                   <span><strong>Live Chat Apps:</strong> Instant message delivery.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500">✔</span>
                   <span><strong>Online Gaming:</strong> Real-time player synchronization.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500">✔</span>
                   <span><strong>Stock Tickers:</strong> Live price updates without refreshing.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500">✔</span>
                   <span><strong>Collaborative Tools:</strong> Like Google Docs multi-user editing.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Fetch Streams':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-500">
              Fetch Streams
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The <code>body</code> of a <code>fetch()</code> response is a <code>ReadableStream</code>. This allows you to start processing the response data chunk by chunk as it arrives from the server, instead of waiting for the entire response to download. This is extremely useful for handling large files or streaming video.
            </p>
          </section>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">Streaming Reader Example</span>
             </div>
             <pre className="text-cyan-300">
{`const response = await fetch('/large-video.mp4');
const reader = response.body.getReader();

while (true) {
  const { done, value } = await reader.read();
  
  if (done) break;
  
  // Value is a Uint8Array of the chunk data
  console.log(\`Received \${value.length} bytes\`);
}`}
             </pre>
          </div>

          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Main Advantages</h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Memory Efficiency:</strong> No need to store the entire file in RAM.</li>
                   <li>• <strong>Lower Latency:</strong> Start rendering or processing data immediately.</li>
                   <li>• <strong>Better UX:</strong> Progress bars can be updated in real-time.</li>
                </ul>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-2 italic">Common Use Cases</h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• Streaming high-definition video or audio.</li>
                   <li>• Processing massive CSV or JSON datasets.</li>
                   <li>• Real-time log monitoring from a server.</li>
                </ul>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
               <strong>Note:</strong> Most modern browsers support Readable Streams. It transforms the way we think about data transfer from "all-at-once" to "piece-by-piece".
             </p>
          </div>
        </div>
      );
      case 'Temporal API':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-500">
              Temporal API
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The Temporal API is a new, modern proposal for working with dates and times in JavaScript. It aims to fix the many long-standing issues with the old <code>Date</code> object by providing a simpler, more powerful, and immutable API for date and time manipulations. It is an upcoming feature and not yet available in all browsers.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-2 italic">Why Temporal?</h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Immutable:</strong> Methods return new objects instead of modifying existing ones.</li>
                   <li>• <strong>Time Zone Aware:</strong> First-class support for time zones and daylight saving time.</li>
                   <li>• <strong>Better Parsing:</strong> Strictly follows ISO 8601 formats.</li>
                   <li>• <strong>Consistent API:</strong> Separate objects for dates, times, and date-times.</li>
                </ul>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-2 italic">Key Types</h4>
                <div className="space-y-2 text-[10px] font-mono">
                   <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">Temporal.Now.zonedDateTimeISO()</div>
                   <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">Temporal.PlainDate</div>
                   <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded">Temporal.Duration</div>
                </div>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Modern Date Manipulation</div>
             <pre className="text-purple-300">
{`// Current date in a specific time zone
const today = Temporal.Now.plainDateISO();

// Adding duration is simple and safe
const nextWeek = today.add({ days: 7 });

console.log(today.toString());    // "2026-02-24"
console.log(nextWeek.toString()); // "2026-03-03"`}
             </pre>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Current Status:</strong> As of 2026, the Temporal API is in the advanced stages of the TC39 process. For production use today, you should use a <strong>polyfill</strong> as native browser support is still rolling out.
             </p>
          </div>
        </div>
      );
      case 'Edge Functions':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
              Edge Functions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Edge Functions are a new trend in web development where you run server-side code on a CDN (Content Delivery Network) "edge" location, which is physically close to the user. This reduces latency and improves performance. Platforms like Vercel, Netlify, and Cloudflare Workers offer this capability.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Traditional Server vs Edge */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-slate-500 mb-2 text-xs uppercase">Traditional Server</h4>
                <p className="text-[11px] text-slate-400 leading-relaxed">Requests travel to a single central data center, increasing latency for distant users.</p>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm ring-2 ring-cyan-500/20">
                <h4 className="font-bold text-cyan-500 mb-2 text-xs uppercase">Edge Computing</h4>
                <p className="text-[11px] text-slate-400 leading-relaxed">Code execution happens at the nearest edge node, providing near-instant responses globally.</p>
             </div>
          </div>

          
          <div className="p-6 bg-slate-900 rounded-3xl border border-slate-800">
             <h4 className="font-bold mb-4 text-cyan-400 text-sm">Key Benefits</h4>
             <ul className="space-y-4 text-xs text-slate-400">
                <li className="flex gap-3">
                   <div className="w-5 h-5 rounded-full bg-cyan-900/30 flex items-center justify-center text-cyan-500 font-bold text-[10px]">1</div>
                   <span><strong>Ultra-low Latency:</strong> Drastically reduces TTFB (Time to First Byte).</span>
                </li>
                <li className="flex gap-3">
                   <div className="w-5 h-5 rounded-full bg-cyan-900/30 flex items-center justify-center text-cyan-500 font-bold text-[10px]">2</div>
                   <span><strong>Auto-scaling:</strong> No servers to manage; handles millions of requests automatically.</span>
                </li>
                <li className="flex gap-3">
                   <div className="w-5 h-5 rounded-full bg-cyan-900/30 flex items-center justify-center text-cyan-500 font-bold text-[10px]">3</div>
                   <span><strong>Dynamic at the Edge:</strong> Personalize content or handle Auth closer to the user.</span>
                </li>
             </ul>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
               <strong>Popular Platforms:</strong> Vercel Edge Functions, Cloudflare Workers, and Netlify Edge Functions are currently leading this space.
             </p>
          </div>
        </div>
      );
      case 'React.js Basics':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-blue-600">
              React.js Basics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              React is a JavaScript library for building user interfaces. It lets you create reusable UI components that manage their own state and can be composed to build complex applications.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Concepts List */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-sky-600 mb-4 uppercase text-xs tracking-widest">Core Concepts</h4>
                <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                   <li className="flex gap-3">
                      <span className="font-bold text-sky-500">•</span>
                      <span><strong>Components:</strong> Reusable pieces of UI (e.g., a button, a form).</span>
                   </li>
                   <li className="flex gap-3">
                      <span className="font-bold text-sky-500">•</span>
                      <span><strong>JSX:</strong> A syntax extension that lets you write HTML-like code in JavaScript.</span>
                   </li>
                   <li className="flex gap-3">
                      <span className="font-bold text-sky-500">•</span>
                      <span><strong>Props:</strong> Data passed down from a parent component to a child component.</span>
                   </li>
                   <li className="flex gap-3">
                      <span className="font-bold text-sky-500">•</span>
                      <span><strong>State:</strong> Data that is managed within a component. When state changes, the component re-renders.</span>
                   </li>
                </ul>
             </div>

             {/* Visual Component Example */}
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 flex flex-col justify-center">
                <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                   <span className="text-sky-400 font-bold uppercase tracking-widest text-[10px]">Greeting.jsx</span>
                </div>
                <pre className="text-sky-300 font-mono text-xs leading-relaxed">
{`function Greeting(props) {
  const [count, setCount] = useState(0); // State

  return (
    <div>
      <h1>Hello, {props.name}</h1> {/* Props */}
      <button onClick={() => setCount(count + 1)}>
        Count: {count}
      </button>
    </div>
  );
}`}
                </pre>
             </div>
          </div>

          
          <div className="p-5 bg-sky-50 dark:bg-sky-900/10 border-l-4 border-sky-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed italic">
               <strong>Note:</strong> React follows a <strong>declarative</strong> approach, meaning you describe <em>what</em> the UI should look like for a given state, and React handles the <em>how</em> of updating the DOM efficiently.
             </p>
          </div>
        </div>
      );
      case 'Next.js Basics':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-black to-slate-600 dark:from-white dark:to-slate-400">
              Next.js Basics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Next.js is a popular framework built on top of React. It provides production-ready features out of the box, such as file-based routing, server-side rendering (SSR), static site generation (SSG), and API routes, making it easier to build fast and SEO-friendly applications.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Features */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                   <div className="w-2 h-6 bg-black dark:bg-white rounded-full"></div> Key Features
                </h4>
                <ul className="space-y-3 text-xs text-slate-600 dark:text-slate-400">
                   <li>• <strong>File-based Routing:</strong> Folders and files in <code>app/</code> or <code>pages/</code> define your routes.</li>
                   <li>• <strong>SSR & SSG:</strong> Renders pages on the server or at build time for better performance and SEO.</li>
                   <li>• <strong>API Routes:</strong> Build your backend directly within the Next.js app.</li>
                   <li>• <strong>Optimized Images:</strong> Automatic image resizing and lazy loading.</li>
                </ul>
             </div>

             {/* Visual comparison */}
             <div className="flex flex-col gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200">
                   <span className="text-[10px] font-black uppercase text-blue-500">React</span>
                   <p className="text-xs text-slate-500 mt-1">Client-Side Rendering (CSR) only by default. Needs extra libraries for routing.</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-2xl border border-slate-700 ring-2 ring-white/10">
                   <span className="text-[10px] font-black uppercase text-white">Next.js</span>
                   <p className="text-xs text-slate-400 mt-1">Full-stack framework with built-in routing, SSR, and optimizations.</p>
                </div>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">Folder Structure Routing</div>
             <pre className="text-slate-300 font-mono text-xs leading-relaxed">
{`app/
├── layout.js   (Shared UI)
├── page.js     (Home page /)
├── about/
│   └── page.js (About page /about)
└── api/
    └── hello/
        └── route.js (API endpoint)`}
             </pre>
          </div>
        </div>
      );
      case 'TypeScript':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-400">
              TypeScript
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              TypeScript is a strongly typed programming language that builds on JavaScript, giving you better tooling at any scale. It's a "superset" of JavaScript, meaning any valid JS code is also valid TS code. It adds static types to the language.
            </p>
          </section>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-sky-400 font-bold uppercase tracking-widest text-[10px]">main.ts</span>
             </div>
             <pre className="text-sky-300">
{`// TypeScript adds static types with annotations
function greet(name: string): string {
  return "Hello, " + name;
}

// ✅ This works perfectly
console.log(greet("Koteshwar"));

// ❌ This would cause a compile-time error, catching bugs early!
// greet(123); `}
             </pre>
          </div>

          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-3 italic">Why Use TypeScript?</h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Static Typing:</strong> Catch errors during development instead of runtime.</li>
                   <li>• <strong>Better Tooling:</strong> Enhanced autocomplete (IntelliSense) in editors like VS Code.</li>
                   <li>• <strong>Readability:</strong> Types act as documentation for your functions and variables.</li>
                </ul>
             </div>
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-primary mb-3 italic">Core Concepts</h4>
                <div className="flex flex-wrap gap-2">
                   {['Interfaces', 'Enums', 'Generics', 'Union Types', 'Type Aliases'].map((item) => (
                      <span key={item} className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-[10px] font-bold">
                         {item}
                      </span>
                   ))}
                </div>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
               <strong>Pro Tip:</strong> React projects lo (React Native/Expo tho saha) TypeScript use cheyadam valla data flow (Props/State) chala safe ga mariyu predictable ga untundi.
             </p>
          </div>
        </div>
      );


    default:
      return null;
  }
};
export const  renderPythonContent = (topic) => {
  switch (topic) {
    case 'What is Python?':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-yellow-500">
              What is Python?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Python is a high-level, interpreted, general-purpose programming language. Its design philosophy emphasizes code readability with its notable use of significant indentation. It's often described as a "batteries included" language due to its comprehensive standard library.
            </p>
          </section>

          <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
             <h4 className="font-bold text-blue-600 mb-4 uppercase text-xs tracking-widest">Key Features</h4>
             <ul className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-3">
                   <span className="font-bold text-yellow-500">•</span>
                   <span><strong>Easy to Learn:</strong> Simple, clean syntax makes it a great first language.</span>
                </li>
                <li className="flex gap-3">
                   <span className="font-bold text-yellow-500">•</span>
                   <span><strong>Interpreted:</strong> Code is executed line by line, which simplifies debugging.</span>
                </li>
                <li className="flex gap-3">
                   <span className="font-bold text-yellow-500">•</span>
                   <span><strong>Dynamically Typed:</strong> You don't need to declare the type of a variable.</span>
                </li>
                <li className="flex gap-3">
                   <span className="font-bold text-yellow-500">•</span>
                   <span><strong>Cross-Platform:</strong> Python programs can run on Windows, macOS, and Linux without change.</span>
                </li>
             </ul>
          </div>

                  </div>
      );

    case 'Installation & Setup':
      return (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold">Installation & Setup</h2>
          <p className="text-slate-600 dark:text-slate-400">
            To get started, you need to install Python on your system. You can download it from the official website. It's also recommended to use a good code editor like Visual Studio Code.
          </p>

          <div className="flex gap-4">
             <button className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-500/20">Download Python</button>
             <button className="px-6 py-3 bg-slate-800 text-white rounded-xl font-bold text-sm">Download VS Code</button>
          </div>

          <div className="space-y-4">
             <h3 className="text-xl font-bold italic">Verifying Installation</h3>
             <p className="text-xs text-slate-500">Once installed, open your terminal or command prompt and type:</p>
             <div className="bg-slate-900 rounded-2xl p-6 font-mono text-sm border border-slate-800">
                <pre className="text-green-400">
{`python --version
# or on some systems
python3 --version`}
                </pre>
             </div>
          </div>
        </div>
      );

    case 'Variables':
      return (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold">Variables</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
            Variables are containers for storing data values. In Python, a variable is created the moment you first assign a value to it.
          </p>

          <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <pre className="text-blue-300">
{`# A variable is a named location to store data.
# No need to declare the type of variable.

name = "CodeAlpha"  # A string variable
interns = 10000     # An integer variable
rating = 4.9        # A float variable
is_popular = True   # A boolean variable

print(name)
print(interns)`}
             </pre>
          </div>
                  </div>
      );

    case 'Data Types':
      return (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold">Data Types</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Python has various built-in data types. The most common ones are:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
             {[
               { t: 'Text Type', v: 'str' },
               { t: 'Numeric Types', v: 'int, float, complex' },
               { t: 'Sequence Types', v: 'list, tuple, range' },
               { t: 'Mapping Type', v: 'dict' },
               { t: 'Set Types', v: 'set, frozenset' },
               { t: 'Boolean Type', v: 'bool' }
             ].map((item) => (
               <div key={item.t} className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl flex justify-between">
                  <span className="font-bold">{item.t}:</span>
                  <span className="text-blue-500 font-mono">{item.v}</span>
               </div>
             ))}
          </div>

          <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800 font-mono text-xs text-slate-300">
             <pre>
{`x = "Hello"      # str
y = 20           # int
a = ["apple", "banana"] # list

# You can get the data type with the type() function.
print(type(x))`}
             </pre>
          </div>
        </div>
      );
      case 'Input/Output':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-400">
              Input & Output
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Python provides built-in functions to interact with the user. You can display messages using <code>print()</code> and capture user data using the <code>input()</code> function.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Output Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-blue-500"></div> Output: print()
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                   The <code>print()</code> function is used to display output to the console. It can take multiple arguments and print them separated by a space by default.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`print("Welcome to CodeAlpha!")\n# Output: Welcome to CodeAlpha!`}</pre>
                </div>
             </div>

             {/* Input Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-teal-500"></div> Input: input()
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                   The <code>input()</code> function allows you to get data from the user. <strong>Note:</strong> It always returns the input as a string.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-teal-300">
                   <pre>{`name = input("Enter your name: ")\nprint("Hello, " + name + "!")`}</pre>
                </div>
             </div>
          </div>

          
          <div className="space-y-4">
             <h3 className="text-xl font-bold">Type Casting with Input</h3>
             <p className="text-sm text-slate-500">
                Since <code>input()</code> returns a string, you must convert it if you need a number for calculations.
             </p>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
                <pre className="text-yellow-200">
{`# Casting string input to integer
age_str = input("Enter your age: ")
age_int = int(age_str)

print("Next year you will be", age_int + 1)`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Quick Tip:</strong> You can combine printing strings and numbers using commas in the <code>print()</code> function, which automatically handles the spacing for you.
             </p>
          </div>
        </div>
      );
      case 'Operators':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Python Operators
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Operators are used to perform operations on variables and values. Python divides the operators into several groups based on their functionality.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Arithmetic Operators */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-orange-500"></div> Arithmetic
                </h4>
                <p className="text-xs text-slate-500 mb-4">Used with numeric values to perform common mathematical operations.</p>
                <div className="space-y-2 text-[10px] font-mono text-slate-600 dark:text-slate-400">
                   <div className="flex justify-between border-b pb-1"><span>Addition</span><span>+</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Subtraction</span><span>-</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Multiplication</span><span>*</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Division</span><span>/</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Modulus (Remainder)</span><span>%</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Exponentiation</span><span>**</span></div>
                </div>
             </div>

             {/* Comparison Operators */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-red-500"></div> Comparison
                </h4>
                <p className="text-xs text-slate-500 mb-4">Used to compare two values. They always return a Boolean (True or False).</p>
                <div className="space-y-2 text-[10px] font-mono text-slate-600 dark:text-slate-400">
                   <div className="flex justify-between border-b pb-1"><span>Equal</span><span>==</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Not equal</span><span>!=</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Greater than</span><span>&gt;</span></div>
                   <div className="flex justify-between border-b pb-1"><span>Less than</span><span>&lt;</span></div>
                </div>
             </div>
          </div>

          

          <div className="space-y-4">
             <h3 className="text-xl font-bold">Logical Operators</h3>
             <p className="text-sm text-slate-500">Used to combine conditional statements.</p>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
                <pre className="text-orange-300">
{`x = 5
y = 10

# returns True because both statements are true
print(x > 3 and y < 15) 

# returns True because one of the statements is true
print(x > 3 or y < 5)   

# reverse the result, returns False
print(not(x > 3))`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border-l-4 border-orange-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Assignment Operators:</strong> Python includes shorthand operators like <code>+=</code>, <code>-=</code>, and <code>*=</code> to perform an operation and assign the result in one step (e.g., <code>x += 3</code> is the same as <code>x = x + 3</code>).
             </p>
          </div>
        </div>
      );
      case 'Type Casting':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500">
              Type Casting
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Type Casting is the method to convert a variable's data type into a certain data type in order to perform the required operation. This is essential when you need to ensure data is in the correct format for calculations or display.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {/* int() casting */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-2 text-xs uppercase">int()</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Constructs an integer number from an integer literal, a float literal, or a string literal.</p>
             </div>
             {/* float() casting */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-2 text-xs uppercase">float()</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Constructs a float number from an integer literal, a float literal, or a string literal.</p>
             </div>
             {/* str() casting */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-2 text-xs uppercase">str()</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Constructs a string from a wide variety of data types, including strings, integer literals, and float literals.</p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-emerald-400 font-bold uppercase tracking-widest text-[10px]">casting_examples.py</span>
             </div>
             <pre className="text-emerald-300">
{`x = int(1)       # x will be 1
y = int(2.8)     # y will be 2 (truncates decimal)
z = int("3")     # z will be 3

a = float("4.2") # a will be 4.2
b = str(3.0)     # b will be '3.0'`}
             </pre>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> Not all types can be cast into one another. For example, trying to cast a non-numeric string like <code>int("Hello")</code> will raise a <code>ValueError</code>.
             </p>
          </div>
        </div>
      );
      case 'Comments':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-slate-400 to-slate-600">
              Comments
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Comments are used to explain Python code and make it more readable. They are ignored by the interpreter during execution, meaning they don't affect how the program runs.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Purpose of Comments */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                   <div className="w-2 h-6 bg-slate-400 rounded-full"></div> Why Use Comments?
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• To explain what the code is intended to do.</li>
                   <li>• To make the code more readable for yourself and others.</li>
                   <li>• To prevent execution when testing alternative code.</li>
                </ul>
             </div>

             {/* Implementation Details */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-slate-700 dark:text-slate-200 mb-3">Syntax</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                   Python uses the <code>#</code> symbol for single-line comments. For multi-line comments, though not officially supported, triple quotes (<code>"""</code>) are commonly used as docstrings.
                </p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">comments_demo.py</span>
             </div>
             <pre className="text-slate-300">
{`# This is a single-line comment
print("Hello World")

"""
This is a 
multi-line comment
often used for docstrings.
"""

x = 5 # This is an inline comment`}
             </pre>
          </div>

          <div className="p-4 bg-slate-50 dark:bg-slate-800 border-l-4 border-slate-400 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400 italic">
               <strong>Best Practice:</strong> Write comments that explain <em>why</em> something is done, rather than <em>what</em> is being done (the code itself should be clear enough for the "what").
             </p>
          </div>
        </div>
      );
      case 'Keywords':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-500">
              Keywords
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Keywords are reserved words in Python that have special meanings. They define the syntax and structure of the Python language. You cannot use them as variable names, function names, or any other identifiers.
            </p>
          </section>

          <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
             <h4 className="font-bold text-indigo-600 mb-4 uppercase text-xs tracking-widest italic">Common Keywords</h4>
             <div className="flex flex-wrap gap-2">
                {['False', 'True', 'None', 'and', 'or', 'not', 'if', 'else', 'elif', 'for', 'while', 'break', 'continue', 'def', 'return', 'class', 'import', 'try', 'except'].map((kw) => (
                   <span key={kw} className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-mono font-bold text-slate-700 dark:text-slate-300">
                      {kw}
                   </span>
                ))}
             </div>
             <p className="mt-4 text-[10px] text-slate-500">There are roughly 35 keywords in modern Python versions.</p>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="text-slate-500 text-[10px] mb-2 uppercase font-black">How to see all keywords</div>
             <pre className="text-blue-300">
{`import keyword

# This prints the list of all reserved keywords
print(keyword.kwlist)`}
             </pre>
          </div>

          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> Keywords are case-sensitive. For example, <code>True</code> is a keyword, but <code>true</code> is not. Most keywords are lowercase, but <code>True</code>, <code>False</code>, and <code>None</code> are capitalized.
             </p>
          </div>
        </div>
      );
      case 'Basic Programs':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Basic Programs
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Let's put our knowledge into practice. These fundamental programs demonstrate how to use variables, arithmetic operators, and conditional logic to solve simple problems.
            </p>
          </section>

          {/* Program 1: Addition */}
          <div className="space-y-4">
             <h3 className="text-xl font-bold flex items-center gap-2">
                <span className="text-blue-500">1.</span> Add Two Numbers
             </h3>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
                <pre className="text-blue-300">
{`num1 = 10
num2 = 20
sum = num1 + num2
print("The sum is:", sum)`}
                </pre>
             </div>
          </div>

          
          {/* Program 2: Largest Number */}
          <div className="space-y-4">
             <h3 className="text-xl font-bold flex items-center gap-2">
                <span className="text-blue-500">2.</span> Find the Largest of Three Numbers
             </h3>
             <p className="text-xs text-slate-500 italic">This program uses nested logic and comparison operators to evaluate which variable holds the highest value.</p>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
                <pre className="text-indigo-300">
{`a = 10
b = 30
c = 20

if (a >= b) and (a >= c):
    largest = a
elif (b >= a) and (b >= c):
    largest = b
else:
    largest = c

print("The largest number is", largest)`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Challenge:</strong> Try modifying the "Largest Number" program to take user input using the <code>input()</code> function we learned earlier!
             </p>
          </div>
        </div>
      );
      case 'if / elif / else':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              if / elif / else
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Conditional statements allow you to execute certain blocks of code based on whether a condition is true or false. This is the fundamental way to add logic and branching to your programs.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {/* if Statement */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2 text-xs uppercase">if</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Executes the code block only if the initial condition evaluates to True.</p>
             </div>
             {/* elif Statement */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2 text-xs uppercase">elif</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Short for "else if". It checks another condition if the previous ones were False.</p>
             </div>
             {/* else Statement */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2 text-xs uppercase">else</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">The catch-all block that executes if none of the preceding conditions were True.</p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">grading_system.py</span>
             </div>
             <pre className="text-orange-200">
{`score = 85

if score >= 90:
    print("Grade: A")
elif score >= 80:
    print("Grade: B")
elif score >= 70:
    print("Grade: C")
else:
    print("Grade: F")`}
             </pre>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-orange-500 rounded-full"></div> Important Rules
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-orange-500 font-bold">•</span>
                   <span><strong>Colon (:):</strong> Always end your conditional statements with a colon.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-orange-500 font-bold">•</span>
                   <span><strong>Indentation:</strong> The code to be executed must be indented under the statement.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-orange-500 font-bold">•</span>
                   <span><strong>Order:</strong> Conditions are checked from top to bottom. Once one is True, the rest are skipped.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Loops (for, while)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">
              Loops (for, while)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Loops are used to execute a block of code repeatedly. Python provides two primary types of loops: <code>for</code> loops for iterating over sequences and <code>while</code> loops for repeating as long as a condition is true.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* For Loop Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-blue-500"></div> 'for' loop
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                   Used for iterating over a sequence like a list, tuple, dictionary, set, or string.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`fruits = ["apple", "banana", "cherry"]\nfor fruit in fruits:\n    print(fruit)`}</pre>
                </div>
             </div>

             {/* While Loop Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-cyan-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-cyan-500"></div> 'while' loop
                </h4>
                <p className="text-xs text-slate-500 mb-4">
                   Used to execute a block of statements as long as a specific condition remains True.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-cyan-300">
                   <pre>{`count = 0\nwhile count < 5:\n    print(count)\n    count += 1`}</pre>
                </div>
             </div>
          </div>

          
          <div className="space-y-4">
             <h3 className="text-xl font-bold italic">Using range() with for loops</h3>
             <p className="text-sm text-slate-500">
                The <code>range()</code> function is commonly used to loop through a set of code a specified number of times.
             </p>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
                <pre className="text-blue-200">
{`# Loops from 0 to 4
for i in range(5):
    print(i)`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Loop Control:</strong> You can use <code>break</code> to stop a loop even if the condition is true, and <code>continue</code> to skip the current iteration and move to the next.
             </p>
          </div>
        </div>
      );
      case 'break / continue / pass':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-rose-600">
              break / continue / pass
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              These control statements are used to change the execution flow of a loop or act as placeholders in your code. They provide more flexibility when working with repetitive tasks.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {/* break Statement */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-2 text-xs uppercase">break</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Terminates the loop entirely, immediately jumping to the code after the loop.</p>
             </div>
             {/* continue Statement */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-rose-500 mb-2 text-xs uppercase">continue</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Skips the rest of the current iteration and moves directly to the next one.</p>
             </div>
             {/* pass Statement */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-slate-500 mb-2 text-xs uppercase">pass</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Acts as a null placeholder. It does nothing and is used to avoid syntax errors in empty blocks.</p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-rose-400 font-bold uppercase tracking-widest text-[10px]">control_demo.py</span>
             </div>
             <pre className="text-rose-200">
{`# break example
for i in range(10):
    if i == 5:
        break  # Loop stops at 5
    print(i)   # Prints 0, 1, 2, 3, 4

# continue example
for i in range(5):
    if i == 2:
        continue # Skips printing 2
    print(i)     # Prints 0, 1, 3, 4

# pass example
def future_function():
    pass # Placeholder for code not yet written`}
             </pre>
          </div>

          <div className="p-4 bg-rose-50 dark:bg-rose-900/10 border-l-4 border-rose-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Use Case:</strong> <code>pass</code> is particularly useful when you are designing the structure of your classes or functions but haven't implemented the logic yet.
             </p>
          </div>
        </div>
      );
      case 'Pattern Printing':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
              Pattern Printing
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Nested loops are commonly used to print various patterns, a classic exercise for beginners to understand how loops within loops operate.
            </p>
          </section>

          <div className="space-y-4">
             <h3 className="text-xl font-bold flex items-center gap-2">
                Right-Angled Triangle Pattern
             </h3>
             <p className="text-sm text-slate-500 italic">This pattern uses an outer loop for rows and an inner loop for columns (stars).</p>
             
             <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
                <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                   <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">triangle.py</span>
                </div>
                <pre className="text-blue-200">
{`rows = 5
for i in range(1, rows + 1):
    for j in range(i):
        print("*", end="") # Prints stars in a row
    print() # Moves to the next line (Newline)

# Output:
# *
# **
# ***
# ****
# *****`}
                </pre>
             </div>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-blue-500 rounded-full"></div> Logic Breakdown
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span><strong>Outer Loop:</strong> Controls the number of rows being printed.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span><strong>Inner Loop:</strong> Controls how many characters (stars) appear in each specific row.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span><strong>print("*", end=""):</strong> The <code>end=""</code> parameter prevents Python from adding a newline after every star, allowing them to stay on the same row.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Lists':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600">
              Lists
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Lists are used to store multiple items in a single variable. In Python, lists are ordered, mutable (changeable), and allow duplicate elements. They are defined by placing all items inside square brackets <code>[]</code>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-emerald-600 text-xs uppercase">Ordered</h4>
                <p className="text-[10px] text-slate-500">Items have a defined order that will not change.</p>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-emerald-600 text-xs uppercase">Mutable</h4>
                <p className="text-[10px] text-slate-500">You can change, add, or remove items after creation.</p>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm">
                <h4 className="font-bold text-emerald-600 text-xs uppercase">Allow Duplicates</h4>
                <p className="text-[10px] text-slate-500">Since lists are indexed, they can have items with the same value.</p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-emerald-400 font-bold uppercase tracking-widest text-[10px]">list_operations.py</span>
             </div>
             <pre className="text-emerald-300">
{`my_list = ["apple", "banana", "cherry"]
print(my_list[1]) # Access by index: 'banana'

my_list.append("orange") # Add an item
my_list[0] = "blackcurrant" # Change an item
my_list.pop(1) # Remove by index

print(my_list)`}
             </pre>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-emerald-500 rounded-full"></div> Key Methods
             </h4>
             <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-emerald-500 font-bold">•</span>
                   <span><code>append()</code>: Adds an element at the end of the list.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-emerald-500 font-bold">•</span>
                   <span><code>insert()</code>: Adds an element at the specified position.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-emerald-500 font-bold">•</span>
                   <span><code>remove()</code>: Removes the item with the specified value.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-emerald-500 font-bold">•</span>
                   <span><code>pop()</code>: Removes the element at the specified position.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Tuples':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Tuples
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Tuples are used to store multiple items in a single variable. A tuple is a collection which is ordered and <strong>immutable</strong> (unchangeable). They are written with round brackets <code>()</code>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   <div className="w-2 h-2 rounded-full bg-indigo-500"></div> Core Characteristics
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Ordered:</strong> Items have a defined order, and that order will not change.</li>
                   <li>• <strong>Immutable:</strong> You cannot change, add, or remove items after the tuple has been created.</li>
                   <li>• <strong>Allow Duplicates:</strong> Since tuples are indexed, they can have items with the same value.</li>
                </ul>
             </div>
             
             <div className="flex flex-col justify-center p-6 bg-indigo-50 dark:bg-indigo-900/10 rounded-3xl border border-indigo-100 dark:border-indigo-800">
                <h4 className="font-bold text-indigo-700 dark:text-indigo-300 mb-2 italic text-sm">List vs Tuple</h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                   Use a <strong>List</strong> if you need a collection that can change. Use a <strong>Tuple</strong> for data that should remain constant throughout the program execution.
                </p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">tuple_demo.py</span>
             </div>
             <pre className="text-indigo-300">
{`my_tuple = ("apple", "banana", "cherry")
print(my_tuple[0]) # Output: 'apple'

# ❌ Trying to change an item will cause an error
# my_tuple[0] = "blackcurrant" 
# Result: TypeError: 'tuple' object does not support item assignment

print(len(my_tuple)) # Check length: 3`}
             </pre>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why use Tuples?</strong> They are faster than lists and provide write-protection for data that you don't want to be accidentally modified.
             </p>
          </div>
        </div>
      );
      case 'Sets':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">
              Sets
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Sets are used to store multiple items in a single variable. A set is a collection which is <strong>unordered</strong>, <strong>mutable</strong>, and <strong>unindexed</strong>. They are written with curly brackets <code>{}</code>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm text-center">
                <h4 className="font-bold text-orange-600 text-xs uppercase">Unordered</h4>
                <p className="text-[10px] text-slate-500">Items do not have a defined order; they appear in different positions each time.</p>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm text-center">
                <h4 className="font-bold text-orange-600 text-xs uppercase">No Duplicates</h4>
                <p className="text-[10px] text-slate-500">Duplicate values are ignored. Each element must be unique.</p>
             </div>
             <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 rounded-2xl shadow-sm text-center">
                <h4 className="font-bold text-orange-600 text-xs uppercase">Unindexed</h4>
                <p className="text-[10px] text-slate-500">You cannot access items by referring to an index or a key.</p>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">set_methods.py</span>
             </div>
             <pre className="text-orange-200">
{`my_set = {"apple", "banana", "cherry"}

my_set.add("orange") # Adding an item
my_set.add("apple")  # ❗ No change, duplicates are ignored

print(my_set) # Order is not guaranteed

# Mathematical Set Operations
set1 = {1, 2, 3}
set2 = {3, 4, 5}

print(set1.union(set2))        # {1, 2, 3, 4, 5}
print(set1.intersection(set2)) # {3}`}
             </pre>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-orange-500 rounded-full"></div> When to use Sets?
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-orange-500 font-bold">•</span>
                   <span>To remove duplicate elements from a list or collection.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-orange-500 font-bold">•</span>
                   <span>When the order of items is not important for your logic.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-orange-500 font-bold">•</span>
                   <span>To perform mathematical operations like finding common items (intersection).</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'Dictionaries':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-600">
              Dictionaries
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Dictionaries are used to store data values in <strong>key:value</strong> pairs. In modern Python (3.7+), dictionaries are ordered, mutable, and do not allow duplicate keys. They are created with curly brackets <code>{}</code>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-pink-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   <div className="w-2 h-2 rounded-full bg-pink-500"></div> Key Features
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Key-Value Mapping:</strong> Each item has a unique key that maps to a specific value.</li>
                   <li>• <strong>Mutable:</strong> You can change, add, or remove items after the dictionary has been created.</li>
                   <li>• <strong>No Duplicate Keys:</strong> If you use the same key twice, the second value will overwrite the first.</li>
                </ul>
             </div>

             <div className="p-6 bg-slate-900 rounded-3xl border border-slate-800 flex flex-col justify-center">
                <span className="text-pink-400 font-bold uppercase tracking-widest text-[10px] mb-4">Iteration Example</span>
                <div className="bg-slate-800/50 p-4 rounded-xl font-mono text-[10px] text-pink-200">
                   <pre>{`for key, value in my_dict.items():\n  print(key, ":", value)`}</pre>
                </div>
             </div>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-pink-400 font-bold uppercase tracking-widest text-[10px]">dict_ops.py</span>
             </div>
             <pre className="text-pink-200">
{`my_dict = {
  "brand": "CodeAlpha",
  "course": "Python",
  "year": 2024
}

print(my_dict["brand"]) # Access value by key

my_dict["year"] = 2025 # Update value
my_dict["rating"] = 5  # Add a new key-value pair

print(my_dict)`}
             </pre>
          </div>

          <div className="p-4 bg-pink-50 dark:bg-pink-900/10 border-l-4 border-pink-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Dictionaries are highly optimized for retrieving data. Searching for a value by its key is much faster than searching for an item in a list.
             </p>
          </div>
        </div>
      );
      case 'Comprehensions':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">
              Comprehensions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Comprehensions provide a concise way to create lists, sets, and dictionaries. Instead of using multiple lines with loops and <code>append()</code>, you can define the collection and its logic in a single, readable line.
            </p>
          </section>

          <div className="grid grid-cols-1 gap-6">
             {/* List Comprehension */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   List Comprehension
                </h4>
                <p className="text-[11px] text-slate-500 mb-4">Create a list by performing an operation on each item in an existing iterable.</p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[11px] text-blue-300">
                   <pre>{`# Create a list of squares from 0 to 9\nsquares = [x**2 for x in range(10)]\nprint(squares)`}</pre>
                </div>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Set Comprehension */}
                <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                   <h4 className="font-bold text-cyan-600 mb-3 flex items-center gap-2 text-xs uppercase">
                      Set Comprehension
                   </h4>
                   <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-cyan-300">
                      <pre>{`# Create a set of unique first letters\nnames = ["CodeAlpha", "Internship"]\nfirst_letters = {name[0] for name in names}\nprint(first_letters)`}</pre>
                   </div>
                </div>

                {/* Dictionary Comprehension */}
                <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                   <h4 className="font-bold text-indigo-600 mb-3 flex items-center gap-2 text-xs uppercase">
                      Dictionary Comprehension
                   </h4>
                   <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-indigo-300">
                      <pre>{`# Create a dict of numbers and squares\nnum_squares = {x: x**2 for x in range(5)}\nprint(num_squares)`}</pre>
                   </div>
                </div>
             </div>
          </div>

          
          <div className="p-5 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <h4 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-1 italic">Why use them?</h4>
             <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                <li>• <strong>Readability:</strong> Logic is self-contained and clear.</li>
                <li>• <strong>Performance:</strong> Usually faster than manual loops with <code>append()</code>.</li>
                <li>• <strong>Elegance:</strong> Reduces boilerplate code significantly.</li>
             </ul>
          </div>
        </div>
      );
      case 'Slicing':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-500">
              Slicing
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Slicing allows you to get a sub-section of a sequence (like a list, tuple, or string). It provides a very efficient way to access ranges of elements without using manual loops.
            </p>
          </section>

          <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
             <h4 className="font-bold text-blue-600 mb-4 uppercase text-xs tracking-widest italic">The Slicing Syntax</h4>
             <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-xl text-center mb-4">
                <code className="text-lg font-black text-slate-800 dark:text-slate-200">[ start : stop : step ]</code>
             </div>
             <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                <li>• <strong>start:</strong> Starting index of the slice (inclusive).</li>
                <li>• <strong>stop:</strong> Ending index where the slice stops (exclusive).</li>
                <li>• <strong>step:</strong> Optional value to determine the increment between indices.</li>
             </ul>
          </div>

          
          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">slicing_pro.py</span>
             </div>
             <pre className="text-cyan-300">
{`my_list = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

# Get elements from index 2 to 4
print(my_list[2:5]) # Output: [2, 3, 4]

# From start up to index 3
print(my_list[:4])  # Output: [0, 1, 2, 3]

# From index 5 to the end
print(my_list[5:])  # Output: [5, 6, 7, 8, 9]

# Get every second element
print(my_list[::2]) # Output: [0, 2, 4, 6, 8]

# Reverse the list
print(my_list[::-1]) # Output: [9, 8, 7, 6, 5, 4, 3, 2, 1, 0]`}
             </pre>
          </div>

          <div className="p-4 bg-cyan-50 dark:bg-cyan-900/10 border-l-4 border-cyan-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> If <code>start</code> is omitted, it defaults to 0. If <code>stop</code> is omitted, it defaults to the length of the sequence.
             </p>
          </div>
        </div>
      );
      case 'Indentation':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
              Indentation
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Indentation is crucial in Python. It's not just for readability; it's how Python defines blocks of code like loops, functions, and classes. The standard is to use 4 spaces per indentation level.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Correct Indentation */}
             <div className="rounded-3xl border-2 border-green-100 dark:border-green-900/30 overflow-hidden shadow-sm">
                <div className="bg-green-50 dark:bg-green-900/10 px-6 py-3 border-b border-green-100 dark:border-green-900/30">
                   <span className="text-green-600 font-bold text-xs uppercase tracking-widest">Correct Syntax</span>
                </div>
                <div className="bg-slate-900 p-6 font-mono text-xs leading-relaxed h-full">
                   <pre className="text-green-400">
{`if 5 > 2:
    print("Five is greater than two!") 
    # This line is indented correctly`}
                   </pre>
                </div>
             </div>

             {/* Incorrect Indentation */}
             <div className="rounded-3xl border-2 border-red-100 dark:border-red-900/30 overflow-hidden shadow-sm">
                <div className="bg-red-50 dark:bg-red-900/10 px-6 py-3 border-b border-red-100 dark:border-red-900/30">
                   <span className="text-red-600 font-bold text-xs uppercase tracking-widest">Causes Error</span>
                </div>
                <div className="bg-slate-900 p-6 font-mono text-xs leading-relaxed h-full">
                   <pre className="text-slate-400">
{`# This will cause an IndentationError
# if 5 > 2:
# print("This will fail!")`}
                   </pre>
                </div>
             </div>
          </div>

          
          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-blue-500 rounded-full"></div> Key Rules
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span>Python uses indentation to define code structure for functions and classes.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span>Skipping indentation leads to an <code>IndentationError</code>.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-blue-500 font-bold">•</span>
                   <span>Using the industry-standard 4 spaces ensures cross-environment compatibility.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'String Methods':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              String Methods
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Python has a set of built-in methods that you can use on strings. Since strings are immutable, these methods always return a new string without modifying the original one.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Common Case Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-blue-500"></div> Case Conversions
                </h4>
                <div className="space-y-2 text-[10px] font-mono text-slate-600 dark:text-slate-400">
                   <div className="flex justify-between border-b pb-1"><span>upper()</span><span>"HELLO"</span></div>
                   <div className="flex justify-between border-b pb-1"><span>lower()</span><span>"hello"</span></div>
                   <div className="flex justify-between border-b pb-1"><span>capitalize()</span><span>"Hello"</span></div>
                   <div className="flex justify-between border-b pb-1"><span>title()</span><span>"Hello World"</span></div>
                </div>
             </div>

             {/* Search & Replace Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-indigo-500"></div> Search & Modify
                </h4>
                <div className="space-y-2 text-[10px] font-mono text-slate-600 dark:text-slate-400">
                   <div className="flex justify-between border-b pb-1"><span>strip()</span><span>Removes whitespace</span></div>
                   <div className="flex justify-between border-b pb-1"><span>replace()</span><span>Swaps substrings</span></div>
                   <div className="flex justify-between border-b pb-1"><span>find()</span><span>Returns index of match</span></div>
                   <div className="flex justify-between border-b pb-1"><span>split()</span><span>Splits into a list</span></div>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">string_magic.py</span>
             </div>
             <pre className="text-indigo-300">
{`text = "  Hello, CodeAlpha!  "

print(text.upper())      # "  HELLO, CODEALPHA!  "
print(text.strip())      # "Hello, CodeAlpha!"
print(text.replace("H", "J")) # "  Jello, CodeAlpha!  "

words = text.split(",")  # ['  Hello', ' CodeAlpha!  ']
print(words[1].strip())  # "CodeAlpha!"`}
             </pre>
          </div>

          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why use <code>split()</code>?</strong> It's extremely useful for parsing CSV data or user input where multiple values are separated by commas or spaces.
             </p>
          </div>
        </div>
      );
      case 'Formatting (f-strings)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-600">
              f-strings (Literal String Interpolation)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Introduced in Python 3.6, f-strings provide a way to embed expressions inside string literals using curly braces <code>{`{}`}</code>. They are faster and more readable than older formatting methods like <code>%</code> or <code>.format()</code>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   <div className="w-2 h-2 rounded-full bg-orange-500"></div> Why f-strings?
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Readable:</strong> You can see the variable name directly in the string.</li>
                   <li>• <strong>Fast:</strong> They are evaluated at runtime rather than being constant values.</li>
                   <li>• <strong>Expressions:</strong> You can perform math or call methods inside the braces.</li>
                </ul>
             </div>

             <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800 flex flex-col justify-center">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px] mb-4">Old vs New Syntax</span>
                <div className="space-y-2 font-mono text-[10px]">
                   <p className="text-slate-500"># Old (.format)</p>
                   <p className="text-slate-300">"Price: {}".format(p)</p>
                   <p className="text-slate-500 mt-2"># New (f-string)</p>
                   <p className="text-orange-300 font-bold">f"Price: {`{p}`}"</p>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">fstring_examples.py</span>
             </div>
             <pre className="text-orange-200">
{`name = "Koteshwar"
role = "Developer"
salary = 50000

# Simple variable injection
print(f"Name: {name}, Role: {role}")

# Arithmetic inside f-string
print(f"Salary next year: {salary * 1.1}")

# Method calls inside f-string
print(f"Shouting: {name.upper()}")`}
             </pre>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border-l-4 border-orange-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Formatting Numbers:</strong> You can even control decimals easily, like <code>{`f"{3.14159:.2f}"`}</code> which outputs <strong>3.14</strong>.
             </p>
          </div>
        </div>
      );
      case 'Function Definition':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-600">
              Function Definition
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A function is a block of code which only runs when it is called. You can pass data, known as parameters, into a function. A function can return data as a result.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Syntax Parts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   <div className="w-2 h-2 rounded-full bg-purple-500"></div> Syntax Rules
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>def Keyword:</strong> Functions are defined using the <code>def</code> keyword.</li>
                   <li>• <strong>Parentheses ():</strong> Parameters or arguments are placed within these.</li>
                   <li>• <strong>Indentation:</strong> The code block inside the function must be indented.</li>
                   <li>• <strong>return:</strong> Used to exit a function and send a value back to the caller.</li>
                </ul>
             </div>

             <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800 flex flex-col justify-center">
                <span className="text-purple-400 font-bold uppercase tracking-widest text-[10px] mb-4">Function Structure</span>
                <div className="bg-slate-800/50 p-4 rounded-xl font-mono text-[11px] text-purple-200">
                   <pre>{`def my_function(name):\n  print("Hello " + name)\n\nmy_function("Koteshwar")`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-purple-400 font-bold uppercase tracking-widest text-[10px]">functions_demo.py</span>
             </div>
             <pre className="text-purple-300">
{`# 1. Defining a function with parameters
def calculate_total(price, quantity):
    total = price * quantity
    return total

# 2. Calling the function
bill = calculate_total(500, 3)

# 3. Output with f-string
print(f"Total Bill: {bill}")

# Result: Total Bill: 1500`}
             </pre>
          </div>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/10 border-l-4 border-purple-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Functions make your code "DRY" (Don't Repeat Yourself). Instead of writing the same logic multiple times for your projects like <strong>TECHLIFE_APP</strong>, you can define it once in a function.
             </p>
          </div>
        </div>
      );
      case 'Function Definition':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-600">
              Function Definition
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A function is a block of code which only runs when it is called. You can pass data, known as parameters, into a function. A function can return data as a result.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Syntax Parts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   <div className="w-2 h-2 rounded-full bg-purple-500"></div> Syntax Rules
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li className="flex gap-2">
                      <span className="font-bold text-purple-500">•</span>
                      <span><strong>def Keyword:</strong> Functions are defined using the <code>def</code> keyword.</span>
                   </li>
                   <li className="flex gap-2">
                      <span className="font-bold text-purple-500">•</span>
                      <span><strong>Parentheses ():</strong> Parameters or arguments are placed within these.</span>
                   </li>
                   <li className="flex gap-2">
                      <span className="font-bold text-purple-500">•</span>
                      <span><strong>Indentation:</strong> The code block inside the function must be indented.</span>
                   </li>
                   <li className="flex gap-2">
                      <span className="font-bold text-purple-500">•</span>
                      <span><strong>return:</strong> Used to exit a function and send a value back to the caller.</span>
                   </li>
                </ul>
             </div>

             <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800 flex flex-col justify-center">
                <span className="text-purple-400 font-bold uppercase tracking-widest text-[10px] mb-4">Function Structure</span>
                <div className="bg-slate-800/50 p-4 rounded-xl font-mono text-[11px] text-purple-200">
                   <pre>{`def my_function(name):\n  print("Hello " + name)\n\nmy_function("Koteshwar")`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-purple-400 font-bold uppercase tracking-widest text-[10px]">functions_demo.py</span>
             </div>
             <pre className="text-purple-300">
{`# 1. Defining a function with parameters
def calculate_total(price, quantity):
    total = price * quantity
    return total

# 2. Calling the function
bill = calculate_total(500, 3)

# 3. Output with f-string
print(f"Total Bill: {bill}")

# Result: Total Bill: 1500`}
             </pre>
          </div>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/10 border-l-4 border-purple-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Functions make your code "DRY" (Don't Repeat Yourself). Instead of writing the same logic multiple times for your projects like <strong>TECHLIFE_APP</strong>, you can define it once in a function.
             </p>
          </div>
        </div>
      );
      case '*args & **kwargs':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600">
              *args and **kwargs
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Sometimes you do not know in advance how many arguments will be passed into your function. Python allows you to handle this using <code>*args</code> for non-keyword arguments and <code>**kwargs</code> for keyword arguments.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* *args Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   *args (Positional Arguments)
                </h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Receives arguments as a <strong>tuple</strong>. Use this when you want to pass a variable number of positional arguments.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-indigo-300">
                   <pre>{`def my_sum(*args):\n    return sum(args)\n\nprint(my_sum(1, 2, 3, 4)) # Result: 10`}</pre>
                </div>
             </div>

             {/* **kwargs Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   **kwargs (Keyword Arguments)
                </h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Receives arguments as a <strong>dictionary</strong>. Use this to pass variable-length keyword arguments (key=value).
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-purple-300">
                   <pre>{`def my_info(**kwargs):\n    for key, val in kwargs.items():\n        print(f"{key}: {val}")\n\nmy_info(Name="Koteshwar", Role="Dev")`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">dynamic_args.py</span>
             </div>
             <pre className="text-indigo-200">
{`# Function using both *args and **kwargs
def master_function(*args, **kwargs):
    print("Positional:", args)
    print("Keyword:", kwargs)

master_function(1, 2, name="Koteshwar", status="Active")

# Output:
# Positional: (1, 2)
# Keyword: {'name': 'Koteshwar', 'status': 'Active'}`}
             </pre>
          </div>

          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> The words <code>args</code> and <code>kwargs</code> are just conventions. You can use any name, but the <code>*</code> and <code>**</code> operators are what actually do the magic.
             </p>
          </div>
        </div>
      );
      case 'import & Standard Library':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-blue-600">
              Import & Standard Library
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Python's "batteries included" philosophy means it comes with a vast Standard Library of modules that you can <strong>import</strong> to add functionality to your programs without writing everything from scratch.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Import Syntax */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 flex items-center gap-2 text-xs uppercase">
                   <div className="w-2 h-2 rounded-full bg-teal-500"></div> Import Methods
                </h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li className="flex gap-2">
                      <span className="font-bold text-teal-500">•</span>
                      <span><strong>import module:</strong> Imports the entire module.</span>
                   </li>
                   <li className="flex gap-2">
                      <span className="font-bold text-teal-500">•</span>
                      <span><strong>from module import x:</strong> Imports only a specific function or class.</span>
                   </li>
                   <li className="flex gap-2">
                      <span className="font-bold text-teal-500">•</span>
                      <span><strong>import module as alias:</strong> Renames the module for easier reference in your code.</span>
                   </li>
                </ul>
             </div>

             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase">Popular Modules</h4>
                <div className="flex flex-wrap gap-2">
                   {['math', 'random', 'datetime', 'os', 'sys', 'json', 're'].map((mod) => (
                      <span key={mod} className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-lg text-[10px] font-mono font-bold text-blue-500">
                         {mod}
                      </span>
                   ))}
                </div>
                <p className="mt-4 text-[10px] text-slate-500">These are built-in and require no extra installation.</p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-teal-400 font-bold uppercase tracking-widest text-[10px]">library_demo.py</span>
             </div>
             <pre className="text-teal-300">
{`import math
import random as rd
from datetime import datetime

# Using math module
print(math.sqrt(16))  # 4.0

# Using aliased random module
print(rd.randint(1, 10)) # Random number between 1-10

# Using specific import from datetime
print(datetime.now()) # Current date and time`}
             </pre>
          </div>

          <div className="p-4 bg-teal-50 dark:bg-teal-900/10 border-l-4 border-teal-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why use it?</strong> For example, instead of writing your own logic to list all files in a folder for your <strong>TECHLIFE_APP</strong>, you can simply <code>import os</code> and use its pre-built functions.
             </p>
          </div>
        </div>
      );
      case 'pip & PyPI':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-500">
              pip and PyPI
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              While the Standard Library is great, sometimes you need specialized tools created by the community. <strong>PyPI</strong> (Python Package Index) is a repository of software for the Python programming language, and <strong>pip</strong> is the package manager used to install them.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Definitions */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Definitions</h4>
                <ul className="text-xs space-y-4 text-slate-600 dark:text-slate-400">
                   <li>
                      <strong>PyPI:</strong> A public warehouse for thousands of Python packages (like a "Play Store" for Python code).
                   </li>
                   <li>
                      <strong>pip:</strong> A command-line tool that downloads and installs packages from PyPI automatically.
                   </li>
                </ul>
             </div>

             {/* Common Commands */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 text-xs uppercase italic">Essential Commands</h4>
                <div className="space-y-2 font-mono text-[10px]">
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">pip install package_name</div>
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">pip uninstall package_name</div>
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">pip list (See installed packages)</div>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2 text-slate-500 text-[10px] uppercase font-bold tracking-widest">
                Terminal Example
             </div>
             <pre className="text-blue-300">
{`# 1. Installing a popular library for HTTP requests
$ pip install requests

# 2. Using the installed package in your code
import requests
response = requests.get("https://api.github.com")
print(response.status_code)`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Projects like <strong>JJROrganic</strong> or <strong>TECHLIFE_APP</strong> use many external packages. You can use <code>pip freeze  requirements.txt</code> to save a list of all your project's dependencies.
             </p>
          </div>
        </div>
      );
      case 'Virtual Environments (venv)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">
              Virtual Environments (venv)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A virtual environment is a self-contained directory tree that contains a Python installation for a particular version of Python, plus a number of additional packages. This prevents dependency conflicts between different projects.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Why use venv? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-3 text-xs uppercase italic">Why Isolate?</h4>
                <ul className="text-xs space-y-4 text-slate-600 dark:text-slate-400">
                   <li className="flex gap-2">
                      <span className="font-bold text-emerald-500">•</span>
                      <span>Project A might need Version 1.0 of a library, while Project B needs Version 2.0.</span>
                   </li>
                   <li className="flex gap-2">
                      <span className="font-bold text-emerald-500">•</span>
                      <span>It keeps your global Python installation clean and avoids "dependency hell".</span>
                   </li>
                </ul>
             </div>

             {/* Workflow Commands */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 text-xs uppercase italic">Setup Workflow</h4>
                <div className="space-y-2 font-mono text-[10px]">
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">python -m venv myenv (Create)</div>
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">source myenv/bin/activate (Mac/Linux)</div>
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">.\myenv\Scripts\activate (Windows)</div>
                   <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded">deactivate (Exit)</div>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2 text-slate-500 text-[10px] uppercase font-bold tracking-widest">
                Terminal Implementation
             </div>
             <pre className="text-emerald-300">
{`# 1. Create a virtual environment named 'env'
$ python -m venv env

# 2. Activate it (Windows)
$ .\\env\\Scripts\\activate

# 3. Install packages only for this project
(env) $ pip install requests

# 4. Check that it's local
(env) $ pip list`}
             </pre>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Best Practice:</strong> Always create a virtual environment for every new project, such as your <strong>TECHLIFE_APP</strong> or <strong>JJROrganic</strong> store, to ensure that the app runs the same on your machine as it will in production.
             </p>
          </div>
        </div>
      );
      case 'Read/Write Files':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600">
              Read/Write Files
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              File handling is an important part of any application. Python has several functions for creating, reading, updating, and deleting files. The key function for working with files in Python is the <code>open()</code> function.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* File Modes */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-3 text-xs uppercase italic">File Modes</h4>
                <ul className="text-xs space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>'r' (Read):</strong> Default value. Opens a file for reading; error if the file does not exist.</li>
                   <li>• <strong>'w' (Write):</strong> Opens a file for writing; creates the file if it does not exist or truncates it if it does.</li>
                   <li>• <strong>'a' (Append):</strong> Opens a file for appending data to the end of the file.</li>
                   <li>• <strong>'x' (Create):</strong> Creates the specified file; returns an error if the file exists.</li>
                </ul>
             </div>

             <div className="p-6 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-emerald-700 dark:text-emerald-300 mb-2 italic text-sm">The "with" Statement</h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                   Using the <code>with</code> statement is the best practice because it automatically closes the file for you, even if an exception is raised.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-emerald-400 font-bold uppercase tracking-widest text-[10px]">file_ops.py</span>
             </div>
             <pre className="text-emerald-300">
{`# Writing to a file
with open("note.txt", "w") as f:
    f.write("Hello, Koteshwar!\\n")
    f.write("Learning File Handling.")

# Reading from a file
with open("note.txt", "r") as f:
    content = f.read()
    print(content)

# Appending to a file
with open("note.txt", "a") as f:
    f.write("\\nAdded this line later.")`}
             </pre>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Use Case:</strong> In your <strong>TECHLIFE_APP</strong>, you can use file handling to store local logs or export user data into <code>.txt</code> or <code>.csv</code> files.
             </p>
          </div>
        </div>
      );
      case 'Working with JSON':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600">
              Working with JSON
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              JSON is a syntax for storing and exchanging data. Python has a built-in package called <code>json</code>, which can be used to work with JSON data. This is crucial for your full-stack projects when communicating between a React frontend and a Python backend.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Parsing JSON */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-amber-600 mb-3 text-xs uppercase italic">Parse JSON (To Python)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   If you have a JSON string, you can parse it by using the <code>json.loads()</code> method. The result will be a Python <strong>dictionary</strong>.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-amber-300">
                   <pre>{`import json\nx = '{"name":"Koteshwar", "age":25}'\ny = json.loads(x)\nprint(y["name"])`}</pre>
                </div>
             </div>

             {/* Convert to JSON */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Convert to JSON (From Python)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   If you have a Python object, you can convert it into a JSON string by using the <code>json.dumps()</code> method.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-orange-300">
                   <pre>{`import json\nx = {"name": "Koteshwar", "age": 25}\ny = json.dumps(x)\nprint(y)`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-amber-400 font-bold uppercase tracking-widest text-[10px]">json_pro_tips.py</span>
             </div>
             <pre className="text-amber-200">
{`import json

# Python Dictionary
user_data = {
  "name": "Koteshwar",
  "skills": ["React", "Python", "Flutter"],
  "active": True
}

# Convert with formatting (indentation)
json_string = json.dumps(user_data, indent=4)
print(json_string)

# Reading JSON from a file
with open('data.json', 'r') as f:
    data = json.load(f)`}
             </pre>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Web Development Tip:</strong> When you build APIs for <strong>senvra ai</strong>, you'll constantly use <code>json.dumps()</code> to send data from your server to the client-side UI.
             </p>
          </div>
        </div>
      );
      case 'Working with CSV':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-500">
              Working with CSV
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              CSV (Comma Separated Values) is a common file format for transferring and storing data. Python provides a built-in <code>csv</code> module that makes it easy to read from and write to these files.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Reading CSV */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-3 text-xs uppercase italic">Reading CSV</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The <code>csv.reader()</code> function creates an object that iterates over lines in the given CSV file.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-emerald-300">
                   <pre>{`import csv\nwith open('data.csv', 'r') as file:\n    reader = csv.reader(file)\n    for row in reader:\n        print(row)`}</pre>
                </div>
             </div>

             {/* Writing CSV */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-cyan-600 mb-3 text-xs uppercase italic">Writing CSV</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   To write data, you use <code>csv.writer()</code>. You can write single rows or multiple rows at once.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-cyan-300">
                   <pre>{`import csv\ndata = [['Name', 'Age'], ['Koteshwar', 25]]\nwith open('output.csv', 'w', newline='') as file:\n    writer = csv.writer(file)\n    writer.writerows(data)`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">dict_csv_pro.py</span>
             </div>
             <pre className="text-cyan-200">
{`import csv

# Writing CSV using Dictionary (Cleanest way)
fieldnames = ['id', 'name', 'skill']
rows = [{'id': 1, 'name': 'Koteshwar', 'skill': 'React'}]

with open('devs.csv', 'w', newline='') as f:
    writer = csv.DictWriter(f, fieldnames=fieldnames)
    writer.writeheader()
    writer.writerows(rows)

# Reading CSV into Dictionaries
with open('devs.csv', 'r') as f:
    reader = csv.DictReader(f)
    for row in reader:
        print(f"{row['name']} knows {row['skill']}")`}
             </pre>
          </div>

          <div className="p-4 bg-cyan-50 dark:bg-cyan-900/10 border-l-4 border-cyan-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Integration Tip:</strong> If you're building reports for <strong>JJROrganic</strong>, you can use these methods to export orders or inventory lists directly into Excel-compatible files.
             </p>
          </div>
        </div>
      );
      case 'try / except / finally':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Exception Handling (try / except)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              When an error occurs, Python will normally stop and generate an error message. These are called exceptions. You can use <code>try...except</code> blocks to handle these errors gracefully instead of letting the program crash.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {/* try Block */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-2 text-xs uppercase">try</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">The code block where you test for errors. Execution starts here.</p>
             </div>
             {/* except Block */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-2 text-xs uppercase">except</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Handles the error if one occurs in the try block.</p>
             </div>
             {/* finally Block */}
             <div className="p-5 bg-white dark:bg-slate-900 border border-slate-200 rounded-3xl shadow-sm">
                <h4 className="font-bold text-slate-600 mb-2 text-xs uppercase">finally</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">Executes code regardless of whether an exception was raised or not.</p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">error_handling.py</span>
             </div>
             <pre className="text-orange-200">
{`try:
    num = int(input("Enter a number: "))
    result = 10 / num
    print(f"Result: {result}")
except ValueError:
    print("Invalid Input! Please enter a number.")
except ZeroDivisionError:
    print("Error: You cannot divide by zero!")
finally:
    print("Execution completed.")`}
             </pre>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl">
             <h4 className="font-bold mb-3 flex items-center gap-2">
                <div className="w-2 h-6 bg-red-500 rounded-full"></div> Why use it?
             </h4>
             <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex gap-2">
                   <span className="text-red-500 font-bold">•</span>
                   <span><strong>User Experience:</strong> Prevents showing ugly error messages to users.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-red-500 font-bold">•</span>
                   <span><strong>Cleanup:</strong> Use the <code>finally</code> block to close files or database connections even if an error occurs.</span>
                </li>
                <li className="flex gap-2">
                   <span className="text-red-500 font-bold">•</span>
                   <span><strong>Specific Handling:</strong> You can catch different types of errors separately like <code>ZeroDivisionError</code>.</span>
                </li>
             </ul>
          </div>
        </div>
      );
      case 'raise & Custom Exceptions':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-700">
              Raise & Custom Exceptions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              In Python, you can choose to throw an exception if a specific condition occurs using the <code>raise</code> keyword. You can also define your own error types by creating a new class that inherits from the built-in Exception class.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The raise Keyword */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">The raise Keyword</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Used to interrupt the program flow and signal that something went wrong based on your custom logic.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-red-300">
                   <pre>{`age = -5\nif age < 0:\n    raise ValueError("Age cannot be negative!")`}</pre>
                </div>
             </div>

             {/* Custom Exceptions */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-rose-600 mb-3 text-xs uppercase italic">Custom Exceptions</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Creating your own Exception class allows you to categorize errors specific to your application (e.g., <code>InsufficientFundsError</code>).
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-rose-300">
                   <pre>{`class MyError(Exception):\n    pass\n\nraise MyError("Something custom failed!")`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-rose-400 font-bold uppercase tracking-widest text-[10px]">custom_validation.py</span>
             </div>
             <pre className="text-rose-200">
{`# 1. Define a specific exception for your app
class BalanceError(Exception):
    """Exception raised for errors in the account balance."""
    def __init__(self, message="Insufficient balance"):
        self.message = message
        super().__init__(self.message)

# 2. Use it in your logic
def withdraw(amount, balance):
    if amount > balance:
        raise BalanceError(f"You tried to withdraw {amount} but only have {balance}")
    return balance - amount

try:
    withdraw(1000, 500)
except BalanceError as e:
    print(f"Transaction Failed: {e}")`}
             </pre>
          </div>

          <div className="p-4 bg-rose-50 dark:bg-rose-900/10 border-l-4 border-rose-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Real-world Application:</strong> In your <strong>JJROrganic</strong> store, if a user tries to order more items than available in stock, you can <code>raise</code> a <code>StockOutError</code> to handle the UI feedback gracefully.
             </p>
          </div>
        </div>
      );
      case 'Classes & Objects':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-600">
              Classes and Objects
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Python is an object-oriented programming language. Almost everything in Python is an object, with its properties and methods. A <strong>Class</strong> is like a blueprint, while an <strong>Object</strong> is a specific instance of that blueprint.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Class Definition */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 text-xs uppercase italic">The Class (Blueprint)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   You use the <code>class</code> keyword to create a class. It defines the structure and behavior that all objects of this type will have.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-purple-300">
                   <pre>{`class Car:\n    brand = "Toyota"\n    speed = 100`}</pre>
                </div>
             </div>

             {/* Object Definition */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">The Object (Instance)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   An object is created by calling the class. Each object can have its own unique values for the properties defined in the class.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`my_car = Car()\nprint(my_car.brand) # Toyota`}</pre>
                </div>
             </div>
          </div><div className="space-y-4">
             <h3 className="text-xl font-bold italic">The __init__() Function</h3>
             <p className="text-sm text-slate-500">
                All classes have a function called <code>__init__()</code>, which is executed when the object is being initiated. It is used to assign values to object properties.
             </p>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
                <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                   <span className="text-purple-400 font-bold uppercase tracking-widest text-[10px]">oop_basics.py</span>
                </div>
                <pre className="text-purple-200">
{`class Developer:
    def __init__(self, name, skill):
        self.name = name  # Instance variable
        self.skill = skill

    def intro(self):
        print(f"Hi, I am {self.name} and I work with {self.skill}")

# Creating objects
dev1 = Developer("Koteshwar", "Python")
dev2 = Developer("Anasol", "React")

dev1.intro() # Hi, I am Koteshwar and I work with Python
dev2.intro()`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/10 border-l-4 border-purple-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why OOP?</strong> It helps in organizing code for complex projects. For example, in <strong>TECHLIFE_APP</strong>, you can have a <code>User</code> class and create separate objects for <code>Admin</code>, <code>Employee</code>, etc..
             </p>
          </div>
        </div>
      );
      case 'Inheritance':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Inheritance
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Inheritance allows us to define a class that inherits all the methods and properties from another class. It promotes code reusability and establishes a hierarchical relationship between classes.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Parent Class */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Parent Class (Base)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The class being inherited from, also called the base class.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`class Person:\n    def __init__(self, name):\n        self.name = name`}</pre>
                </div>
             </div>

             {/* Child Class */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 text-xs uppercase italic">Child Class (Derived)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The class that inherits from another class. It can also have its own unique methods.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-indigo-300">
                   <pre>{`class Student(Person):\n    pass`}</pre>
                </div>
             </div>
          </div><div className="space-y-4">
             <h3 className="text-xl font-bold italic">The super() Function</h3>
             <p className="text-sm text-slate-500">
                The <code>super()</code> function is used to give the child class access to all the methods and properties of its parent class.
             </p>
             <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
                <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                   <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">inheritance_demo.py</span>
                </div>
                <pre className="text-blue-200">
{`class Employee:
    def __init__(self, name, salary):
        self.name = name
        self.salary = salary

class Developer(Employee):
    def __init__(self, name, salary, language):
        # Use super() to call parent constructor
        super().__init__(name, salary)
        self.language = language

    def show_info(self):
        print(f"{self.name} earns {self.salary} and codes in {self.language}")

dev = Developer("Koteshwar", 80000, "Python")
dev.show_info()`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Logic Building:</strong> Inheritance nee <strong>HRMS system</strong> project lo chala help avthundi. Okka basic <code>User</code> class define chesi, <code>Admin</code>, <code>Manager</code>, mariyu <code>Employee</code> classes ni vatini nundi inherit cheyochu.
             </p>
          </div>
        </div>
      );
      case 'Encapsulation & Abstraction':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-blue-600">
              Encapsulation & Abstraction
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              These two principles help in managing code complexity and protecting data from unauthorized access or modification.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Encapsulation Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 text-xs uppercase italic">Encapsulation</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Restricting access to methods and variables by using private prefixes (<code>__</code>). This prevents accidental data modification.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-teal-300">
                   <pre>{`class Bank:\n    def __init__(self):\n        self.__balance = 1000 # Private`}</pre>
                </div>
             </div>

             {/* Abstraction Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Abstraction</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Hiding complex implementation details and showing only the necessary features of an object. Usually achieved using abstract classes.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`from abc import ABC, abstractmethod\nclass Shape(ABC):\n    @abstractmethod\n    def area(self): pass`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-teal-400 font-bold uppercase tracking-widest text-[10px]">secure_app.py</span>
             </div>
             <pre className="text-teal-200">
{`class Account:
    def __init__(self, owner, balance):
        self.owner = owner
        self.__balance = balance # Encapsulated (Private)

    def deposit(self, amount):
        if amount > 0:
            self.__balance += amount
            print(f"Deposited {amount}")

    def get_balance(self):
        return self.__balance # Controlled Access

acc = Account("Koteshwar", 5000)
# print(acc.__balance) # ❌ This would cause an AttributeError
print(acc.get_balance()) # ✅ Output: 5000`}
             </pre>
          </div>

          <div className="p-4 bg-teal-50 dark:bg-teal-900/10 border-l-4 border-teal-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Why use them?</strong> Abstraction helps you focus on <strong>what</strong> an object does instead of <strong>how</strong> it does it. Encapsulation ensures that critical data (like user passwords in your HRMS project) cannot be changed directly from outside the class.
             </p>
          </div>
        </div>
      );
      case 'Polymorphism':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600">
              Polymorphism
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Polymorphism means "many forms". In programming, it allows different classes to have methods with the same name, but with different implementations based on the object calling them.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Method Overriding */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-pink-600 mb-3 text-xs uppercase italic">Method Overriding</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   When a child class provides a specific implementation of a method that is already defined in its parent class.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-pink-300">
                   <pre>{`class Animal:\n    def speak(self): pass\n\nclass Dog(Animal):\n    def speak(self): return "Woof!"`}</pre>
                </div>
             </div>

             {/* Function Polymorphism */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 text-xs uppercase italic">Built-in Polymorphism</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The same function (like <code>len()</code>) can work on different types of objects like strings, lists, or dictionaries.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-purple-300">
                   <pre>{`len("Hello") # String length: 5\nlen([1, 2, 3]) # List length: 3`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-pink-400 font-bold uppercase tracking-widest text-[10px]">poly_demo.py</span>
             </div>
             <pre className="text-pink-200">
{`class Bird:
    def move(self):
        print("Flying in the sky")

class Ostrich(Bird):
    def move(self):
        print("Running on the ground") # Overriding parent method

class Penguin(Bird):
    def move(self):
        print("Swimming in the ocean")

# Polymorphic behavior in a loop
birds = [Bird(), Ostrich(), Penguin()]

for bird in birds:
    bird.move()

# Output:
# Flying in the sky
# Running on the ground
# Swimming in the ocean`}
             </pre>
          </div>

          <div className="p-4 bg-pink-50 dark:bg-pink-900/10 border-l-4 border-pink-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Software Design Tip:</strong> Polymorphism nee **Travel Booking Platform** project lo chala help avthundi. `Payment` ane base class petti, `CreditCard`, `UPI`, mariyu `NetBanking` classes lo separate <code>process()</code> logic rayochu.
             </p>
          </div>
        </div>
      );
      case 'Generators':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
              Generators
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Generators are special functions that return an iterable set of items, one at a time, in a special way. Instead of using <code>return</code>, they use the <code>yield</code> keyword to pause and resume execution.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Yield vs Return */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-green-600 mb-3 text-xs uppercase italic">yield Keyword</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Unlike <code>return</code>, which terminates the function, <code>yield</code> pauses the function, saving its state for next time.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-green-300">
                   <pre>{`def my_gen():\n    yield 1\n    yield 2\n\ng = my_gen()\nprint(next(g)) # 1`}</pre>
                </div>
             </div>

             {/* Memory Efficiency */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Memory Efficiency</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Generators don't store the entire list in memory. They produce items only when requested (Lazy Evaluation).
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`# Generator Expression\nnums = (x*x for x in range(1000000))\n# Uses minimal RAM!`}</pre>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-green-400 font-bold uppercase tracking-widest text-[10px]">generator_demo.py</span>
             </div>
             <pre className="text-green-200">
{`def count_up_to(max):
    count = 1
    while count <= max:
        yield count # Pause here and return count
        count += 1

# Using the generator in a loop
counter = count_up_to(5)

for num in counter:
    print(num)

# Output: 1, 2, 3, 4, 5`}
             </pre>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Pro Tip:</strong> Generators are excellent for processing large CSV or log files in your <strong>TECHLIFE_APP</strong>. Instead of loading a 1GB file into RAM, you can yield it line-by-line.
             </p>
          </div>
        </div>
      );
      case 'Decorators':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-600">
              Decorators
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Decorators are a very powerful and useful tool in Python since it allows programmers to modify the behavior of a function or class. Decorators allow us to wrap another function in order to extend the behavior of the wrapped function, without permanently modifying it.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The @ Syntax */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 text-xs uppercase italic">The @ Symbol</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Python provides a shorthand for decorators using the <code>@</code> symbol. It is placed right above the function definition.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-indigo-300">
                   <pre>{`@my_decorator\ndef hello():\n    print("Hello!")`}</pre>
                </div>
             </div>

             {/* Functions as Objects */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 text-xs uppercase italic">Higher-Order Functions</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   In Python, functions are first-class objects. This means they can be passed as arguments to other functions.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-purple-300">
                   <pre>{`def greet(func):\n    func()\n\ngreet(print_hi)`}</pre>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">decorator_example.py</span>
             </div>
             <pre className="text-indigo-200">
{`def my_decorator(func):
    def wrapper():
        print("Something is happening before the function is called.")
        func()
        print("Something is happening after the function is called.")
    return wrapper

@my_decorator
def say_whee():
    print("Whee!")

say_whee()

# Output:
# Something is happening before...
# Whee!
# Something is happening after...`}
             </pre>
          </div>

          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Real-world Use:</strong> In your <strong>senvra ai</strong> backend, you can use decorators to check if a user is logged in before allowing them to access specific API routes. It keeps your code DRY and organized!
             </p>
          </div>
        </div>
      );
      case 'Collections Module':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Collections Module
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The <code>collections</code> module implements specialized container datatypes providing alternatives to Python’s general purpose built-in containers. It enhances data handling with structures like <strong>namedtuple</strong>, <strong>Counter</strong>, and <strong>deque</strong>.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* namedtuple & Counter */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Essential Types</h4>
                <ul className="text-[11px] space-y-4 text-slate-600 dark:text-slate-400">
                   <li>
                      <strong>namedtuple():</strong> Creates tuple subclasses with named fields, making your code more readable.
                   </li>
                   <li>
                      <strong>Counter:</strong> A dict subclass for counting hashable objects. Perfect for frequency analysis.
                   </li>
                </ul>
             </div>

             {/* deque & OrderedDict */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 text-xs uppercase italic">Performance Types</h4>
                <ul className="text-[11px] space-y-4 text-slate-600 dark:text-slate-400">
                   <li>
                      <strong>deque:</strong> A list-like container with fast appends and pops on either end. Ideal for queues.
                   </li>
                   <li>
                      <strong>defaultdict:</strong> A dict subclass that calls a factory function to supply missing values.
                   </li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">collections_magic.py</span>
             </div>
             <pre className="text-blue-200">
{`from collections import Counter, namedtuple, deque

# 1. Counter: Frequency of elements
my_list = ['a', 'b', 'a', 'c', 'b', 'a']
print(Counter(my_list)) # Counter({'a': 3, 'b': 2, 'c': 1})

# 2. namedtuple: More readable tuples
Point = namedtuple('Point', ['x', 'y'])
p = Point(10, 20)
print(p.x, p.y) # 10 20

# 3. deque: Efficient double-ended queue
d = deque([1, 2, 3])
d.appendleft(0)
d.pop()
print(d) # deque([0, 1, 2])`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Usage Tip:</strong> Nee <strong>Data Science</strong> interest base chesukuni, frequency counting ki <code>Counter</code> mariyu memory-efficient queues ki <code>deque</code> chala help avthayi.
             </p>
          </div>
        </div>
      );
      case 'NumPy':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              NumPy Basics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              NumPy is the fundamental package for scientific computing in Python. It provides a high-performance multidimensional array object and tools for working with these arrays.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Why NumPy? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Why NumPy?</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Speed:</strong> NumPy arrays are up to 50x faster than traditional Python lists.</li>
                   <li>• <strong>Memory:</strong> They use much less memory to store data.</li>
                   <li>• <strong>Math Power:</strong> Built-in functions for linear algebra, Fourier transform, and matrices.</li>
                </ul>
             </div>

             <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-blue-700 dark:text-blue-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-blue-300">
                   <code>pip install numpy</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">numpy_basics.py</span>
             </div>
             <pre className="text-blue-200">
{`import numpy as np

# 1. Creating a 1D Array
arr = np.array([1, 2, 3, 4, 5])
print(arr)

# 2. Creating a 2D Array (Matrix)
matrix = np.array([[1, 2], [3, 4]])
print(matrix.shape) # Output: (2, 2)

# 3. Fast Math (Element-wise)
arr2 = arr * 2
print(arr2) # [2, 4, 6, 8, 10]

# 4. Built-in functions
print(np.mean(arr)) # Average
print(np.max(arr))  # Maximum`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Note:</strong> Data Science or AI projects lo images mariyu sounds ni NumPy arrays ga convert chese process cheshaka matrame computation modal avthundi.
             </p>
          </div>
        </div>
      );
      case 'Pandas':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600">
              Pandas Basics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Pandas is a fast, powerful, flexible, and easy-to-use open-source data analysis and manipulation tool. It is built on top of NumPy and is the go-to library for handling structured data like CSVs or SQL tables.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Data Structures */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-3 text-xs uppercase italic">Data Structures</h4>
                <ul className="text-[11px] space-y-4 text-slate-600 dark:text-slate-400">
                   <li>
                      <strong>Series:</strong> A 1D labeled array capable of holding any data type (like a single column in Excel).
                   </li>
                   <li>
                      <strong>DataFrame:</strong> A 2D labeled data structure with columns of potentially different types (like a whole Excel table).
                   </li>
                </ul>
             </div>

             <div className="p-6 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-emerald-700 dark:text-emerald-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-emerald-300">
                   <code>pip install pandas</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-emerald-400 font-bold uppercase tracking-widest text-[10px]">pandas_demo.py</span>
             </div>
             <pre className="text-emerald-200">
{`import pandas as pd

# 1. Creating a DataFrame from a dictionary
data = {
    'Name': ['Koteshwar', 'Anasol', 'Dev'],
    'Skills': ['Python', 'React', 'Java'],
    'Exp': [5, 3, 4]
}
df = pd.DataFrame(data)

# 2. Reading from a CSV
# df = pd.read_csv('data.csv')

# 3. Quick Analysis
print(df.head())    # See first 5 rows
print(df.describe()) # Statistical summary
print(df['Skills']) # Access a single column`}
             </pre>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Real-world Power:</strong> Nee <strong>JJROrganic</strong> e-commerce project lo sales data ni analyze cheyali ante, Pandas `read_csv()` mariyu `groupby()` functions tho reports ni seconds lo generate cheyochu.
             </p>
          </div>
        </div>
      );
      case 'Matplotlib & Seaborn':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
              Data Visualization
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Visualization is the easiest way to analyze and absorb complex information. While <strong>Matplotlib</strong> provides the foundation for 2D plotting, <strong>Seaborn</strong> sits on top of it to provide a high-level, aesthetically pleasing interface.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Matplotlib */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Matplotlib</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   The "grandfather" of Python plotting. It gives you full control over every element of a figure like axes, labels, and legends.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-orange-300">
                   <pre>{`import matplotlib.pyplot as plt\nplt.plot([1, 2, 3], [4, 5, 6])\nplt.show()`}</pre>
                </div>
             </div>

             {/* Seaborn */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Seaborn</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Specialized for statistical graphics. It integrates deeply with Pandas DataFrames and produces beautiful charts with less code.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-red-300">
                   <pre>{`import seaborn as sns\nsns.scatterplot(data=df, x='Exp', y='Salary')`}</pre>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">visualize_data.py</span>
             </div>
             <pre className="text-orange-200">
{`import matplotlib.pyplot as plt
import seaborn as sns
import pandas as pd

# Sample data
data = {'Month': ['Jan', 'Feb', 'Mar'], 'Sales': [250, 400, 350]}
df = pd.DataFrame(data)

# Creating a stylish Bar Plot with Seaborn
sns.set_theme(style="whitegrid")
sns.barplot(x='Month', y='Sales', data=df, palette='viridis')

# Customizing with Matplotlib
plt.title("Monthly Sales Analysis")
plt.xlabel("Month")
plt.ylabel("Revenue (₹)")
plt.show()`}
             </pre>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border-l-4 border-orange-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Business Value:</strong> Nee <strong>senvra ai</strong> website visibility analytics ni graphs ga chupinchalanna, leda <strong>JJROrganic</strong> store performance reports ni visualize cheyalanna ee libraries thone modalavthundi.
             </p>
          </div>
        </div>
      );
      case 'Working with APIs':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-600">
              Working with APIs
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              APIs allow your Python programs to interact with other software services over the web. Whether you're fetching weather data or connecting to your <strong>HRMS</strong> backend, the <code>requests</code> library is the industry standard for making HTTP calls.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* HTTP Methods */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Common HTTP Methods</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>GET:</strong> Used to retrieve data from a server.</li>
                   <li>• <strong>POST:</strong> Used to send data to a server to create/update a resource.</li>
                   <li>• <strong>PUT:</strong> Used to update an existing resource.</li>
                   <li>• <strong>DELETE:</strong> Used to remove a resource.</li>
                </ul>
             </div>

             <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-blue-700 dark:text-blue-300 mb-2 italic text-sm">Status Codes</h4>
                <ul className="text-[10px] space-y-1 font-mono text-slate-600 dark:text-slate-400">
                   <li>200: OK (Success)</li>
                   <li>201: Created</li>
                   <li>404: Not Found</li>
                   <li>500: Server Error</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">api_fetch_demo.py</span>
             </div>
             <pre className="text-blue-300">
{`import requests
import json

# 1. Sending a GET Request
url = "https://api.github.com/users/KoteshwarChinnolla"
response = requests.get(url)

# 2. Handling the Response
if response.status_code == 200:
    data = response.json() # Parse JSON automatically
    print(f"User: {data['name']}")
    print(f"Bio: {data['bio']}")
else:
    print("Failed to retrieve data")`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Integration Tip:</strong> Nee <strong>TECHLIFE_APP</strong> lo real-time updates kosam (e.g., leave status check cheyadam), nuvvu backend API endpoints ni ide logic tho connect cheyochu.
             </p>
          </div>
        </div>
      );
      case 'SQLite':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              SQLite Database
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              SQLite is a C-language library that implements a small, fast, self-contained, high-reliability, full-featured, SQL database engine. Python has a built-in module called <code>sqlite3</code> to work with it.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Concepts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Database Workflow</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Connection:</strong> Establish a link to the database file.</li>
                   <li>• <strong>Cursor:</strong> An object used to execute SQL commands and fetch results.</li>
                   <li>• <strong>Commit:</strong> Saving the changes made to the database.</li>
                   <li>• <strong>Close:</strong> Properly closing the connection to prevent data leaks.</li>
                </ul>
             </div>

             <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-blue-700 dark:text-blue-300 mb-2 italic text-sm">Why SQLite?</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   It doesn't require a separate server process. The entire database is stored as a single cross-platform file on your disk.
                </p>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">sqlite_demo.py</span>
             </div>
             <pre className="text-blue-300">
{`import sqlite3

# 1. Connect to database (creates it if not exists)
conn = sqlite3.connect('my_app.db')
cursor = conn.cursor()

# 2. Create a Table
cursor.execute('''CREATE TABLE IF NOT EXISTS users 
               (id INTEGER PRIMARY KEY, name TEXT, role TEXT)''')

# 3. Insert Data
cursor.execute("INSERT INTO users (name, role) VALUES (?, ?)", 
               ('Koteshwar', 'Developer'))

# 4. Commit and Fetch
conn.commit()
cursor.execute("SELECT * FROM users")
print(cursor.fetchall()) 

# 5. Close Connection
conn.close()`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Application:</strong> Nee <strong>TECHLIFE_APP</strong> lo local user settings or offline data storage kosam SQLite perfect match. Idi fast ga untundi mariyu manage cheyadam chala easy.
             </p>
          </div>
        </div>
      );
      case 'MySQL Connector':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              MySQL Connector
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              To interact with a MySQL database from Python, we use the <code>mysql-connector-python</code> driver. Unlike SQLite, MySQL is a server-based system, requiring host details and credentials to establish a connection.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Connection Params */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Connection Parameters</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>host:</strong> The server address (usually 'localhost' or an IP).</li>
                   <li>• <strong>user:</strong> Your database username (e.g., 'root').</li>
                   <li>• <strong>password:</strong> Your database password.</li>
                   <li>• <strong>database:</strong> The specific database name you want to access.</li>
                </ul>
             </div>

             <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-blue-700 dark:text-blue-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-blue-300">
                   <code>pip install mysql-connector-python</code>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">mysql_integration.py</span>
             </div>
             <pre className="text-blue-300">
{`import mysql.connector

# 1. Establish connection to the server
db = mysql.connector.connect(
  host="localhost",
  user="yourusername",
  password="yourpassword",
  database="mydatabase"
)

cursor = db.cursor()

# 2. Execute a Query
cursor.execute("SELECT * FROM employees")

# 3. Fetching Data
results = cursor.fetchall()
for row in results:
    print(row)

# 4. Closing properly
cursor.close()
db.close()`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Enterprise Tip:</strong> Nee **HRMS platform** backend lo real-time employee data ni handle cheyali ante, MySQL Connector thone structural data management modal avthundi. Large datasets ki idi SQLite kante chala reliable.
             </p>
          </div>
        </div>
      );
      case 'Flask':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
              Flask Web Framework
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Flask is a micro web framework written in Python. It is classified as a micro-framework because it does not require particular tools or libraries, making it easy to learn and scale for APIs and small-to-medium web apps.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Features */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Key Features</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Routing:</strong> Maps URLs to specific Python functions.</li>
                   <li>• <strong>Lightweight:</strong> Very little boilerplate code required to get started.</li>
                   <li>• <strong>Extensible:</strong> Easy to add databases like SQLite or MySQL.</li>
                   <li>• <strong>Development Server:</strong> Built-in server for testing your code locally.</li>
                </ul>
             </div>

             <div className="p-6 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-orange-700 dark:text-orange-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-orange-300">
                   <code>pip install Flask</code>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">app.py</span>
             </div>
             <pre className="text-orange-200">
{`from flask import Flask, jsonify

app = Flask(__name__)

# 1. Basic Route
@app.route('/')
def home():
    return "Welcome to CodeAlpha Python Course!"

# 2. API Route (JSON Response)
@app.route('/api/user')
def get_user():
    user_info = {
        "name": "Koteshwar",
        "role": "Full Stack Developer"
    }
    return jsonify(user_info) # Converts dict to JSON

if __name__ == '__main__':
    app.run(debug=True)`}
             </pre>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border-l-4 border-orange-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Full-Stack Tip:</strong> Since you work with <strong>React and React Native</strong>, you can use Flask to build the backend REST APIs. Your frontend can then fetch data using the <code>Working with APIs</code> logic we discussed earlier.
             </p>
          </div>
        </div>
      );case 'BeautifulSoup (Web Scraping)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-600">
              Web Scraping with BeautifulSoup
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Web scraping is the process of extracting data from websites. <strong>BeautifulSoup</strong> is a Python library that makes it easy to scrape information from web pages by parsing HTML and XML documents.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* The Scraping Process */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Workflow</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Requests:</strong> Fetch the HTML content of a page using the <code>requests</code> library.</li>
                   <li>• <strong>Parsing:</strong> Use BeautifulSoup to convert raw HTML into a searchable tree.</li>
                   <li>• <strong>Extraction:</strong> Find specific tags like <code>&lt;h1&gt;</code>, <code>&lt;p&gt;</code>, or <code>&lt;div&gt;</code>.</li>
                </ul>
             </div>

             <div className="p-6 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-orange-700 dark:text-orange-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-orange-300">
                   <code>pip install beautifulsoup4 requests</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">scraper.py</span>
             </div>
             <pre className="text-orange-200">
{`import requests
from bs4 import BeautifulSoup

# 1. Fetch the page
url = "https://example.com"
response = requests.get(url)

# 2. Parse HTML
soup = BeautifulSoup(response.text, 'html.parser')

# 3. Find Data
title = soup.find('h1').text
links = soup.find_all('a')

print(f"Page Title: {title}")
for link in links:
    print(link.get('href'))`}
             </pre>
          </div>

          <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border-l-4 border-orange-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Project Use Case:</strong> Nee <strong>JJROrganic</strong> project kosam competitor prices ni track cheyali anna, leda <strong>senvra ai</strong> website visibility analysis kosam SEO meta tags extract cheyali anna BeautifulSoup chala help avthundi.
             </p>
          </div>
        </div>
      );
      case 'Selenium (Browser Automation)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-600">
              Selenium (Browser Automation)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Selenium is a powerful tool for controlling a web browser through a program. It is functional for all browsers, works on all major OS, and its scripts are written in various languages (Python, Java, etc.). It is ideal for automating repetitive web tasks and testing web applications.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Capabilities */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-cyan-600 mb-3 text-xs uppercase italic">Key Capabilities</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>User Actions:</strong> Clicking buttons, scrolling, and dragging items.</li>
                   <li>• <strong>Form Interaction:</strong> Typing text into search bars and login fields.</li>
                   <li>• <strong>JS Support:</strong> Can wait for elements to load via JavaScript (unlike BeautifulSoup).</li>
                   <li>• <strong>Headless Mode:</strong> Run the browser in the background without a UI.</li>
                </ul>
             </div>

             <div className="p-6 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-cyan-700 dark:text-cyan-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-cyan-300">
                   <code>pip install selenium</code>
                </div>
                <p className="mt-2 text-[9px] text-slate-500 italic">*Note: Requires a WebDriver like ChromeDriver.</p>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">automation_script.py</span>
             </div>
             <pre className="text-cyan-200">
{`from selenium import webdriver
from selenium.webdriver.common.by import By
from selenium.webdriver.common.keys import Keys

# 1. Setup Chrome Driver
driver = webdriver.Chrome()

# 2. Open Website
driver.get("https://www.google.com")

# 3. Find Search Bar and Type
search_box = driver.find_element(By.NAME, "q")
search_box.send_keys("Koteshwar Chinnolla GitHub")
search_box.send_keys(Keys.RETURN)

# 4. Wait & Print Title
print(driver.title)

# 5. Close Browser
# driver.quit()`}
             </pre>
          </div>

          <div className="p-4 bg-cyan-50 dark:bg-cyan-900/10 border-l-4 border-cyan-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Testing Power:</strong> Nee <strong>TECHLIFE_APP</strong> updates release chese mundu, login logic correct ga work avthunda leda ani test cheyadaniki Selenium automation scripts raayochu. Idi chala professional way of working!
             </p>
          </div>
        </div>
      );
      case 'Scikit-Learn':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-sky-600">
              Machine Learning (Scikit-Learn)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Scikit-learn is the most popular library for Machine Learning in Python. It provides simple and efficient tools for predictive data analysis, built on top of NumPy, SciPy, and matplotlib.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* ML Types */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-3 text-xs uppercase italic">Key Algorithms</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Regression:</strong> Predicting continuous values (e.g., House Prices).</li>
                   <li>• <strong>Classification:</strong> Categorizing data (e.g., Spam vs Not Spam).</li>
                   <li>• <strong>Clustering:</strong> Grouping similar data points together.</li>
                   <li>• <strong>Preprocessing:</strong> Scaling and cleaning data before training.</li>
                </ul>
             </div>

             <div className="p-6 bg-sky-50 dark:bg-sky-900/10 border border-sky-100 dark:border-sky-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-sky-700 dark:text-sky-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-sky-300">
                   <code>pip install scikit-learn</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-emerald-400 font-bold uppercase tracking-widest text-[10px]">linear_regression.py</span>
             </div>
             <pre className="text-emerald-300">
{`from sklearn.linear_model import LinearRegression
import numpy as np

# 1. Prepare Data (Exp vs Salary)
X = np.array([[1], [2], [3], [4], [5]]) # Experience
y = np.array([30000, 45000, 60000, 75000, 90000]) # Salary

# 2. Initialize and Train Model
model = LinearRegression()
model.fit(X, y) # Training the model

# 3. Make a Prediction
new_exp = np.array([[6]])
predicted_salary = model.predict(new_exp)
print(f"Predicted Salary for 6 years: {predicted_salary[0]}")`}
             </pre>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>AI Vision:</strong> Nee <strong>senvra ai</strong> project lo intelligent features add cheyali ante—ante user behavior ni predict cheyadam vanti vatiki—Scikit-learn algorithms chala powerful foundation ni isthayi.
             </p>
          </div>
        </div>
      );case 'LLM Integrations (LangChain)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-600">
              LLM Integrations (LangChain)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              LangChain is a framework for developing applications powered by large language models (LLMs). It allows you to "chain" together different components like prompts, models, and memory to create complex AI workflows.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Concepts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-amber-600 mb-3 text-xs uppercase italic">Key Components</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Prompts:</strong> Managing and optimizing instructions for the AI.</li>
                   <li>• <strong>Chains:</strong> Linking multiple LLM calls or tools in a sequence.</li>
                   <li>• <strong>Memory:</strong> Storing past interactions for conversational AI.</li>
                   <li>• <strong>Agents:</strong> Allowing the LLM to use tools like Google Search or Calculators.</li>
                </ul>
             </div>

             <div className="p-6 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-amber-700 dark:text-amber-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-amber-300">
                   <code>pip install langchain langchain-openai</code>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-amber-400 font-bold uppercase tracking-widest text-[10px]">ai_agent.py</span>
             </div>
             <pre className="text-amber-200">
{`from langchain_openai import ChatOpenAI
from langchain.prompts import ChatPromptTemplate

# 1. Initialize Model
model = ChatOpenAI(model="gpt-3.5-turbo")

# 2. Define a Prompt Template
prompt = ChatPromptTemplate.from_template("Explain {topic} like I am 5 years old.")

# 3. Create a Simple Chain
chain = prompt | model # Using the Pipe operator

# 4. Invoke the AI
response = chain.invoke({"topic": "Quantum Computing"})
print(response.content)`}
             </pre>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>AI Vision:</strong> Nee <strong>senvra ai</strong> project lo smart customer support or automated code analysis features build cheyali ante, LangChain is the way to go. Idi multiple AI models ni backend lo handle cheyadam chala easy chesthundhi.
             </p>
          </div>
        </div>
      );
      case 'Docker':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-600">
              Docker & Containerization
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Docker is a platform that allows you to package an application and all its dependencies into a single "container". This ensures that your Python app runs consistently across different environments, solving the "it works on my machine" problem.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Concepts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Core Components</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Dockerfile:</strong> A text file with instructions to build a Docker image.</li>
                   <li>• <strong>Image:</strong> A read-only template containing your code, libraries, and tools.</li>
                   <li>• <strong>Container:</strong> A running instance of an image.</li>
                   <li>• <strong>Docker Hub:</strong> A cloud registry to share and download images.</li>
                </ul>
             </div>

             <div className="p-6 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-cyan-700 dark:text-cyan-300 mb-2 italic text-sm">Deployment Benefit</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   Instead of installing Python, Pip, and MySQL manually on a server, you just run the Docker container which has everything pre-configured.
                </p>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">Dockerfile (Example)</span>
             </div>
             <pre className="text-cyan-200">
{`# 1. Start from a Python base image
FROM python:3.9-slim

# 2. Set the working directory
WORKDIR /app

# 3. Copy requirements and install
COPY requirements.txt .
RUN pip install -r requirements.txt

# 4. Copy the rest of the code
COPY . .

# 5. Run the application
CMD ["python", "app.py"]`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>DevOps Integration:</strong> In professional setups like <strong>Anasol Consultancy</strong>, we use Docker to isolate the frontend (React), backend (Python/Flask), and database (MySQL) into separate containers and manage them using <strong>Docker Compose</strong>.
             </p>
          </div>
        </div>
      );
      case 'CI/CD Basics':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700">
              CI/CD Basics
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              CI/CD bridges the gaps between development and operation activities by enforcing automation in building, testing, and deployment of applications.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* CI Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Continuous Integration (CI)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Focuses on automatically building and testing code every time a developer commits changes to GitHub/GitLab.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600">
                   <li>• Code Linting</li>
                   <li>• Automated Unit Testing</li>
                   <li>• Security Scans</li>
                </ul>
             </div>

             {/* CD Section */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 text-xs uppercase italic">Continuous Deployment (CD)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Automatically deploys the tested code to production servers (like AWS, Azure, or Heroku) if the CI stage passes.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600">
                   <li>• Automatic Releases</li>
                   <li>• Zero-downtime Deployment</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">github-actions-example.yml</span>
             </div>
             <pre className="text-blue-200 text-[12px]">
{`name: Python CI
on: [push]

jobs:
  build:
    runs-on: ubuntu-latest
    steps:
    - uses: actions/checkout@v2
    - name: Set up Python
      uses: actions/setup-python@v2
      with:
        python-version: '3.9'
    - name: Install dependencies
      run: pip install -r requirements.txt
    - name: Run Tests
      run: pytest`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Industry Standard:</strong> Professionally, nuvvu <strong>Anasol Consultancy</strong> lo chesthunna projects ki GitHub Actions or Jenkins use chesi ee pipeline set-up chesthe, manual errors thagguthayi mariyu deployment chala fast ga avthundi.
             </p>
          </div>
        </div>
      );
      case 'Type Hints & pydantic':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Type Hints & Data Validation (Pydantic)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Python is dynamically typed, but <strong>Type Hints</strong> allow you to explicitly declare the data types of variables and function returns. <strong>Pydantic</strong> takes this further by enforcing these types at runtime, ensuring data integrity in your APIs.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Type Hints */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Type Hints (Static)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Helps IDEs like VS Code provide better autocompletion and catch bugs before you run the code.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-blue-300">
                   <pre>{`def greet(name: str) -> str:\n    return "Hello " + name`}</pre>
                </div>
             </div>

             {/* Pydantic */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-indigo-600 mb-3 text-xs uppercase italic">Pydantic (Runtime)</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Automatically validates incoming data. If a field is supposed to be an <code>int</code> but gets a <code>str</code>, Pydantic raises an error.
                </p>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[10px] text-indigo-300">
                   <code>pip install pydantic</code>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">data_model.py</span>
             </div>
             <pre className="text-blue-200">
{`from pydantic import BaseModel, EmailStr
from typing import List, Optional

# 1. Define a Schema
class UserSchema(BaseModel):
    id: int
    name: str
    email: str
    skills: List[str]
    is_active: Optional[bool] = True

# 2. Validate Data
data = {
    "id": "101", # Pydantic will auto-convert this to int!
    "name": "Koteshwar",
    "email": "kotesh@example.com",
    "skills": ["Python", "React"]
}

user = UserSchema(**data)
print(user.name) # Output: Koteshwar`}
             </pre>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Full-Stack Pro Tip:</strong> Fast-API lanti modern frameworks lo Pydantic use chesi schema define chesthe, automatic ga Swagger UI documentation generate avthundi. Idhi nee <strong>Anasol Consultancy</strong> APIs ki chala professional look isthundi.
             </p>
          </div>
        </div>
      );
      case 'Structural Pattern Matching (match-case)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">
              Structural Pattern Matching
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Introduced in Python 3.10, the <code>match</code> statement allows you to compare a value against several patterns. It’s more powerful than a simple switch-case because it can destructure objects and validate data types simultaneously.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Components */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-cyan-600 mb-3 text-xs uppercase italic">How it Works</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>match:</strong> The keyword used to start the comparison.</li>
                   <li>• <strong>case:</strong> Defines a specific pattern to look for.</li>
                   <li>• <strong>Wildcard (_):</strong> A catch-all pattern that matches anything if no other case does.</li>
                   <li>• <strong>Guards:</strong> Adding <code>if</code> conditions inside a case for extra filtering.</li>
                </ul>
             </div>

             <div className="p-6 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-cyan-700 dark:text-cyan-300 mb-2 italic text-sm">Benefits</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   Reduces deep nested <code>if-elif-else</code> chains and makes code much more readable, especially when dealing with complex data structures like JSON or Tuples.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px]">match_case_demo.py</span>
             </div>
             <pre className="text-cyan-200">
{`def process_command(command):
    match command.split(): # Destructuring the input
        case ["quit"]:
            return "Shutting down..."
        case ["load", filename]:
            return f"Loading {filename}..."
        case ["move", x, y] if int(x) > 0: # Case with Guard logic
            return f"Moving to coordinates ({x}, {y})"
        case _:
            return "Unknown command!"

print(process_command("load data.csv")) 
print(process_command("move 10 20"))`}
             </pre>
          </div>

          <div className="p-4 bg-cyan-50 dark:bg-cyan-900/10 border-l-4 border-cyan-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Web Development Tip:</strong> In your <strong>Full-Stack</strong> projects, you can use <code>match-case</code> to handle different HTTP status codes or API response types cleanly. It works much like the <code>switch</code> statement in TypeScript but with extra Pythonic "magic".
             </p>
          </div>
        </div>
      );
      case 'LLM Integrations (LangChain)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-600">
              LLM Integrations (LangChain)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              LangChain is a framework for developing applications powered by large language models (LLMs). It allows you to "chain" together different components like prompts, models, and memory to create complex AI workflows.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Concepts */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-amber-600 mb-3 text-xs uppercase italic">Key Components</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Prompts:</strong> Managing and optimizing instructions for the AI.</li>
                   <li>• <strong>Chains:</strong> Linking multiple LLM calls or tools in a sequence.</li>
                   <li>• <strong>Memory:</strong> Storing past interactions for conversational AI.</li>
                   <li>• <strong>Agents:</strong> Allowing the LLM to use tools like Google Search or Calculators.</li>
                </ul>
             </div>

             <div className="p-6 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-amber-700 dark:text-amber-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-amber-300">
                   <code>pip install langchain langchain-openai</code>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-amber-400 font-bold uppercase tracking-widest text-[10px]">ai_agent.py</span>
             </div>
             <pre className="text-amber-200">
{`from langchain_openai import ChatOpenAI
from langchain.prompts import ChatPromptTemplate

# 1. Initialize Model
model = ChatOpenAI(model="gpt-3.5-turbo")

# 2. Define a Prompt Template
prompt = ChatPromptTemplate.from_template("Explain {topic} like I am 5 years old.")

# 3. Create a Simple Chain
chain = prompt | model # Using the Pipe operator

# 4. Invoke the AI
response = chain.invoke({"topic": "Quantum Computing"})
print(response.content)`}
             </pre>
          </div>

          <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border-l-4 border-amber-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>AI Vision:</strong> Nee <strong>senvra ai</strong> project lo smart customer support or automated code analysis features build cheyali ante, LangChain is the way to go. Idi multiple AI models ni backend lo handle cheyadam chala easy chesthundhi.
             </p>
          </div>
        </div>
      );
      case 'pytest':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-600">
              Automated Testing with pytest
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Testing is essential to ensure code reliability. <strong>pytest</strong> is a mature, full-featured Python testing tool that helps you write better programs. It makes it easy to write small tests, yet scales to support complex functional testing.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Why pytest? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-emerald-600 mb-3 text-xs uppercase italic">Key Features</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Simple Syntax:</strong> No boilerplate code needed like in <code>unittest</code>.</li>
                   <li>• <strong>Assert Logic:</strong> Uses standard <code>assert</code> statements for verification.</li>
                   <li>• <strong>Auto-discovery:</strong> Automatically finds and runs files named <code>test_*.py</code>.</li>
                   <li>• <strong>Fixtures:</strong> Reusable setup code for tests.</li>
                </ul>
             </div>

             <div className="p-6 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-emerald-700 dark:text-emerald-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-emerald-300">
                   <code>pip install pytest</code>
                </div>
             </div>
          </div><div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-emerald-400 font-bold uppercase tracking-widest text-[10px]">test_logic.py</span>
             </div>
             <pre className="text-emerald-300">
{`# Function to test
def add(a, b):
    return a + b

# The Test Case
def test_add():
    assert add(1, 2) == 3 # Should Pass
    assert add(-1, 1) == 0

def test_add_fail():
    assert add(2, 2) == 5 # This will FAIL

# To run tests, type in terminal:
# $ pytest`}
             </pre>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 border-l-4 border-emerald-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Integration:</strong> Earlier manam discuss chesina <strong>CI/CD pipeline</strong> lo, code deploy avvaka mundu ee <code>pytest</code> command automatically run avthundi. Okka test fail ayina deployment stop avthundi. Professional coding ante ide bro!
             </p>
          </div>
        </div>
      );
      case 'async/await & asyncio':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-indigo-600">
              Asynchronous Programming (asyncio)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Asynchronous programming is a design pattern that allows a unit of work to run separately from the main application thread. In Python, <code>asyncio</code> is used to write concurrent code using the <code>async/await</code> syntax.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Keywords */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-purple-600 mb-3 text-xs uppercase italic">Core Keywords</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>async def:</strong> Defines a coroutine (a function that can be paused).</li>
                   <li>• <strong>await:</strong> Tells the program to wait for the task to finish without blocking other tasks.</li>
                   <li>• <strong>Event Loop:</strong> The central execution mechanism that manages all async tasks.</li>
                </ul>
             </div>

             <div className="p-6 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-indigo-700 dark:text-indigo-300 mb-2 italic text-sm">Concurrency vs Parallelism</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   Concurrency is about <strong>dealing</strong> with many things at once (like a waiter taking orders), while parallelism is about <strong>doing</strong> many things at once.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-indigo-400 font-bold uppercase tracking-widest text-[10px]">async_demo.py</span>
             </div>
             <pre className="text-indigo-200">
{`import asyncio

async def fetch_data():
    print("Start fetching...")
    await asyncio.sleep(2) # Simulates an API call
    print("Data fetched!")
    return {"data": 100}

async def main():
    # Schedule multiple tasks simultaneously
    print("Main started")
    result = await asyncio.gather(fetch_data(), fetch_data())
    print(f"Results: {result}")

asyncio.run(main())`}
             </pre>
          </div>

          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/10 border-l-4 border-indigo-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Performance Tip:</strong> If you are building a scraper for <strong>senvra ai</strong>, using <code>asyncio</code> with <code>httpx</code> or <code>aiohttp</code> will make it much faster than the standard <code>requests</code> library because it can fetch hundreds of pages concurrently.
             </p>
          </div>
        </div>
      );
      case 'Dynamic Programming (DP)':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-600">
              Dynamic Programming (DP)
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Dynamic Programming is an algorithmic technique for solving an optimization problem by breaking it down into simpler subproblems and utilizing the fact that the optimal solution to the overall problem depends upon the optimal solution to its subproblems.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Key Properties */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-pink-600 mb-3 text-xs uppercase italic">Two Pillars of DP</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Overlapping Subproblems:</strong> When the same subproblems are solved multiple times (e.g., Fibonacci).</li>
                   <li>• <strong>Optimal Substructure:</strong> When the optimal solution of a problem can be obtained by using the optimal solutions of its subproblems.</li>
                </ul>
             </div>

             {/* Memoization vs Tabulation */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-rose-600 mb-3 text-xs uppercase italic">Approaches</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Memoization (Top-Down):</strong> Starts with the main problem and stores results of subproblems in a cache (dictionary/array).</li>
                   <li>• <strong>Tabulation (Bottom-Up):</strong> Starts from the smallest subproblem and builds up to the main problem using a table.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-pink-400 font-bold uppercase tracking-widest text-[10px]">fibonacci_dp.py</span>
             </div>
             <pre className="text-pink-200">
{`# 1. Recursive Approach (Inefficient - O(2^n))
def fib_rec(n):
    if n <= 1: return n
    return fib_rec(n-1) + fib_rec(n-2)

# 2. DP Approach - Memoization (Efficient - O(n))
memo = {}
def fib_memo(n):
    if n in memo: return memo[n] # Return cached result
    if n <= 1: return n
    
    memo[n] = fib_memo(n-1) + fib_memo(n-2) # Store result
    return memo[n]

# 3. DP Approach - Tabulation (O(n) time, O(n) space)
def fib_tab(n):
    table = [0] * (n + 1)
    table[1] = 1
    for i in range(2, n + 1):
        table[i] = table[i-1] + table[i-2]
    return table[n]`}
             </pre>
          </div>

          <div className="p-4 bg-pink-50 dark:bg-pink-900/10 border-l-4 border-pink-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Logic Mastery:</strong> DP approach valla manam Exponential time complexity $O(2^n)$ nundi Linear time complexity $O(n)$ ki ravachu. Large-scale data processing or optimization algorithms (like shortest path) lo idi magic la pani chesthundi.
             </p>
          </div>
        </div>
      );
      case 'Time & Space Complexity':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600">
              Big O Notation: Time & Space Complexity
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Big O notation is used to describe the performance or complexity of an algorithm. It specifically describes the worst-case scenario and execution time or space used by an algorithm as the input size <code>n</code> grows.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Common Complexity Classes */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-red-600 mb-3 text-xs uppercase italic">Common Complexity Levels</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>O(1) - Constant:</strong> Time stays the same regardless of input (e.g., accessing array index).</li>
                   <li>• <strong>O(log n) - Logarithmic:</strong> Time increases slowly (e.g., Binary Search).</li>
                   <li>• <strong>O(n) - Linear:</strong> Time increases proportionally with input (e.g., simple loop).</li>
                   <li>• <strong>O(n²) - Quadratic:</strong> Performance drops quickly (e.g., Nested loops).</li>
                </ul>
             </div>

             <div className="p-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-2 italic text-sm">Space Complexity</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   Space complexity measures the total amount of memory that an algorithm or a program uses, including the space needed for input values and the temporary space used during execution.
                </p>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-red-400 font-bold uppercase tracking-widest text-[10px]">complexity_examples.py</span>
             </div>
             <pre className="text-red-200">
{`# 1. O(1) - Constant Time
def get_first_element(arr):
    return arr[0] # Always one operation

# 2. O(n) - Linear Time
def print_all_elements(arr):
    for item in arr: # Depends on size 'n'
        print(item)

# 3. O(n^2) - Quadratic Time
def find_duplicates(arr):
    for i in arr:
        for j in arr: # Nested loop
            if i == j:
                pass

# 4. O(n) - Space Complexity Example
def create_copy(arr):
    new_list = [item for item in arr] # Allocates 'n' space
    return new_list`}
             </pre>
          </div>

          <div className="p-4 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Interview Tip:</strong> Top companies like Google or Amazon lo DSA questions solve chesetappudu, code raayadam kante mundu "What is the time complexity of your approach?" ani adugutharu. Linear <code>O(n)</code> or Logarithmic <code>O(log n)</code> solutions ki eppudu priority ekkuva untundi.
             </p>
          </div>
        </div>
      );
      case 'Two Pointers & Sliding Window':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-600">
              Two Pointers & Sliding Window
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              These are two of the most common patterns used to solve array and string problems efficiently. They focus on reducing time complexity from $O(n^2)$ to $O(n)$ by avoiding redundant calculations.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Two Pointers */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 text-xs uppercase italic">Two Pointers Technique</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Uses two indices that move toward each other or in the same direction to find a pair or sub-array that meets a condition.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• Opposite Ends: (e.g., Reverse an array, Two Sum in sorted array).</li>
                   <li>• Fast & Slow: (e.g., Cycle detection in Linked Lists).</li>
                </ul>
             </div>

             {/* Sliding Window */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Sliding Window</h4>
                <p className="text-[11px] text-slate-500 mb-4">
                   Used for problems dealing with contiguous sub-arrays or sub-strings. Instead of re-calculating everything, we "slide" the window by adding a new element and removing the old one.
                </p>
                <ul className="text-[10px] space-y-1 text-slate-600 dark:text-slate-400">
                   <li>• Fixed Size: Max sum of 'k' elements.</li>
                   <li>• Variable Size: Smallest sub-array with sum ≥ S.</li>
                </ul>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-teal-400 font-bold uppercase tracking-widest text-[10px]">dsa_patterns.py</span>
             </div>
             <pre className="text-teal-200">
{`# 1. Two Pointers: Check Palindrome
def is_palindrome(s):
    left, right = 0, len(s) - 1
    while left < right:
        if s[left] != s[right]:
            return False
        left += 1
        right -= 1
    return True

# 2. Sliding Window: Max Sum Subarray of size K
def max_sum_k(arr, k):
    if len(arr) < k: return 0
    
    current_window_sum = sum(arr[:k])
    max_sum = current_window_sum
    
    for i in range(len(arr) - k):
        # Slide the window: subtract left, add right
        current_window_sum = current_window_sum - arr[i] + arr[i+k]
        max_sum = max(max_sum, current_window_sum)
        
    return max_sum`}
             </pre>
          </div>

          

          <div className="p-4 bg-teal-50 dark:bg-teal-900/10 border-l-4 border-teal-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Efficiency Note:</strong> Ordinary loops use $O(n^2)$ for sub-array problems. Sliding Window reduces this to $O(n)$, which is the gold standard for high-performance data processing in your <strong>Python/Data Science</strong> projects.
             </p>
          </div>
        </div>
      );
      case 'Django':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-green-700 to-emerald-900">
              Django Web Framework
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Django is a high-level Python web framework that encourages rapid development and clean, pragmatic design. Built by experienced developers, it takes care of much of the hassle of web development, so you can focus on writing your app without needing to reinvent the wheel.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Principles */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-green-700 mb-3 text-xs uppercase italic">Key Features</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Admin Interface:</strong> A ready-to-use site for managing your data.</li>
                   <li>• <strong>ORM:</strong> Interact with MySQL/PostgreSQL using Python instead of SQL.</li>
                   <li>• <strong>Security:</strong> Built-in protection against SQL injection, XSS, and CSRF.</li>
                   <li>• <strong>Scalability:</strong> Used by Instagram and Pinterest to handle millions of users.</li>
                </ul>
             </div>

             <div className="p-6 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-green-800 dark:text-green-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-green-300">
                   <code>pip install django</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-green-400 font-bold uppercase tracking-widest text-[10px]">django_workflow.py</span>
             </div>
             <pre className="text-green-200">
{`# 1. Start a project
# $ django-admin startproject myproject

# 2. Define a Model (models.py)
from django.db import models

class Employee(models.Model):
    name = models.CharField(max_length=100)
    role = models.CharField(max_length=50)

# 3. Create a View (views.py)
from django.http import HttpResponse

def index(request):
    return HttpResponse("Hello, this is the Django Backend!")

# 4. Map the URL (urls.py)
from django.urls import path
from . import views

urlpatterns = [
    path('', views.index),
]`}
             </pre>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/10 border-l-4 border-green-700 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Enterprise Note:</strong> Nee <strong>HRMS</strong> backend ki security mariyu data management chala important kabatti, Flask kante Django use cheyadam professional choice. Daanilo unde <strong>Django Rest Framework (DRF)</strong> thoti React frontend ni chala fast ga connect cheyochu.
             </p>
          </div>
        </div>
      );
      case 'Lambda Functions':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
              Lambda Functions
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A lambda function is a small anonymous function. It can take any number of arguments, but can only have one expression. They are often used as throw-away functions for short-term use.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Syntax Breakdown */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-orange-600 mb-3 text-xs uppercase italic">Syntax</h4>
                <div className="bg-slate-900 p-4 rounded-xl font-mono text-[11px] text-orange-300">
                   <code>lambda arguments : expression</code>
                </div>
                <p className="mt-4 text-[11px] text-slate-500">
                   Idi automatic ga expression result ni return chesthundi. Explicit ga <code>return</code> keyword vaadalsina pani ledhu.
                </p>
             </div>

             {/* Why use Lambda? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-yellow-600 mb-3 text-xs uppercase italic">Common Use Cases</h4>
                <ul className="text-[11px] space-y-2 text-slate-600 dark:text-slate-400">
                   <li>• <strong>map():</strong> To apply logic to every item in a list.</li>
                   <li>• <strong>filter():</strong> To pick items that meet a condition.</li>
                   <li>• <strong>sort():</strong> To define custom sorting logic.</li>
                </ul>
             </div>
          </div>

          [Image of a diagram comparing a regular Python function definition using 'def' with its corresponding 'lambda' function structure, highlighting arguments and the single expression]

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-yellow-400 font-bold uppercase tracking-widest text-[10px]">lambda_magic.py</span>
             </div>
             <pre className="text-yellow-200">
{`# 1. Simple Lambda
square = lambda x: x * x
print(square(5)) # Output: 25

# 2. Using with filter()
nums = [1, 2, 3, 4, 5, 6]
evens = list(filter(lambda x: x % 2 == 0, nums))
print(evens) # Output: [2, 4, 6]

# 3. Using with map()
double = list(map(lambda x: x * 2, nums))
print(double) # Output: [2, 4, 6, 8, 10, 12]

# 4. Sorting a list of tuples by the second element
points = [(1, 2), (4, 1), (5, -1)]
points.sort(key=lambda x: x[1])
print(points) # Output: [(5, -1), (4, 1), (1, 2)]`}
             </pre>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Clean Code Tip:</strong> Lambda functions ni short logic kosame use cheyi bro. Logic complex ga unte, normal <code>def</code> function rayadame better for readability. Idi nee <strong>Data Science</strong> calculations lo transformation logic raayadaaniki chala help avthundi.
             </p>
          </div>
        </div>
      );
      case 'FastAPI':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-500">
              FastAPI
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              FastAPI is a modern, fast (high-performance), web framework for building APIs with Python 3.6+ based on standard Python type hints. It is one of the fastest Python frameworks available, rivaling Node.js and Go performance.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* Core Benefits */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-teal-600 mb-3 text-xs uppercase italic">Why FastAPI?</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Speed:</strong> Very high performance, thanks to Starlette and Pydantic.</li>
                   <li>• <strong>Auto-Docs:</strong> Generates interactive API docs (Swagger UI) automatically.</li>
                   <li>• <strong>Type Safety:</strong> Fewer bugs due to editor support and data validation.</li>
                   <li>• <strong>Async Support:</strong> Supports <code>async</code> and <code>await</code> natively.</li>
                </ul>
             </div>

             <div className="p-6 bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-teal-700 dark:text-teal-300 mb-2 italic text-sm">Installation</h4>
                <div className="bg-slate-900 p-3 rounded-lg font-mono text-[10px] text-teal-300">
                   <code>pip install fastapi uvicorn</code>
                </div>
             </div>
          </div>

          

          <div className="bg-slate-900 rounded-3xl p-6 shadow-2xl border border-slate-800 font-mono text-sm leading-relaxed">
             <div className="flex gap-1.5 mb-4 border-b border-slate-800 pb-2">
                <span className="text-teal-400 font-bold uppercase tracking-widest text-[10px]">main.py</span>
             </div>
             <pre className="text-teal-200">
{`from fastapi import FastAPI
from pydantic import BaseModel

app = FastAPI()

class Item(BaseModel):
    name: str
    price: float

@app.get("/")
async def root():
    return {"message": "Hello FastAPI"}

@app.post("/items/")
async def create_item(item: Item): # Automatic validation!
    return item

# Run using: uvicorn main:app --reload`}
             </pre>
          </div>

          <div className="p-4 bg-teal-50 dark:bg-teal-900/10 border-l-4 border-teal-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Integration:</strong> Earlier manam discuss chesina <strong>Pydantic</strong> mariyu <strong>Type Hints</strong> topics FastAPI lo core parts. Idi nee <strong>senvra ai</strong> backend build cheyadaniki perfect choice, endukante idi JSON data ni chala fast ga handle chesthundi.
             </p>
          </div>
        </div>
      );
      case 'Creating Modules':
      return (
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
              Creating & Using Modules
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              A module is simply a file containing Python definitions and statements. By breaking your code into modules, you make it more organized and reusable across different parts of your application.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {/* What is a Module? */}
             <div className="p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm">
                <h4 className="font-bold text-blue-600 mb-3 text-xs uppercase italic">Module Basics</h4>
                <ul className="text-[11px] space-y-3 text-slate-600 dark:text-slate-400">
                   <li>• <strong>Definition:</strong> Any <code>.py</code> file is a module.</li>
                   <li>• <strong>Importing:</strong> Use the <code>import</code> keyword to use code from another file.</li>
                   <li>• <strong>Namespace:</strong> Modules help avoid variable name conflicts.</li>
                </ul>
             </div>

             <div className="p-6 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-3xl flex flex-col justify-center">
                <h4 className="font-bold text-blue-700 dark:text-blue-300 mb-2 italic text-sm">Packages</h4>
                <p className="text-[10px] text-slate-500 leading-relaxed">
                   A package is a collection of modules in a folder that contains an <code>__init__.py</code> file. It allows for hierarchical structuring of module namespaces.
                </p>
             </div>
          </div>

          

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             {/* File 1 */}
             <div className="bg-slate-900 rounded-2xl p-4 shadow-xl border border-slate-800 font-mono text-xs">
                <div className="text-blue-400 mb-2 border-b border-slate-800 pb-1 italic">calculator.py (Module)</div>
                <pre className="text-blue-200">
{`def add(a, b):
    return a + b

def multiply(a, b):
    return a * b`}
                </pre>
             </div>

             {/* File 2 */}
             <div className="bg-slate-900 rounded-2xl p-4 shadow-xl border border-slate-800 font-mono text-xs">
                <div className="text-green-400 mb-2 border-b border-slate-800 pb-1 italic">main.py (Application)</div>
                <pre className="text-green-200">
{`import calculator

result = calculator.add(10, 5)
print(result) # Output: 15

# Or import specific function
from calculator import multiply
print(multiply(4, 5)) # 20`}
                </pre>
             </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl">
             <p className="text-xs text-slate-600 dark:text-slate-400">
               <strong>Organization Tip:</strong> In your <strong>senvra ai</strong> backend, separate your logic into <code>auth.py</code>, <code>database.py</code>, and <code>ai_logic.py</code> modules. This makes it easier to find and fix bugs as your project grows!
             </p>
          </div>
        </div>
      );

    default:
      return <div className="p-20 text-center opacity-20 font-black italic tracking-tighter text-6xl">SELECT A TOPIC</div>;
  }
};